(function(window, document, undefined) {
    /**
     * @license
     * Lodash (Custom Build) <https://lodash.com/>
     * Build: `lodash include="assign,bind,create,debounce,defaults,defer,delay,keys,partial,throttle,extend,identity,isArguments,isArray,isFunction,isObject,isString,noop,now" iife="var _ = (function() { %output% return lodash; }.call(this));" --output lodash-1571398373585.build.js --development`
     * Copyright OpenJS Foundation and other contributors <https://openjsf.org/>
     * Released under MIT license <https://lodash.com/license>
     * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
     * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
     */
    var _ = (function() {
        /** Used as a safe reference for `undefined` in pre-ES5 environments. */
        var undefined;

        /** Used as the semantic version number. */
        var VERSION = '4.17.21';

        /** Error message constants. */
        var FUNC_ERROR_TEXT = 'Expected a function';

        /** Used as the internal argument placeholder. */
        var PLACEHOLDER = '__lodash_placeholder__';

        /** Used to compose bitmasks for function metadata. */
        var WRAP_BIND_FLAG = 1,
            WRAP_BIND_KEY_FLAG = 2,
            WRAP_CURRY_BOUND_FLAG = 4,
            WRAP_CURRY_FLAG = 8,
            WRAP_CURRY_RIGHT_FLAG = 16,
            WRAP_PARTIAL_FLAG = 32,
            WRAP_PARTIAL_RIGHT_FLAG = 64,
            WRAP_ARY_FLAG = 128,
            WRAP_REARG_FLAG = 256,
            WRAP_FLIP_FLAG = 512;

        /** Used to detect hot functions by number of calls within a span of milliseconds. */
        var HOT_COUNT = 800,
            HOT_SPAN = 16;

        /** Used as references for various `Number` constants. */
        var INFINITY = 1 / 0,
            MAX_SAFE_INTEGER = 9007199254740991,
            MAX_INTEGER = 1.7976931348623157e+308,
            NAN = 0 / 0;

        /** Used as references for the maximum length and index of an array. */
        var MAX_ARRAY_LENGTH = 4294967295;

        /** Used to associate wrap methods with their bit flags. */
        var wrapFlags = [
            ['ary', WRAP_ARY_FLAG],
            ['bind', WRAP_BIND_FLAG],
            ['bindKey', WRAP_BIND_KEY_FLAG],
            ['curry', WRAP_CURRY_FLAG],
            ['curryRight', WRAP_CURRY_RIGHT_FLAG],
            ['flip', WRAP_FLIP_FLAG],
            ['partial', WRAP_PARTIAL_FLAG],
            ['partialRight', WRAP_PARTIAL_RIGHT_FLAG],
            ['rearg', WRAP_REARG_FLAG]
        ];

        /** `Object#toString` result references. */
        var argsTag = '[object Arguments]',
            arrayTag = '[object Array]',
            asyncTag = '[object AsyncFunction]',
            boolTag = '[object Boolean]',
            dateTag = '[object Date]',
            errorTag = '[object Error]',
            funcTag = '[object Function]',
            genTag = '[object GeneratorFunction]',
            mapTag = '[object Map]',
            numberTag = '[object Number]',
            nullTag = '[object Null]',
            objectTag = '[object Object]',
            proxyTag = '[object Proxy]',
            regexpTag = '[object RegExp]',
            setTag = '[object Set]',
            stringTag = '[object String]',
            symbolTag = '[object Symbol]',
            undefinedTag = '[object Undefined]',
            weakMapTag = '[object WeakMap]';

        var arrayBufferTag = '[object ArrayBuffer]',
            dataViewTag = '[object DataView]',
            float32Tag = '[object Float32Array]',
            float64Tag = '[object Float64Array]',
            int8Tag = '[object Int8Array]',
            int16Tag = '[object Int16Array]',
            int32Tag = '[object Int32Array]',
            uint8Tag = '[object Uint8Array]',
            uint8ClampedTag = '[object Uint8ClampedArray]',
            uint16Tag = '[object Uint16Array]',
            uint32Tag = '[object Uint32Array]';

        /**
         * Used to match `RegExp`
         * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
         */
        var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

        /** Used to match leading and trailing whitespace. */
        var reTrim = /^\s+|\s+$/g;

        /** Used to match wrap detail comments. */
        var reWrapComment = /\{(?:\n\/\* \[wrapped with .+\] \*\/)?\n?/,
            reWrapDetails = /\{\n\/\* \[wrapped with (.+)\] \*/,
            reSplitDetails = /,? & /;

        /** Used to detect bad signed hexadecimal string values. */
        var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

        /** Used to detect binary string values. */
        var reIsBinary = /^0b[01]+$/i;

        /** Used to detect host constructors (Safari). */
        var reIsHostCtor = /^\[object .+?Constructor\]$/;

        /** Used to detect octal string values. */
        var reIsOctal = /^0o[0-7]+$/i;

        /** Used to detect unsigned integer values. */
        var reIsUint = /^(?:0|[1-9]\d*)$/;

        /** Used to identify `toStringTag` values of typed arrays. */
        var typedArrayTags = {};
        typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
            typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
                typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
                    typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
                        typedArrayTags[uint32Tag] = true;
        typedArrayTags[argsTag] = typedArrayTags[arrayTag] =
            typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
                typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
                    typedArrayTags[errorTag] = typedArrayTags[funcTag] =
                        typedArrayTags[mapTag] = typedArrayTags[numberTag] =
                            typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
                                typedArrayTags[setTag] = typedArrayTags[stringTag] =
                                    typedArrayTags[weakMapTag] = false;

        /** Built-in method references without a dependency on `root`. */
        var freeParseInt = parseInt;

        /** Detect free variable `global` from Node.js. */
        var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

        /** Detect free variable `self`. */
        var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

        /** Used as a reference to the global object. */
        var root = freeGlobal || freeSelf || Function('return this')();

        /** Detect free variable `exports`. */
        var freeExports = typeof exports == 'object' && exports && !exports.nodeType && exports;

        /** Detect free variable `module`. */
        var freeModule = freeExports && typeof module == 'object' && module && !module.nodeType && module;

        /** Detect the popular CommonJS extension `module.exports`. */
        var moduleExports = freeModule && freeModule.exports === freeExports;

        /** Detect free variable `process` from Node.js. */
        var freeProcess = moduleExports && freeGlobal.process;

        /** Used to access faster Node.js helpers. */
        var nodeUtil = (function() {
            try {
                // Use `util.types` for Node.js 10+.
                var types = freeModule && freeModule.require && freeModule.require('util').types;

                if (types) {
                    return types;
                }

                // Legacy `process.binding('util')` for Node.js < 10.
                return freeProcess && freeProcess.binding && freeProcess.binding('util');
            } catch (e) {}
        }());

        /* Node.js helper references. */
        var nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

        /*--------------------------------------------------------------------------*/

        /**
         * A faster alternative to `Function#apply`, this function invokes `func`
         * with the `this` binding of `thisArg` and the arguments of `args`.
         *
         * @private
         * @param {Function} func The function to invoke.
         * @param {*} thisArg The `this` binding of `func`.
         * @param {Array} args The arguments to invoke `func` with.
         * @returns {*} Returns the result of `func`.
         */
        function apply(func, thisArg, args) {
            switch (args.length) {
                case 0: return func.call(thisArg);
                case 1: return func.call(thisArg, args[0]);
                case 2: return func.call(thisArg, args[0], args[1]);
                case 3: return func.call(thisArg, args[0], args[1], args[2]);
            }
            return func.apply(thisArg, args);
        }

        /**
         * A specialized version of `_.forEach` for arrays without support for
         * iteratee shorthands.
         *
         * @private
         * @param {Array} [array] The array to iterate over.
         * @param {Function} iteratee The function invoked per iteration.
         * @returns {Array} Returns `array`.
         */
        function arrayEach(array, iteratee) {
            var index = -1,
                length = array == null ? 0 : array.length;

            while (++index < length) {
                if (iteratee(array[index], index, array) === false) {
                    break;
                }
            }
            return array;
        }

        /**
         * A specialized version of `_.includes` for arrays without support for
         * specifying an index to search from.
         *
         * @private
         * @param {Array} [array] The array to inspect.
         * @param {*} target The value to search for.
         * @returns {boolean} Returns `true` if `target` is found, else `false`.
         */
        function arrayIncludes(array, value) {
            var length = array == null ? 0 : array.length;
            return !!length && baseIndexOf(array, value, 0) > -1;
        }

        /**
         * The base implementation of `_.findIndex` and `_.findLastIndex` without
         * support for iteratee shorthands.
         *
         * @private
         * @param {Array} array The array to inspect.
         * @param {Function} predicate The function invoked per iteration.
         * @param {number} fromIndex The index to search from.
         * @param {boolean} [fromRight] Specify iterating from right to left.
         * @returns {number} Returns the index of the matched value, else `-1`.
         */
        function baseFindIndex(array, predicate, fromIndex, fromRight) {
            var length = array.length,
                index = fromIndex + (fromRight ? 1 : -1);

            while ((fromRight ? index-- : ++index < length)) {
                if (predicate(array[index], index, array)) {
                    return index;
                }
            }
            return -1;
        }

        /**
         * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
         *
         * @private
         * @param {Array} array The array to inspect.
         * @param {*} value The value to search for.
         * @param {number} fromIndex The index to search from.
         * @returns {number} Returns the index of the matched value, else `-1`.
         */
        function baseIndexOf(array, value, fromIndex) {
            return value === value
                ? strictIndexOf(array, value, fromIndex)
                : baseFindIndex(array, baseIsNaN, fromIndex);
        }

        /**
         * The base implementation of `_.isNaN` without support for number objects.
         *
         * @private
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
         */
        function baseIsNaN(value) {
            return value !== value;
        }

        /**
         * The base implementation of `_.times` without support for iteratee shorthands
         * or max array length checks.
         *
         * @private
         * @param {number} n The number of times to invoke `iteratee`.
         * @param {Function} iteratee The function invoked per iteration.
         * @returns {Array} Returns the array of results.
         */
        function baseTimes(n, iteratee) {
            var index = -1,
                result = Array(n);

            while (++index < n) {
                result[index] = iteratee(index);
            }
            return result;
        }

        /**
         * The base implementation of `_.unary` without support for storing metadata.
         *
         * @private
         * @param {Function} func The function to cap arguments for.
         * @returns {Function} Returns the new capped function.
         */
        function baseUnary(func) {
            return function(value) {
                return func(value);
            };
        }

        /**
         * Gets the number of `placeholder` occurrences in `array`.
         *
         * @private
         * @param {Array} array The array to inspect.
         * @param {*} placeholder The placeholder to search for.
         * @returns {number} Returns the placeholder count.
         */
        function countHolders(array, placeholder) {
            var length = array.length,
                result = 0;

            while (length--) {
                if (array[length] === placeholder) {
                    ++result;
                }
            }
            return result;
        }

        /**
         * Gets the value at `key` of `object`.
         *
         * @private
         * @param {Object} [object] The object to query.
         * @param {string} key The key of the property to get.
         * @returns {*} Returns the property value.
         */
        function getValue(object, key) {
            return object == null ? undefined : object[key];
        }

        /**
         * Creates a unary function that invokes `func` with its argument transformed.
         *
         * @private
         * @param {Function} func The function to wrap.
         * @param {Function} transform The argument transform.
         * @returns {Function} Returns the new function.
         */
        function overArg(func, transform) {
            return function(arg) {
                return func(transform(arg));
            };
        }

        /**
         * Replaces all `placeholder` elements in `array` with an internal placeholder
         * and returns an array of their indexes.
         *
         * @private
         * @param {Array} array The array to modify.
         * @param {*} placeholder The placeholder to replace.
         * @returns {Array} Returns the new array of placeholder indexes.
         */
        function replaceHolders(array, placeholder) {
            var index = -1,
                length = array.length,
                resIndex = 0,
                result = [];

            while (++index < length) {
                var value = array[index];
                if (value === placeholder || value === PLACEHOLDER) {
                    array[index] = PLACEHOLDER;
                    result[resIndex++] = index;
                }
            }
            return result;
        }

        /**
         * A specialized version of `_.indexOf` which performs strict equality
         * comparisons of values, i.e. `===`.
         *
         * @private
         * @param {Array} array The array to inspect.
         * @param {*} value The value to search for.
         * @param {number} fromIndex The index to search from.
         * @returns {number} Returns the index of the matched value, else `-1`.
         */
        function strictIndexOf(array, value, fromIndex) {
            var index = fromIndex - 1,
                length = array.length;

            while (++index < length) {
                if (array[index] === value) {
                    return index;
                }
            }
            return -1;
        }

        /*--------------------------------------------------------------------------*/

        /** Used for built-in method references. */
        var funcProto = Function.prototype,
            objectProto = Object.prototype;

        /** Used to detect overreaching core-js shims. */
        var coreJsData = root['__core-js_shared__'];

        /** Used to resolve the decompiled source of functions. */
        var funcToString = funcProto.toString;

        /** Used to check objects for own properties. */
        var hasOwnProperty = objectProto.hasOwnProperty;

        /** Used to detect methods masquerading as native. */
        var maskSrcKey = (function() {
            var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
            return uid ? ('Symbol(src)_1.' + uid) : '';
        }());

        /**
         * Used to resolve the
         * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
         * of values.
         */
        var nativeObjectToString = objectProto.toString;

        /** Used to detect if a method is native. */
        var reIsNative = RegExp('^' +
            funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&')
                .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
        );

        /** Built-in value references. */
        var Buffer = moduleExports ? root.Buffer : undefined,
            Symbol = root.Symbol,
            objectCreate = Object.create,
            propertyIsEnumerable = objectProto.propertyIsEnumerable,
            symToStringTag = Symbol ? Symbol.toStringTag : undefined;

        var defineProperty = (function() {
            try {
                var func = getNative(Object, 'defineProperty');
                func({}, '', {});
                return func;
            } catch (e) {}
        }());

        /* Built-in method references for those with the same name as other `lodash` methods. */
        var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined,
            nativeKeys = overArg(Object.keys, Object),
            nativeMax = Math.max,
            nativeMin = Math.min,
            nativeNow = Date.now;

        /* Built-in method references that are verified to be native. */
        var WeakMap = getNative(root, 'WeakMap');

        /** Used to store function metadata. */
        var metaMap = WeakMap && new WeakMap;

        /** Used to lookup unminified function names. */
        var realNames = {};

        /*------------------------------------------------------------------------*/

        /**
         * Creates a `lodash` object which wraps `value` to enable implicit method
         * chain sequences. Methods that operate on and return arrays, collections,
         * and functions can be chained together. Methods that retrieve a single value
         * or may return a primitive value will automatically end the chain sequence
         * and return the unwrapped value. Otherwise, the value must be unwrapped
         * with `_#value`.
         *
         * Explicit chain sequences, which must be unwrapped with `_#value`, may be
         * enabled using `_.chain`.
         *
         * The execution of chained methods is lazy, that is, it's deferred until
         * `_#value` is implicitly or explicitly called.
         *
         * Lazy evaluation allows several methods to support shortcut fusion.
         * Shortcut fusion is an optimization to merge iteratee calls; this avoids
         * the creation of intermediate arrays and can greatly reduce the number of
         * iteratee executions. Sections of a chain sequence qualify for shortcut
         * fusion if the section is applied to an array and iteratees accept only
         * one argument. The heuristic for whether a section qualifies for shortcut
         * fusion is subject to change.
         *
         * Chaining is supported in custom builds as long as the `_#value` method is
         * directly or indirectly included in the build.
         *
         * In addition to lodash methods, wrappers have `Array` and `String` methods.
         *
         * The wrapper `Array` methods are:
         * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
         *
         * The wrapper `String` methods are:
         * `replace` and `split`
         *
         * The wrapper methods that support shortcut fusion are:
         * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
         * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
         * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
         *
         * The chainable wrapper methods are:
         * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
         * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
         * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
         * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
         * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
         * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
         * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
         * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
         * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
         * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
         * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
         * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
         * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
         * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
         * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
         * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
         * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
         * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
         * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
         * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
         * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
         * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
         * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
         * `zipObject`, `zipObjectDeep`, and `zipWith`
         *
         * The wrapper methods that are **not** chainable by default are:
         * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
         * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `conformsTo`, `deburr`,
         * `defaultTo`, `divide`, `each`, `eachRight`, `endsWith`, `eq`, `escape`,
         * `escapeRegExp`, `every`, `find`, `findIndex`, `findKey`, `findLast`,
         * `findLastIndex`, `findLastKey`, `first`, `floor`, `forEach`, `forEachRight`,
         * `forIn`, `forInRight`, `forOwn`, `forOwnRight`, `get`, `gt`, `gte`, `has`,
         * `hasIn`, `head`, `identity`, `includes`, `indexOf`, `inRange`, `invoke`,
         * `isArguments`, `isArray`, `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`,
         * `isBoolean`, `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`,
         * `isEqualWith`, `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`,
         * `isMap`, `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`,
         * `isNumber`, `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`,
         * `isSafeInteger`, `isSet`, `isString`, `isUndefined`, `isTypedArray`,
         * `isWeakMap`, `isWeakSet`, `join`, `kebabCase`, `last`, `lastIndexOf`,
         * `lowerCase`, `lowerFirst`, `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`,
         * `min`, `minBy`, `multiply`, `noConflict`, `noop`, `now`, `nth`, `pad`,
         * `padEnd`, `padStart`, `parseInt`, `pop`, `random`, `reduce`, `reduceRight`,
         * `repeat`, `result`, `round`, `runInContext`, `sample`, `shift`, `size`,
         * `snakeCase`, `some`, `sortedIndex`, `sortedIndexBy`, `sortedLastIndex`,
         * `sortedLastIndexBy`, `startCase`, `startsWith`, `stubArray`, `stubFalse`,
         * `stubObject`, `stubString`, `stubTrue`, `subtract`, `sum`, `sumBy`,
         * `template`, `times`, `toFinite`, `toInteger`, `toJSON`, `toLength`,
         * `toLower`, `toNumber`, `toSafeInteger`, `toString`, `toUpper`, `trim`,
         * `trimEnd`, `trimStart`, `truncate`, `unescape`, `uniqueId`, `upperCase`,
         * `upperFirst`, `value`, and `words`
         *
         * @name _
         * @constructor
         * @category Seq
         * @param {*} value The value to wrap in a `lodash` instance.
         * @returns {Object} Returns the new `lodash` wrapper instance.
         * @example
         *
         * function square(n) {
         *   return n * n;
         * }
         *
         * var wrapped = _([1, 2, 3]);
         *
         * // Returns an unwrapped value.
         * wrapped.reduce(_.add);
         * // => 6
         *
         * // Returns a wrapped value.
         * var squares = wrapped.map(square);
         *
         * _.isArray(squares);
         * // => false
         *
         * _.isArray(squares.value());
         * // => true
         */
        function lodash() {
            // No operation performed.
        }

        /**
         * The base implementation of `_.create` without support for assigning
         * properties to the created object.
         *
         * @private
         * @param {Object} proto The object to inherit from.
         * @returns {Object} Returns the new object.
         */
        var baseCreate = (function() {
            function object() {}
            return function(proto) {
                if (!isObject(proto)) {
                    return {};
                }
                if (objectCreate) {
                    return objectCreate(proto);
                }
                object.prototype = proto;
                var result = new object;
                object.prototype = undefined;
                return result;
            };
        }());

        /**
         * The function whose prototype chain sequence wrappers inherit from.
         *
         * @private
         */
        function baseLodash() {
            // No operation performed.
        }

        /*------------------------------------------------------------------------*/

        /**
         * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
         *
         * @private
         * @constructor
         * @param {*} value The value to wrap.
         */
        function LazyWrapper(value) {
            this.__wrapped__ = value;
            this.__actions__ = [];
            this.__dir__ = 1;
            this.__filtered__ = false;
            this.__iteratees__ = [];
            this.__takeCount__ = MAX_ARRAY_LENGTH;
            this.__views__ = [];
        }

        // Ensure `LazyWrapper` is an instance of `baseLodash`.
        LazyWrapper.prototype = baseCreate(baseLodash.prototype);
        LazyWrapper.prototype.constructor = LazyWrapper;

        /*------------------------------------------------------------------------*/

        /**
         * Creates an array of the enumerable property names of the array-like `value`.
         *
         * @private
         * @param {*} value The value to query.
         * @param {boolean} inherited Specify returning inherited property names.
         * @returns {Array} Returns the array of property names.
         */
        function arrayLikeKeys(value, inherited) {
            var isArr = isArray(value),
                isArg = !isArr && isArguments(value),
                isBuff = !isArr && !isArg && isBuffer(value),
                isType = !isArr && !isArg && !isBuff && isTypedArray(value),
                skipIndexes = isArr || isArg || isBuff || isType,
                result = skipIndexes ? baseTimes(value.length, String) : [],
                length = result.length;

            for (var key in value) {
                if ((inherited || hasOwnProperty.call(value, key)) &&
                    !(skipIndexes && (
                        // Safari 9 has enumerable `arguments.length` in strict mode.
                        key == 'length' ||
                        // Node.js 0.10 has enumerable non-index properties on buffers.
                        (isBuff && (key == 'offset' || key == 'parent')) ||
                        // PhantomJS 2 has enumerable non-index properties on typed arrays.
                        (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
                        // Skip index properties.
                        isIndex(key, length)
                    ))) {
                    result.push(key);
                }
            }
            return result;
        }

        /**
         * Assigns `value` to `key` of `object` if the existing value is not equivalent
         * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
         * for equality comparisons.
         *
         * @private
         * @param {Object} object The object to modify.
         * @param {string} key The key of the property to assign.
         * @param {*} value The value to assign.
         */
        function assignValue(object, key, value) {
            var objValue = object[key];
            if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
                (value === undefined && !(key in object))) {
                baseAssignValue(object, key, value);
            }
        }

        /**
         * The base implementation of `_.assign` without support for multiple sources
         * or `customizer` functions.
         *
         * @private
         * @param {Object} object The destination object.
         * @param {Object} source The source object.
         * @returns {Object} Returns `object`.
         */
        function baseAssign(object, source) {
            return object && copyObject(source, keys(source), object);
        }

        /**
         * The base implementation of `assignValue` and `assignMergeValue` without
         * value checks.
         *
         * @private
         * @param {Object} object The object to modify.
         * @param {string} key The key of the property to assign.
         * @param {*} value The value to assign.
         */
        function baseAssignValue(object, key, value) {
            if (key == '__proto__' && defineProperty) {
                defineProperty(object, key, {
                    'configurable': true,
                    'enumerable': true,
                    'value': value,
                    'writable': true
                });
            } else {
                object[key] = value;
            }
        }

        /**
         * The base implementation of `_.delay` and `_.defer` which accepts `args`
         * to provide to `func`.
         *
         * @private
         * @param {Function} func The function to delay.
         * @param {number} wait The number of milliseconds to delay invocation.
         * @param {Array} args The arguments to provide to `func`.
         * @returns {number|Object} Returns the timer id or timeout object.
         */
        function baseDelay(func, wait, args) {
            if (typeof func != 'function') {
                throw new TypeError(FUNC_ERROR_TEXT);
            }
            return setTimeout(function() { func.apply(undefined, args); }, wait);
        }

        /**
         * The base implementation of `getTag` without fallbacks for buggy environments.
         *
         * @private
         * @param {*} value The value to query.
         * @returns {string} Returns the `toStringTag`.
         */
        function baseGetTag(value) {
            if (value == null) {
                return value === undefined ? undefinedTag : nullTag;
            }
            return (symToStringTag && symToStringTag in Object(value))
                ? getRawTag(value)
                : objectToString(value);
        }

        /**
         * The base implementation of `_.isArguments`.
         *
         * @private
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is an `arguments` object,
         */
        function baseIsArguments(value) {
            return isObjectLike(value) && baseGetTag(value) == argsTag;
        }

        /**
         * The base implementation of `_.isNative` without bad shim checks.
         *
         * @private
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a native function,
         *  else `false`.
         */
        function baseIsNative(value) {
            if (!isObject(value) || isMasked(value)) {
                return false;
            }
            var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
            return pattern.test(toSource(value));
        }

        /**
         * The base implementation of `_.isTypedArray` without Node.js optimizations.
         *
         * @private
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
         */
        function baseIsTypedArray(value) {
            return isObjectLike(value) &&
                isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
        }

        /**
         * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
         *
         * @private
         * @param {Object} object The object to query.
         * @returns {Array} Returns the array of property names.
         */
        function baseKeys(object) {
            if (!isPrototype(object)) {
                return nativeKeys(object);
            }
            var result = [];
            for (var key in Object(object)) {
                if (hasOwnProperty.call(object, key) && key != 'constructor') {
                    result.push(key);
                }
            }
            return result;
        }

        /**
         * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
         *
         * @private
         * @param {Object} object The object to query.
         * @returns {Array} Returns the array of property names.
         */
        function baseKeysIn(object) {
            if (!isObject(object)) {
                return nativeKeysIn(object);
            }
            var isProto = isPrototype(object),
                result = [];

            for (var key in object) {
                if (!(key == 'constructor' && (isProto || !hasOwnProperty.call(object, key)))) {
                    result.push(key);
                }
            }
            return result;
        }

        /**
         * The base implementation of `_.rest` which doesn't validate or coerce arguments.
         *
         * @private
         * @param {Function} func The function to apply a rest parameter to.
         * @param {number} [start=func.length-1] The start position of the rest parameter.
         * @returns {Function} Returns the new function.
         */
        function baseRest(func, start) {
            return setToString(overRest(func, start, identity), func + '');
        }

        /**
         * The base implementation of `setData` without support for hot loop shorting.
         *
         * @private
         * @param {Function} func The function to associate metadata with.
         * @param {*} data The metadata.
         * @returns {Function} Returns `func`.
         */
        var baseSetData = !metaMap ? identity : function(func, data) {
            metaMap.set(func, data);
            return func;
        };

        /**
         * The base implementation of `setToString` without support for hot loop shorting.
         *
         * @private
         * @param {Function} func The function to modify.
         * @param {Function} string The `toString` result.
         * @returns {Function} Returns `func`.
         */
        var baseSetToString = !defineProperty ? identity : function(func, string) {
            return defineProperty(func, 'toString', {
                'configurable': true,
                'enumerable': false,
                'value': constant(string),
                'writable': true
            });
        };

        /**
         * Creates an array that is the composition of partially applied arguments,
         * placeholders, and provided arguments into a single array of arguments.
         *
         * @private
         * @param {Array} args The provided arguments.
         * @param {Array} partials The arguments to prepend to those provided.
         * @param {Array} holders The `partials` placeholder indexes.
         * @params {boolean} [isCurried] Specify composing for a curried function.
         * @returns {Array} Returns the new array of composed arguments.
         */
        function composeArgs(args, partials, holders, isCurried) {
            var argsIndex = -1,
                argsLength = args.length,
                holdersLength = holders.length,
                leftIndex = -1,
                leftLength = partials.length,
                rangeLength = nativeMax(argsLength - holdersLength, 0),
                result = Array(leftLength + rangeLength),
                isUncurried = !isCurried;

            while (++leftIndex < leftLength) {
                result[leftIndex] = partials[leftIndex];
            }
            while (++argsIndex < holdersLength) {
                if (isUncurried || argsIndex < argsLength) {
                    result[holders[argsIndex]] = args[argsIndex];
                }
            }
            while (rangeLength--) {
                result[leftIndex++] = args[argsIndex++];
            }
            return result;
        }

        /**
         * This function is like `composeArgs` except that the arguments composition
         * is tailored for `_.partialRight`.
         *
         * @private
         * @param {Array} args The provided arguments.
         * @param {Array} partials The arguments to append to those provided.
         * @param {Array} holders The `partials` placeholder indexes.
         * @params {boolean} [isCurried] Specify composing for a curried function.
         * @returns {Array} Returns the new array of composed arguments.
         */
        function composeArgsRight(args, partials, holders, isCurried) {
            var argsIndex = -1,
                argsLength = args.length,
                holdersIndex = -1,
                holdersLength = holders.length,
                rightIndex = -1,
                rightLength = partials.length,
                rangeLength = nativeMax(argsLength - holdersLength, 0),
                result = Array(rangeLength + rightLength),
                isUncurried = !isCurried;

            while (++argsIndex < rangeLength) {
                result[argsIndex] = args[argsIndex];
            }
            var offset = argsIndex;
            while (++rightIndex < rightLength) {
                result[offset + rightIndex] = partials[rightIndex];
            }
            while (++holdersIndex < holdersLength) {
                if (isUncurried || argsIndex < argsLength) {
                    result[offset + holders[holdersIndex]] = args[argsIndex++];
                }
            }
            return result;
        }

        /**
         * Copies the values of `source` to `array`.
         *
         * @private
         * @param {Array} source The array to copy values from.
         * @param {Array} [array=[]] The array to copy values to.
         * @returns {Array} Returns `array`.
         */
        function copyArray(source, array) {
            var index = -1,
                length = source.length;

            array || (array = Array(length));
            while (++index < length) {
                array[index] = source[index];
            }
            return array;
        }

        /**
         * Copies properties of `source` to `object`.
         *
         * @private
         * @param {Object} source The object to copy properties from.
         * @param {Array} props The property identifiers to copy.
         * @param {Object} [object={}] The object to copy properties to.
         * @param {Function} [customizer] The function to customize copied values.
         * @returns {Object} Returns `object`.
         */
        function copyObject(source, props, object, customizer) {
            var isNew = !object;
            object || (object = {});

            var index = -1,
                length = props.length;

            while (++index < length) {
                var key = props[index];

                var newValue = customizer
                    ? customizer(object[key], source[key], key, object, source)
                    : undefined;

                if (newValue === undefined) {
                    newValue = source[key];
                }
                if (isNew) {
                    baseAssignValue(object, key, newValue);
                } else {
                    assignValue(object, key, newValue);
                }
            }
            return object;
        }

        /**
         * Creates a function like `_.assign`.
         *
         * @private
         * @param {Function} assigner The function to assign values.
         * @returns {Function} Returns the new assigner function.
         */
        function createAssigner(assigner) {
            return baseRest(function(object, sources) {
                var index = -1,
                    length = sources.length,
                    customizer = length > 1 ? sources[length - 1] : undefined,
                    guard = length > 2 ? sources[2] : undefined;

                customizer = (assigner.length > 3 && typeof customizer == 'function')
                    ? (length--, customizer)
                    : undefined;

                if (guard && isIterateeCall(sources[0], sources[1], guard)) {
                    customizer = length < 3 ? undefined : customizer;
                    length = 1;
                }
                object = Object(object);
                while (++index < length) {
                    var source = sources[index];
                    if (source) {
                        assigner(object, source, index, customizer);
                    }
                }
                return object;
            });
        }

        /**
         * Creates a function that wraps `func` to invoke it with the optional `this`
         * binding of `thisArg`.
         *
         * @private
         * @param {Function} func The function to wrap.
         * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
         * @param {*} [thisArg] The `this` binding of `func`.
         * @returns {Function} Returns the new wrapped function.
         */
        function createBind(func, bitmask, thisArg) {
            var isBind = bitmask & WRAP_BIND_FLAG,
                Ctor = createCtor(func);

            function wrapper() {
                var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
                return fn.apply(isBind ? thisArg : this, arguments);
            }
            return wrapper;
        }

        /**
         * Creates a function that produces an instance of `Ctor` regardless of
         * whether it was invoked as part of a `new` expression or by `call` or `apply`.
         *
         * @private
         * @param {Function} Ctor The constructor to wrap.
         * @returns {Function} Returns the new wrapped function.
         */
        function createCtor(Ctor) {
            return function() {
                // Use a `switch` statement to work with class constructors. See
                // http://ecma-international.org/ecma-262/7.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
                // for more details.
                var args = arguments;
                switch (args.length) {
                    case 0: return new Ctor;
                    case 1: return new Ctor(args[0]);
                    case 2: return new Ctor(args[0], args[1]);
                    case 3: return new Ctor(args[0], args[1], args[2]);
                    case 4: return new Ctor(args[0], args[1], args[2], args[3]);
                    case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
                    case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
                    case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
                }
                var thisBinding = baseCreate(Ctor.prototype),
                    result = Ctor.apply(thisBinding, args);

                // Mimic the constructor's `return` behavior.
                // See https://es5.github.io/#x13.2.2 for more details.
                return isObject(result) ? result : thisBinding;
            };
        }

        /**
         * Creates a function that wraps `func` to enable currying.
         *
         * @private
         * @param {Function} func The function to wrap.
         * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
         * @param {number} arity The arity of `func`.
         * @returns {Function} Returns the new wrapped function.
         */
        function createCurry(func, bitmask, arity) {
            var Ctor = createCtor(func);

            function wrapper() {
                var length = arguments.length,
                    args = Array(length),
                    index = length,
                    placeholder = getHolder(wrapper);

                while (index--) {
                    args[index] = arguments[index];
                }
                var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
                    ? []
                    : replaceHolders(args, placeholder);

                length -= holders.length;
                if (length < arity) {
                    return createRecurry(
                        func, bitmask, createHybrid, wrapper.placeholder, undefined,
                        args, holders, undefined, undefined, arity - length);
                }
                var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
                return apply(fn, this, args);
            }
            return wrapper;
        }

        /**
         * Creates a function that wraps `func` to invoke it with optional `this`
         * binding of `thisArg`, partial application, and currying.
         *
         * @private
         * @param {Function|string} func The function or method name to wrap.
         * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
         * @param {*} [thisArg] The `this` binding of `func`.
         * @param {Array} [partials] The arguments to prepend to those provided to
         *  the new function.
         * @param {Array} [holders] The `partials` placeholder indexes.
         * @param {Array} [partialsRight] The arguments to append to those provided
         *  to the new function.
         * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
         * @param {Array} [argPos] The argument positions of the new function.
         * @param {number} [ary] The arity cap of `func`.
         * @param {number} [arity] The arity of `func`.
         * @returns {Function} Returns the new wrapped function.
         */
        function createHybrid(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
            var isAry = bitmask & WRAP_ARY_FLAG,
                isBind = bitmask & WRAP_BIND_FLAG,
                isBindKey = bitmask & WRAP_BIND_KEY_FLAG,
                isCurried = bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG),
                isFlip = bitmask & WRAP_FLIP_FLAG,
                Ctor = isBindKey ? undefined : createCtor(func);

            function wrapper() {
                var length = arguments.length,
                    args = Array(length),
                    index = length;

                while (index--) {
                    args[index] = arguments[index];
                }
                if (isCurried) {
                    var placeholder = getHolder(wrapper),
                        holdersCount = countHolders(args, placeholder);
                }
                if (partials) {
                    args = composeArgs(args, partials, holders, isCurried);
                }
                if (partialsRight) {
                    args = composeArgsRight(args, partialsRight, holdersRight, isCurried);
                }
                length -= holdersCount;
                if (isCurried && length < arity) {
                    var newHolders = replaceHolders(args, placeholder);
                    return createRecurry(
                        func, bitmask, createHybrid, wrapper.placeholder, thisArg,
                        args, newHolders, argPos, ary, arity - length
                    );
                }
                var thisBinding = isBind ? thisArg : this,
                    fn = isBindKey ? thisBinding[func] : func;

                length = args.length;
                if (argPos) {
                    args = reorder(args, argPos);
                } else if (isFlip && length > 1) {
                    args.reverse();
                }
                if (isAry && ary < length) {
                    args.length = ary;
                }
                if (this && this !== root && this instanceof wrapper) {
                    fn = Ctor || createCtor(fn);
                }
                return fn.apply(thisBinding, args);
            }
            return wrapper;
        }

        /**
         * Creates a function that wraps `func` to invoke it with the `this` binding
         * of `thisArg` and `partials` prepended to the arguments it receives.
         *
         * @private
         * @param {Function} func The function to wrap.
         * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
         * @param {*} thisArg The `this` binding of `func`.
         * @param {Array} partials The arguments to prepend to those provided to
         *  the new function.
         * @returns {Function} Returns the new wrapped function.
         */
        function createPartial(func, bitmask, thisArg, partials) {
            var isBind = bitmask & WRAP_BIND_FLAG,
                Ctor = createCtor(func);

            function wrapper() {
                var argsIndex = -1,
                    argsLength = arguments.length,
                    leftIndex = -1,
                    leftLength = partials.length,
                    args = Array(leftLength + argsLength),
                    fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;

                while (++leftIndex < leftLength) {
                    args[leftIndex] = partials[leftIndex];
                }
                while (argsLength--) {
                    args[leftIndex++] = arguments[++argsIndex];
                }
                return apply(fn, isBind ? thisArg : this, args);
            }
            return wrapper;
        }

        /**
         * Creates a function that wraps `func` to continue currying.
         *
         * @private
         * @param {Function} func The function to wrap.
         * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
         * @param {Function} wrapFunc The function to create the `func` wrapper.
         * @param {*} placeholder The placeholder value.
         * @param {*} [thisArg] The `this` binding of `func`.
         * @param {Array} [partials] The arguments to prepend to those provided to
         *  the new function.
         * @param {Array} [holders] The `partials` placeholder indexes.
         * @param {Array} [argPos] The argument positions of the new function.
         * @param {number} [ary] The arity cap of `func`.
         * @param {number} [arity] The arity of `func`.
         * @returns {Function} Returns the new wrapped function.
         */
        function createRecurry(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
            var isCurry = bitmask & WRAP_CURRY_FLAG,
                newHolders = isCurry ? holders : undefined,
                newHoldersRight = isCurry ? undefined : holders,
                newPartials = isCurry ? partials : undefined,
                newPartialsRight = isCurry ? undefined : partials;

            bitmask |= (isCurry ? WRAP_PARTIAL_FLAG : WRAP_PARTIAL_RIGHT_FLAG);
            bitmask &= ~(isCurry ? WRAP_PARTIAL_RIGHT_FLAG : WRAP_PARTIAL_FLAG);

            if (!(bitmask & WRAP_CURRY_BOUND_FLAG)) {
                bitmask &= ~(WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG);
            }
            var newData = [
                func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
                newHoldersRight, argPos, ary, arity
            ];

            var result = wrapFunc.apply(undefined, newData);
            if (isLaziable(func)) {
                setData(result, newData);
            }
            result.placeholder = placeholder;
            return setWrapToString(result, func, bitmask);
        }

        /**
         * Creates a function that either curries or invokes `func` with optional
         * `this` binding and partially applied arguments.
         *
         * @private
         * @param {Function|string} func The function or method name to wrap.
         * @param {number} bitmask The bitmask flags.
         *    1 - `_.bind`
         *    2 - `_.bindKey`
         *    4 - `_.curry` or `_.curryRight` of a bound function
         *    8 - `_.curry`
         *   16 - `_.curryRight`
         *   32 - `_.partial`
         *   64 - `_.partialRight`
         *  128 - `_.rearg`
         *  256 - `_.ary`
         *  512 - `_.flip`
         * @param {*} [thisArg] The `this` binding of `func`.
         * @param {Array} [partials] The arguments to be partially applied.
         * @param {Array} [holders] The `partials` placeholder indexes.
         * @param {Array} [argPos] The argument positions of the new function.
         * @param {number} [ary] The arity cap of `func`.
         * @param {number} [arity] The arity of `func`.
         * @returns {Function} Returns the new wrapped function.
         */
        function createWrap(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
            var isBindKey = bitmask & WRAP_BIND_KEY_FLAG;
            if (!isBindKey && typeof func != 'function') {
                throw new TypeError(FUNC_ERROR_TEXT);
            }
            var length = partials ? partials.length : 0;
            if (!length) {
                bitmask &= ~(WRAP_PARTIAL_FLAG | WRAP_PARTIAL_RIGHT_FLAG);
                partials = holders = undefined;
            }
            ary = ary === undefined ? ary : nativeMax(toInteger(ary), 0);
            arity = arity === undefined ? arity : toInteger(arity);
            length -= holders ? holders.length : 0;

            if (bitmask & WRAP_PARTIAL_RIGHT_FLAG) {
                var partialsRight = partials,
                    holdersRight = holders;

                partials = holders = undefined;
            }
            var data = isBindKey ? undefined : getData(func);

            var newData = [
                func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
                argPos, ary, arity
            ];

            if (data) {
                mergeData(newData, data);
            }
            func = newData[0];
            bitmask = newData[1];
            thisArg = newData[2];
            partials = newData[3];
            holders = newData[4];
            arity = newData[9] = newData[9] === undefined
                ? (isBindKey ? 0 : func.length)
                : nativeMax(newData[9] - length, 0);

            if (!arity && bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG)) {
                bitmask &= ~(WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG);
            }
            if (!bitmask || bitmask == WRAP_BIND_FLAG) {
                var result = createBind(func, bitmask, thisArg);
            } else if (bitmask == WRAP_CURRY_FLAG || bitmask == WRAP_CURRY_RIGHT_FLAG) {
                result = createCurry(func, bitmask, arity);
            } else if ((bitmask == WRAP_PARTIAL_FLAG || bitmask == (WRAP_BIND_FLAG | WRAP_PARTIAL_FLAG)) && !holders.length) {
                result = createPartial(func, bitmask, thisArg, partials);
            } else {
                result = createHybrid.apply(undefined, newData);
            }
            var setter = data ? baseSetData : setData;
            return setWrapToString(setter(result, newData), func, bitmask);
        }

        /**
         * Gets metadata for `func`.
         *
         * @private
         * @param {Function} func The function to query.
         * @returns {*} Returns the metadata for `func`.
         */
        var getData = !metaMap ? noop : function(func) {
            return metaMap.get(func);
        };

        /**
         * Gets the name of `func`.
         *
         * @private
         * @param {Function} func The function to query.
         * @returns {string} Returns the function name.
         */
        function getFuncName(func) {
            var result = (func.name + ''),
                array = realNames[result],
                length = hasOwnProperty.call(realNames, result) ? array.length : 0;

            while (length--) {
                var data = array[length],
                    otherFunc = data.func;
                if (otherFunc == null || otherFunc == func) {
                    return data.name;
                }
            }
            return result;
        }

        /**
         * Gets the argument placeholder value for `func`.
         *
         * @private
         * @param {Function} func The function to inspect.
         * @returns {*} Returns the placeholder value.
         */
        function getHolder(func) {
            var object = hasOwnProperty.call(lodash, 'placeholder') ? lodash : func;
            return object.placeholder;
        }

        /**
         * Gets the native function at `key` of `object`.
         *
         * @private
         * @param {Object} object The object to query.
         * @param {string} key The key of the method to get.
         * @returns {*} Returns the function if it's native, else `undefined`.
         */
        function getNative(object, key) {
            var value = getValue(object, key);
            return baseIsNative(value) ? value : undefined;
        }

        /**
         * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
         *
         * @private
         * @param {*} value The value to query.
         * @returns {string} Returns the raw `toStringTag`.
         */
        function getRawTag(value) {
            var isOwn = hasOwnProperty.call(value, symToStringTag),
                tag = value[symToStringTag];

            try {
                value[symToStringTag] = undefined;
                var unmasked = true;
            } catch (e) {}

            var result = nativeObjectToString.call(value);
            if (unmasked) {
                if (isOwn) {
                    value[symToStringTag] = tag;
                } else {
                    delete value[symToStringTag];
                }
            }
            return result;
        }

        /**
         * Extracts wrapper details from the `source` body comment.
         *
         * @private
         * @param {string} source The source to inspect.
         * @returns {Array} Returns the wrapper details.
         */
        function getWrapDetails(source) {
            var match = source.match(reWrapDetails);
            return match ? match[1].split(reSplitDetails) : [];
        }

        /**
         * Inserts wrapper `details` in a comment at the top of the `source` body.
         *
         * @private
         * @param {string} source The source to modify.
         * @returns {Array} details The details to insert.
         * @returns {string} Returns the modified source.
         */
        function insertWrapDetails(source, details) {
            var length = details.length;
            if (!length) {
                return source;
            }
            var lastIndex = length - 1;
            details[lastIndex] = (length > 1 ? '& ' : '') + details[lastIndex];
            details = details.join(length > 2 ? ', ' : ' ');
            return source.replace(reWrapComment, '{\n/* [wrapped with ' + details + '] */\n');
        }

        /**
         * Checks if `value` is a valid array-like index.
         *
         * @private
         * @param {*} value The value to check.
         * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
         * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
         */
        function isIndex(value, length) {
            var type = typeof value;
            length = length == null ? MAX_SAFE_INTEGER : length;

            return !!length &&
                (type == 'number' ||
                    (type != 'symbol' && reIsUint.test(value))) &&
                (value > -1 && value % 1 == 0 && value < length);
        }

        /**
         * Checks if the given arguments are from an iteratee call.
         *
         * @private
         * @param {*} value The potential iteratee value argument.
         * @param {*} index The potential iteratee index or key argument.
         * @param {*} object The potential iteratee object argument.
         * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
         *  else `false`.
         */
        function isIterateeCall(value, index, object) {
            if (!isObject(object)) {
                return false;
            }
            var type = typeof index;
            if (type == 'number'
                ? (isArrayLike(object) && isIndex(index, object.length))
                : (type == 'string' && index in object)
            ) {
                return eq(object[index], value);
            }
            return false;
        }

        /**
         * Checks if `func` has a lazy counterpart.
         *
         * @private
         * @param {Function} func The function to check.
         * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
         *  else `false`.
         */
        function isLaziable(func) {
            var funcName = getFuncName(func),
                other = lodash[funcName];

            if (typeof other != 'function' || !(funcName in LazyWrapper.prototype)) {
                return false;
            }
            if (func === other) {
                return true;
            }
            var data = getData(other);
            return !!data && func === data[0];
        }

        /**
         * Checks if `func` has its source masked.
         *
         * @private
         * @param {Function} func The function to check.
         * @returns {boolean} Returns `true` if `func` is masked, else `false`.
         */
        function isMasked(func) {
            return !!maskSrcKey && (maskSrcKey in func);
        }

        /**
         * Checks if `value` is likely a prototype object.
         *
         * @private
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
         */
        function isPrototype(value) {
            var Ctor = value && value.constructor,
                proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto;

            return value === proto;
        }

        /**
         * Merges the function metadata of `source` into `data`.
         *
         * Merging metadata reduces the number of wrappers used to invoke a function.
         * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
         * may be applied regardless of execution order. Methods like `_.ary` and
         * `_.rearg` modify function arguments, making the order in which they are
         * executed important, preventing the merging of metadata. However, we make
         * an exception for a safe combined case where curried functions have `_.ary`
         * and or `_.rearg` applied.
         *
         * @private
         * @param {Array} data The destination metadata.
         * @param {Array} source The source metadata.
         * @returns {Array} Returns `data`.
         */
        function mergeData(data, source) {
            var bitmask = data[1],
                srcBitmask = source[1],
                newBitmask = bitmask | srcBitmask,
                isCommon = newBitmask < (WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG | WRAP_ARY_FLAG);

            var isCombo =
                ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_CURRY_FLAG)) ||
                ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_REARG_FLAG) && (data[7].length <= source[8])) ||
                ((srcBitmask == (WRAP_ARY_FLAG | WRAP_REARG_FLAG)) && (source[7].length <= source[8]) && (bitmask == WRAP_CURRY_FLAG));

            // Exit early if metadata can't be merged.
            if (!(isCommon || isCombo)) {
                return data;
            }
            // Use source `thisArg` if available.
            if (srcBitmask & WRAP_BIND_FLAG) {
                data[2] = source[2];
                // Set when currying a bound function.
                newBitmask |= bitmask & WRAP_BIND_FLAG ? 0 : WRAP_CURRY_BOUND_FLAG;
            }
            // Compose partial arguments.
            var value = source[3];
            if (value) {
                var partials = data[3];
                data[3] = partials ? composeArgs(partials, value, source[4]) : value;
                data[4] = partials ? replaceHolders(data[3], PLACEHOLDER) : source[4];
            }
            // Compose partial right arguments.
            value = source[5];
            if (value) {
                partials = data[5];
                data[5] = partials ? composeArgsRight(partials, value, source[6]) : value;
                data[6] = partials ? replaceHolders(data[5], PLACEHOLDER) : source[6];
            }
            // Use source `argPos` if available.
            value = source[7];
            if (value) {
                data[7] = value;
            }
            // Use source `ary` if it's smaller.
            if (srcBitmask & WRAP_ARY_FLAG) {
                data[8] = data[8] == null ? source[8] : nativeMin(data[8], source[8]);
            }
            // Use source `arity` if one is not provided.
            if (data[9] == null) {
                data[9] = source[9];
            }
            // Use source `func` and merge bitmasks.
            data[0] = source[0];
            data[1] = newBitmask;

            return data;
        }

        /**
         * This function is like
         * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
         * except that it includes inherited enumerable properties.
         *
         * @private
         * @param {Object} object The object to query.
         * @returns {Array} Returns the array of property names.
         */
        function nativeKeysIn(object) {
            var result = [];
            if (object != null) {
                for (var key in Object(object)) {
                    result.push(key);
                }
            }
            return result;
        }

        /**
         * Converts `value` to a string using `Object.prototype.toString`.
         *
         * @private
         * @param {*} value The value to convert.
         * @returns {string} Returns the converted string.
         */
        function objectToString(value) {
            return nativeObjectToString.call(value);
        }

        /**
         * A specialized version of `baseRest` which transforms the rest array.
         *
         * @private
         * @param {Function} func The function to apply a rest parameter to.
         * @param {number} [start=func.length-1] The start position of the rest parameter.
         * @param {Function} transform The rest array transform.
         * @returns {Function} Returns the new function.
         */
        function overRest(func, start, transform) {
            start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
            return function() {
                var args = arguments,
                    index = -1,
                    length = nativeMax(args.length - start, 0),
                    array = Array(length);

                while (++index < length) {
                    array[index] = args[start + index];
                }
                index = -1;
                var otherArgs = Array(start + 1);
                while (++index < start) {
                    otherArgs[index] = args[index];
                }
                otherArgs[start] = transform(array);
                return apply(func, this, otherArgs);
            };
        }

        /**
         * Reorder `array` according to the specified indexes where the element at
         * the first index is assigned as the first element, the element at
         * the second index is assigned as the second element, and so on.
         *
         * @private
         * @param {Array} array The array to reorder.
         * @param {Array} indexes The arranged array indexes.
         * @returns {Array} Returns `array`.
         */
        function reorder(array, indexes) {
            var arrLength = array.length,
                length = nativeMin(indexes.length, arrLength),
                oldArray = copyArray(array);

            while (length--) {
                var index = indexes[length];
                array[length] = isIndex(index, arrLength) ? oldArray[index] : undefined;
            }
            return array;
        }

        /**
         * Sets metadata for `func`.
         *
         * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
         * period of time, it will trip its breaker and transition to an identity
         * function to avoid garbage collection pauses in V8. See
         * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
         * for more details.
         *
         * @private
         * @param {Function} func The function to associate metadata with.
         * @param {*} data The metadata.
         * @returns {Function} Returns `func`.
         */
        var setData = shortOut(baseSetData);

        /**
         * Sets the `toString` method of `func` to return `string`.
         *
         * @private
         * @param {Function} func The function to modify.
         * @param {Function} string The `toString` result.
         * @returns {Function} Returns `func`.
         */
        var setToString = shortOut(baseSetToString);

        /**
         * Sets the `toString` method of `wrapper` to mimic the source of `reference`
         * with wrapper details in a comment at the top of the source body.
         *
         * @private
         * @param {Function} wrapper The function to modify.
         * @param {Function} reference The reference function.
         * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
         * @returns {Function} Returns `wrapper`.
         */
        function setWrapToString(wrapper, reference, bitmask) {
            var source = (reference + '');
            return setToString(wrapper, insertWrapDetails(source, updateWrapDetails(getWrapDetails(source), bitmask)));
        }

        /**
         * Creates a function that'll short out and invoke `identity` instead
         * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
         * milliseconds.
         *
         * @private
         * @param {Function} func The function to restrict.
         * @returns {Function} Returns the new shortable function.
         */
        function shortOut(func) {
            var count = 0,
                lastCalled = 0;

            return function() {
                var stamp = nativeNow(),
                    remaining = HOT_SPAN - (stamp - lastCalled);

                lastCalled = stamp;
                if (remaining > 0) {
                    if (++count >= HOT_COUNT) {
                        return arguments[0];
                    }
                } else {
                    count = 0;
                }
                return func.apply(undefined, arguments);
            };
        }

        /**
         * Converts `func` to its source code.
         *
         * @private
         * @param {Function} func The function to convert.
         * @returns {string} Returns the source code.
         */
        function toSource(func) {
            if (func != null) {
                try {
                    return funcToString.call(func);
                } catch (e) {}
                try {
                    return (func + '');
                } catch (e) {}
            }
            return '';
        }

        /**
         * Updates wrapper `details` based on `bitmask` flags.
         *
         * @private
         * @returns {Array} details The details to modify.
         * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
         * @returns {Array} Returns `details`.
         */
        function updateWrapDetails(details, bitmask) {
            arrayEach(wrapFlags, function(pair) {
                var value = '_.' + pair[0];
                if ((bitmask & pair[1]) && !arrayIncludes(details, value)) {
                    details.push(value);
                }
            });
            return details.sort();
        }

        /*------------------------------------------------------------------------*/

        /**
         * Gets the timestamp of the number of milliseconds that have elapsed since
         * the Unix epoch (1 January 1970 00:00:00 UTC).
         *
         * @static
         * @memberOf _
         * @since 2.4.0
         * @category Date
         * @returns {number} Returns the timestamp.
         * @example
         *
         * _.defer(function(stamp) {
         *   console.log(_.now() - stamp);
         * }, _.now());
         * // => Logs the number of milliseconds it took for the deferred invocation.
         */
        var now = function() {
            return root.Date.now();
        };

        /*------------------------------------------------------------------------*/

        /**
         * Creates a function that invokes `func` with the `this` binding of `thisArg`
         * and `partials` prepended to the arguments it receives.
         *
         * The `_.bind.placeholder` value, which defaults to `_` in monolithic builds,
         * may be used as a placeholder for partially applied arguments.
         *
         * **Note:** Unlike native `Function#bind`, this method doesn't set the "length"
         * property of bound functions.
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Function
         * @param {Function} func The function to bind.
         * @param {*} thisArg The `this` binding of `func`.
         * @param {...*} [partials] The arguments to be partially applied.
         * @returns {Function} Returns the new bound function.
         * @example
         *
         * function greet(greeting, punctuation) {
         *   return greeting + ' ' + this.user + punctuation;
         * }
         *
         * var object = { 'user': 'fred' };
         *
         * var bound = _.bind(greet, object, 'hi');
         * bound('!');
         * // => 'hi fred!'
         *
         * // Bound with placeholders.
         * var bound = _.bind(greet, object, _, '!');
         * bound('hi');
         * // => 'hi fred!'
         */
        var bind = baseRest(function(func, thisArg, partials) {
            var bitmask = WRAP_BIND_FLAG;
            if (partials.length) {
                var holders = replaceHolders(partials, getHolder(bind));
                bitmask |= WRAP_PARTIAL_FLAG;
            }
            return createWrap(func, bitmask, thisArg, partials, holders);
        });

        /**
         * Creates a debounced function that delays invoking `func` until after `wait`
         * milliseconds have elapsed since the last time the debounced function was
         * invoked. The debounced function comes with a `cancel` method to cancel
         * delayed `func` invocations and a `flush` method to immediately invoke them.
         * Provide `options` to indicate whether `func` should be invoked on the
         * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
         * with the last arguments provided to the debounced function. Subsequent
         * calls to the debounced function return the result of the last `func`
         * invocation.
         *
         * **Note:** If `leading` and `trailing` options are `true`, `func` is
         * invoked on the trailing edge of the timeout only if the debounced function
         * is invoked more than once during the `wait` timeout.
         *
         * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
         * until to the next tick, similar to `setTimeout` with a timeout of `0`.
         *
         * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
         * for details over the differences between `_.debounce` and `_.throttle`.
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Function
         * @param {Function} func The function to debounce.
         * @param {number} [wait=0] The number of milliseconds to delay.
         * @param {Object} [options={}] The options object.
         * @param {boolean} [options.leading=false]
         *  Specify invoking on the leading edge of the timeout.
         * @param {number} [options.maxWait]
         *  The maximum time `func` is allowed to be delayed before it's invoked.
         * @param {boolean} [options.trailing=true]
         *  Specify invoking on the trailing edge of the timeout.
         * @returns {Function} Returns the new debounced function.
         * @example
         *
         * // Avoid costly calculations while the window size is in flux.
         * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
         *
         * // Invoke `sendMail` when clicked, debouncing subsequent calls.
         * jQuery(element).on('click', _.debounce(sendMail, 300, {
         *   'leading': true,
         *   'trailing': false
         * }));
         *
         * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
         * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
         * var source = new EventSource('/stream');
         * jQuery(source).on('message', debounced);
         *
         * // Cancel the trailing debounced invocation.
         * jQuery(window).on('popstate', debounced.cancel);
         */
        function debounce(func, wait, options) {
            var lastArgs,
                lastThis,
                maxWait,
                result,
                timerId,
                lastCallTime,
                lastInvokeTime = 0,
                leading = false,
                maxing = false,
                trailing = true;

            if (typeof func != 'function') {
                throw new TypeError(FUNC_ERROR_TEXT);
            }
            wait = toNumber(wait) || 0;
            if (isObject(options)) {
                leading = !!options.leading;
                maxing = 'maxWait' in options;
                maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
                trailing = 'trailing' in options ? !!options.trailing : trailing;
            }

            function invokeFunc(time) {
                var args = lastArgs,
                    thisArg = lastThis;

                lastArgs = lastThis = undefined;
                lastInvokeTime = time;
                result = func.apply(thisArg, args);
                return result;
            }

            function leadingEdge(time) {
                // Reset any `maxWait` timer.
                lastInvokeTime = time;
                // Start the timer for the trailing edge.
                timerId = setTimeout(timerExpired, wait);
                // Invoke the leading edge.
                return leading ? invokeFunc(time) : result;
            }

            function remainingWait(time) {
                var timeSinceLastCall = time - lastCallTime,
                    timeSinceLastInvoke = time - lastInvokeTime,
                    timeWaiting = wait - timeSinceLastCall;

                return maxing
                    ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
                    : timeWaiting;
            }

            function shouldInvoke(time) {
                var timeSinceLastCall = time - lastCallTime,
                    timeSinceLastInvoke = time - lastInvokeTime;

                // Either this is the first call, activity has stopped and we're at the
                // trailing edge, the system time has gone backwards and we're treating
                // it as the trailing edge, or we've hit the `maxWait` limit.
                return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
                    (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
            }

            function timerExpired() {
                var time = now();
                if (shouldInvoke(time)) {
                    return trailingEdge(time);
                }
                // Restart the timer.
                timerId = setTimeout(timerExpired, remainingWait(time));
            }

            function trailingEdge(time) {
                timerId = undefined;

                // Only invoke if we have `lastArgs` which means `func` has been
                // debounced at least once.
                if (trailing && lastArgs) {
                    return invokeFunc(time);
                }
                lastArgs = lastThis = undefined;
                return result;
            }

            function cancel() {
                if (timerId !== undefined) {
                    clearTimeout(timerId);
                }
                lastInvokeTime = 0;
                lastArgs = lastCallTime = lastThis = timerId = undefined;
            }

            function flush() {
                return timerId === undefined ? result : trailingEdge(now());
            }

            function debounced() {
                var time = now(),
                    isInvoking = shouldInvoke(time);

                lastArgs = arguments;
                lastThis = this;
                lastCallTime = time;

                if (isInvoking) {
                    if (timerId === undefined) {
                        return leadingEdge(lastCallTime);
                    }
                    if (maxing) {
                        // Handle invocations in a tight loop.
                        clearTimeout(timerId);
                        timerId = setTimeout(timerExpired, wait);
                        return invokeFunc(lastCallTime);
                    }
                }
                if (timerId === undefined) {
                    timerId = setTimeout(timerExpired, wait);
                }
                return result;
            }
            debounced.cancel = cancel;
            debounced.flush = flush;
            return debounced;
        }

        /**
         * Defers invoking the `func` until the current call stack has cleared. Any
         * additional arguments are provided to `func` when it's invoked.
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Function
         * @param {Function} func The function to defer.
         * @param {...*} [args] The arguments to invoke `func` with.
         * @returns {number} Returns the timer id.
         * @example
         *
         * _.defer(function(text) {
         *   console.log(text);
         * }, 'deferred');
         * // => Logs 'deferred' after one millisecond.
         */
        var defer = baseRest(function(func, args) {
            return baseDelay(func, 1, args);
        });

        /**
         * Invokes `func` after `wait` milliseconds. Any additional arguments are
         * provided to `func` when it's invoked.
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Function
         * @param {Function} func The function to delay.
         * @param {number} wait The number of milliseconds to delay invocation.
         * @param {...*} [args] The arguments to invoke `func` with.
         * @returns {number} Returns the timer id.
         * @example
         *
         * _.delay(function(text) {
         *   console.log(text);
         * }, 1000, 'later');
         * // => Logs 'later' after one second.
         */
        var delay = baseRest(function(func, wait, args) {
            return baseDelay(func, toNumber(wait) || 0, args);
        });

        /**
         * Creates a function that invokes `func` with `partials` prepended to the
         * arguments it receives. This method is like `_.bind` except it does **not**
         * alter the `this` binding.
         *
         * The `_.partial.placeholder` value, which defaults to `_` in monolithic
         * builds, may be used as a placeholder for partially applied arguments.
         *
         * **Note:** This method doesn't set the "length" property of partially
         * applied functions.
         *
         * @static
         * @memberOf _
         * @since 0.2.0
         * @category Function
         * @param {Function} func The function to partially apply arguments to.
         * @param {...*} [partials] The arguments to be partially applied.
         * @returns {Function} Returns the new partially applied function.
         * @example
         *
         * function greet(greeting, name) {
         *   return greeting + ' ' + name;
         * }
         *
         * var sayHelloTo = _.partial(greet, 'hello');
         * sayHelloTo('fred');
         * // => 'hello fred'
         *
         * // Partially applied with placeholders.
         * var greetFred = _.partial(greet, _, 'fred');
         * greetFred('hi');
         * // => 'hi fred'
         */
        var partial = baseRest(function(func, partials) {
            var holders = replaceHolders(partials, getHolder(partial));
            return createWrap(func, WRAP_PARTIAL_FLAG, undefined, partials, holders);
        });

        /**
         * Creates a throttled function that only invokes `func` at most once per
         * every `wait` milliseconds. The throttled function comes with a `cancel`
         * method to cancel delayed `func` invocations and a `flush` method to
         * immediately invoke them. Provide `options` to indicate whether `func`
         * should be invoked on the leading and/or trailing edge of the `wait`
         * timeout. The `func` is invoked with the last arguments provided to the
         * throttled function. Subsequent calls to the throttled function return the
         * result of the last `func` invocation.
         *
         * **Note:** If `leading` and `trailing` options are `true`, `func` is
         * invoked on the trailing edge of the timeout only if the throttled function
         * is invoked more than once during the `wait` timeout.
         *
         * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
         * until to the next tick, similar to `setTimeout` with a timeout of `0`.
         *
         * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
         * for details over the differences between `_.throttle` and `_.debounce`.
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Function
         * @param {Function} func The function to throttle.
         * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
         * @param {Object} [options={}] The options object.
         * @param {boolean} [options.leading=true]
         *  Specify invoking on the leading edge of the timeout.
         * @param {boolean} [options.trailing=true]
         *  Specify invoking on the trailing edge of the timeout.
         * @returns {Function} Returns the new throttled function.
         * @example
         *
         * // Avoid excessively updating the position while scrolling.
         * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
         *
         * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
         * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
         * jQuery(element).on('click', throttled);
         *
         * // Cancel the trailing throttled invocation.
         * jQuery(window).on('popstate', throttled.cancel);
         */
        function throttle(func, wait, options) {
            var leading = true,
                trailing = true;

            if (typeof func != 'function') {
                throw new TypeError(FUNC_ERROR_TEXT);
            }
            if (isObject(options)) {
                leading = 'leading' in options ? !!options.leading : leading;
                trailing = 'trailing' in options ? !!options.trailing : trailing;
            }
            return debounce(func, wait, {
                'leading': leading,
                'maxWait': wait,
                'trailing': trailing
            });
        }

        /*------------------------------------------------------------------------*/

        /**
         * Performs a
         * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
         * comparison between two values to determine if they are equivalent.
         *
         * @static
         * @memberOf _
         * @since 4.0.0
         * @category Lang
         * @param {*} value The value to compare.
         * @param {*} other The other value to compare.
         * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
         * @example
         *
         * var object = { 'a': 1 };
         * var other = { 'a': 1 };
         *
         * _.eq(object, object);
         * // => true
         *
         * _.eq(object, other);
         * // => false
         *
         * _.eq('a', 'a');
         * // => true
         *
         * _.eq('a', Object('a'));
         * // => false
         *
         * _.eq(NaN, NaN);
         * // => true
         */
        function eq(value, other) {
            return value === other || (value !== value && other !== other);
        }

        /**
         * Checks if `value` is likely an `arguments` object.
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is an `arguments` object,
         *  else `false`.
         * @example
         *
         * _.isArguments(function() { return arguments; }());
         * // => true
         *
         * _.isArguments([1, 2, 3]);
         * // => false
         */
        var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
            return isObjectLike(value) && hasOwnProperty.call(value, 'callee') &&
                !propertyIsEnumerable.call(value, 'callee');
        };

        /**
         * Checks if `value` is classified as an `Array` object.
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is an array, else `false`.
         * @example
         *
         * _.isArray([1, 2, 3]);
         * // => true
         *
         * _.isArray(document.body.children);
         * // => false
         *
         * _.isArray('abc');
         * // => false
         *
         * _.isArray(_.noop);
         * // => false
         */
        var isArray = Array.isArray;

        /**
         * Checks if `value` is array-like. A value is considered array-like if it's
         * not a function and has a `value.length` that's an integer greater than or
         * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
         *
         * @static
         * @memberOf _
         * @since 4.0.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
         * @example
         *
         * _.isArrayLike([1, 2, 3]);
         * // => true
         *
         * _.isArrayLike(document.body.children);
         * // => true
         *
         * _.isArrayLike('abc');
         * // => true
         *
         * _.isArrayLike(_.noop);
         * // => false
         */
        function isArrayLike(value) {
            return value != null && isLength(value.length) && !isFunction(value);
        }

        /**
         * Checks if `value` is a buffer.
         *
         * @static
         * @memberOf _
         * @since 4.3.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
         * @example
         *
         * _.isBuffer(new Buffer(2));
         * // => true
         *
         * _.isBuffer(new Uint8Array(2));
         * // => false
         */
        var isBuffer = nativeIsBuffer || stubFalse;

        /**
         * Checks if `value` is classified as a `Function` object.
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a function, else `false`.
         * @example
         *
         * _.isFunction(_);
         * // => true
         *
         * _.isFunction(/abc/);
         * // => false
         */
        function isFunction(value) {
            if (!isObject(value)) {
                return false;
            }
            // The use of `Object#toString` avoids issues with the `typeof` operator
            // in Safari 9 which returns 'object' for typed arrays and other constructors.
            var tag = baseGetTag(value);
            return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
        }

        /**
         * Checks if `value` is a valid array-like length.
         *
         * **Note:** This method is loosely based on
         * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
         *
         * @static
         * @memberOf _
         * @since 4.0.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
         * @example
         *
         * _.isLength(3);
         * // => true
         *
         * _.isLength(Number.MIN_VALUE);
         * // => false
         *
         * _.isLength(Infinity);
         * // => false
         *
         * _.isLength('3');
         * // => false
         */
        function isLength(value) {
            return typeof value == 'number' &&
                value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
        }

        /**
         * Checks if `value` is the
         * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
         * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
         *
         * @static
         * @memberOf _
         * @since 0.1.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is an object, else `false`.
         * @example
         *
         * _.isObject({});
         * // => true
         *
         * _.isObject([1, 2, 3]);
         * // => true
         *
         * _.isObject(_.noop);
         * // => true
         *
         * _.isObject(null);
         * // => false
         */
        function isObject(value) {
            var type = typeof value;
            return value != null && (type == 'object' || type == 'function');
        }

        /**
         * Checks if `value` is object-like. A value is object-like if it's not `null`
         * and has a `typeof` result of "object".
         *
         * @static
         * @memberOf _
         * @since 4.0.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
         * @example
         *
         * _.isObjectLike({});
         * // => true
         *
         * _.isObjectLike([1, 2, 3]);
         * // => true
         *
         * _.isObjectLike(_.noop);
         * // => false
         *
         * _.isObjectLike(null);
         * // => false
         */
        function isObjectLike(value) {
            return value != null && typeof value == 'object';
        }

        /**
         * Checks if `value` is classified as a `String` primitive or object.
         *
         * @static
         * @since 0.1.0
         * @memberOf _
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a string, else `false`.
         * @example
         *
         * _.isString('abc');
         * // => true
         *
         * _.isString(1);
         * // => false
         */
        function isString(value) {
            return typeof value == 'string' ||
                (!isArray(value) && isObjectLike(value) && baseGetTag(value) == stringTag);
        }

        /**
         * Checks if `value` is classified as a `Symbol` primitive or object.
         *
         * @static
         * @memberOf _
         * @since 4.0.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
         * @example
         *
         * _.isSymbol(Symbol.iterator);
         * // => true
         *
         * _.isSymbol('abc');
         * // => false
         */
        function isSymbol(value) {
            return typeof value == 'symbol' ||
                (isObjectLike(value) && baseGetTag(value) == symbolTag);
        }

        /**
         * Checks if `value` is classified as a typed array.
         *
         * @static
         * @memberOf _
         * @since 3.0.0
         * @category Lang
         * @param {*} value The value to check.
         * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
         * @example
         *
         * _.isTypedArray(new Uint8Array);
         * // => true
         *
         * _.isTypedArray([]);
         * // => false
         */
        var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

        /**
         * Converts `value` to a finite number.
         *
         * @static
         * @memberOf _
         * @since 4.12.0
         * @category Lang
         * @param {*} value The value to convert.
         * @returns {number} Returns the converted number.
         * @example
         *
         * _.toFinite(3.2);
         * // => 3.2
         *
         * _.toFinite(Number.MIN_VALUE);
         * // => 5e-324
         *
         * _.toFinite(Infinity);
         * // => 1.7976931348623157e+308
         *
         * _.toFinite('3.2');
         * // => 3.2
         */
        function toFinite(value) {
            if (!value) {
                return value === 0 ? value : 0;
            }
            value = toNumber(value);
            if (value === INFINITY || value === -INFINITY) {
                var sign = (value < 0 ? -1 : 1);
                return sign * MAX_INTEGER;
            }
            return value === value ? value : 0;
        }

        /**
         * Converts `value` to an integer.
         *
         * **Note:** This method is loosely based on
         * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
         *
         * @static
         * @memberOf _
         * @since 4.0.0
         * @category Lang
         * @param {*} value The value to convert.
         * @returns {number} Returns the converted integer.
         * @example
         *
         * _.toInteger(3.2);
         * // => 3
         *
         * _.toInteger(Number.MIN_VALUE);
         * // => 0
         *
         * _.toInteger(Infinity);
         * // => 1.7976931348623157e+308
         *
         * _.toInteger('3.2');
         * // => 3
         */
        function toInteger(value) {
            var result = toFinite(value),
                remainder = result % 1;

            return result === result ? (remainder ? result - remainder : result) : 0;
        }

        /**
         * Converts `value` to a number.
         *
         * @static
         * @memberOf _
         * @since 4.0.0
         * @category Lang
         * @param {*} value The value to process.
         * @returns {number} Returns the number.
         * @example
         *
         * _.toNumber(3.2);
         * // => 3.2
         *
         * _.toNumber(Number.MIN_VALUE);
         * // => 5e-324
         *
         * _.toNumber(Infinity);
         * // => Infinity
         *
         * _.toNumber('3.2');
         * // => 3.2
         */
        function toNumber(value) {
            if (typeof value == 'number') {
                return value;
            }
            if (isSymbol(value)) {
                return NAN;
            }
            if (isObject(value)) {
                var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
                value = isObject(other) ? (other + '') : other;
            }
            if (typeof value != 'string') {
                return value === 0 ? value : +value;
            }
            value = value.replace(reTrim, '');
            var isBinary = reIsBinary.test(value);
            return (isBinary || reIsOctal.test(value))
                ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
                : (reIsBadHex.test(value) ? NAN : +value);
        }

        /*------------------------------------------------------------------------*/

        /**
         * Assigns own enumerable string keyed properties of source objects to the
         * destination object. Source objects are applied from left to right.
         * Subsequent sources overwrite property assignments of previous sources.
         *
         * **Note:** This method mutates `object` and is loosely based on
         * [`Object.assign`](https://mdn.io/Object/assign).
         *
         * @static
         * @memberOf _
         * @since 0.10.0
         * @category Object
         * @param {Object} object The destination object.
         * @param {...Object} [sources] The source objects.
         * @returns {Object} Returns `object`.
         * @see _.assignIn
         * @example
         *
         * function Foo() {
         *   this.a = 1;
         * }
         *
         * function Bar() {
         *   this.c = 3;
         * }
         *
         * Foo.prototype.b = 2;
         * Bar.prototype.d = 4;
         *
         * _.assign({ 'a': 0 }, new Foo, new Bar);
         * // => { 'a': 1, 'c': 3 }
         */
        var assign = createAssigner(function(object, source) {
            if (isPrototype(source) || isArrayLike(source)) {
                copyObject(source, keys(source), object);
                return;
            }
            for (var key in source) {
                if (hasOwnProperty.call(source, key)) {
                    assignValue(object, key, source[key]);
                }
            }
        });

        /**
         * This method is like `_.assign` except that it iterates over own and
         * inherited source properties.
         *
         * **Note:** This method mutates `object`.
         *
         * @static
         * @memberOf _
         * @since 4.0.0
         * @alias extend
         * @category Object
         * @param {Object} object The destination object.
         * @param {...Object} [sources] The source objects.
         * @returns {Object} Returns `object`.
         * @see _.assign
         * @example
         *
         * function Foo() {
         *   this.a = 1;
         * }
         *
         * function Bar() {
         *   this.c = 3;
         * }
         *
         * Foo.prototype.b = 2;
         * Bar.prototype.d = 4;
         *
         * _.assignIn({ 'a': 0 }, new Foo, new Bar);
         * // => { 'a': 1, 'b': 2, 'c': 3, 'd': 4 }
         */
        var assignIn = createAssigner(function(object, source) {
            copyObject(source, keysIn(source), object);
        });

        /**
         * Creates an object that inherits from the `prototype` object. If a
         * `properties` object is given, its own enumerable string keyed properties
         * are assigned to the created object.
         *
         * @static
         * @memberOf _
         * @since 2.3.0
         * @category Object
         * @param {Object} prototype The object to inherit from.
         * @param {Object} [properties] The properties to assign to the object.
         * @returns {Object} Returns the new object.
         * @example
         *
         * function Shape() {
         *   this.x = 0;
         *   this.y = 0;
         * }
         *
         * function Circle() {
         *   Shape.call(this);
         * }
         *
         * Circle.prototype = _.create(Shape.prototype, {
         *   'constructor': Circle
         * });
         *
         * var circle = new Circle;
         * circle instanceof Circle;
         * // => true
         *
         * circle instanceof Shape;
         * // => true
         */
        function create(prototype, properties) {
            var result = baseCreate(prototype);
            return properties == null ? result : baseAssign(result, properties);
        }

        /**
         * Assigns own and inherited enumerable string keyed properties of source
         * objects to the destination object for all destination properties that
         * resolve to `undefined`. Source objects are applied from left to right.
         * Once a property is set, additional values of the same property are ignored.
         *
         * **Note:** This method mutates `object`.
         *
         * @static
         * @since 0.1.0
         * @memberOf _
         * @category Object
         * @param {Object} object The destination object.
         * @param {...Object} [sources] The source objects.
         * @returns {Object} Returns `object`.
         * @see _.defaultsDeep
         * @example
         *
         * _.defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
         * // => { 'a': 1, 'b': 2 }
         */
        var defaults = baseRest(function(object, sources) {
            object = Object(object);

            var index = -1;
            var length = sources.length;
            var guard = length > 2 ? sources[2] : undefined;

            if (guard && isIterateeCall(sources[0], sources[1], guard)) {
                length = 1;
            }

            while (++index < length) {
                var source = sources[index];
                var props = keysIn(source);
                var propsIndex = -1;
                var propsLength = props.length;

                while (++propsIndex < propsLength) {
                    var key = props[propsIndex];
                    var value = object[key];

                    if (value === undefined ||
                        (eq(value, objectProto[key]) && !hasOwnProperty.call(object, key))) {
                        object[key] = source[key];
                    }
                }
            }

            return object;
        });

        /**
         * Creates an array of the own enumerable property names of `object`.
         *
         * **Note:** Non-object values are coerced to objects. See the
         * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
         * for more details.
         *
         * @static
         * @since 0.1.0
         * @memberOf _
         * @category Object
         * @param {Object} object The object to query.
         * @returns {Array} Returns the array of property names.
         * @example
         *
         * function Foo() {
         *   this.a = 1;
         *   this.b = 2;
         * }
         *
         * Foo.prototype.c = 3;
         *
         * _.keys(new Foo);
         * // => ['a', 'b'] (iteration order is not guaranteed)
         *
         * _.keys('hi');
         * // => ['0', '1']
         */
        function keys(object) {
            return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
        }

        /**
         * Creates an array of the own and inherited enumerable property names of `object`.
         *
         * **Note:** Non-object values are coerced to objects.
         *
         * @static
         * @memberOf _
         * @since 3.0.0
         * @category Object
         * @param {Object} object The object to query.
         * @returns {Array} Returns the array of property names.
         * @example
         *
         * function Foo() {
         *   this.a = 1;
         *   this.b = 2;
         * }
         *
         * Foo.prototype.c = 3;
         *
         * _.keysIn(new Foo);
         * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
         */
        function keysIn(object) {
            return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
        }

        /*------------------------------------------------------------------------*/

        /**
         * Creates a function that returns `value`.
         *
         * @static
         * @memberOf _
         * @since 2.4.0
         * @category Util
         * @param {*} value The value to return from the new function.
         * @returns {Function} Returns the new constant function.
         * @example
         *
         * var objects = _.times(2, _.constant({ 'a': 1 }));
         *
         * console.log(objects);
         * // => [{ 'a': 1 }, { 'a': 1 }]
         *
         * console.log(objects[0] === objects[1]);
         * // => true
         */
        function constant(value) {
            return function() {
                return value;
            };
        }

        /**
         * This method returns the first argument it receives.
         *
         * @static
         * @since 0.1.0
         * @memberOf _
         * @category Util
         * @param {*} value Any value.
         * @returns {*} Returns `value`.
         * @example
         *
         * var object = { 'a': 1 };
         *
         * console.log(_.identity(object) === object);
         * // => true
         */
        function identity(value) {
            return value;
        }

        /**
         * This method returns `undefined`.
         *
         * @static
         * @memberOf _
         * @since 2.3.0
         * @category Util
         * @example
         *
         * _.times(2, _.noop);
         * // => [undefined, undefined]
         */
        function noop() {
            // No operation performed.
        }

        /**
         * This method returns `false`.
         *
         * @static
         * @memberOf _
         * @since 4.13.0
         * @category Util
         * @returns {boolean} Returns `false`.
         * @example
         *
         * _.times(2, _.stubFalse);
         * // => [false, false]
         */
        function stubFalse() {
            return false;
        }

        /*------------------------------------------------------------------------*/

        // Add methods that return wrapped values in chain sequences.
        lodash.assign = assign;
        lodash.assignIn = assignIn;
        lodash.bind = bind;
        lodash.constant = constant;
        lodash.create = create;
        lodash.debounce = debounce;
        lodash.defaults = defaults;
        lodash.defer = defer;
        lodash.delay = delay;
        lodash.keys = keys;
        lodash.keysIn = keysIn;
        lodash.partial = partial;
        lodash.throttle = throttle;

        // Add aliases.
        lodash.extend = assignIn;

        /*------------------------------------------------------------------------*/

        // Add methods that return unwrapped values in chain sequences.
        lodash.eq = eq;
        lodash.identity = identity;
        lodash.isArguments = isArguments;
        lodash.isArray = isArray;
        lodash.isArrayLike = isArrayLike;
        lodash.isBuffer = isBuffer;
        lodash.isFunction = isFunction;
        lodash.isLength = isLength;
        lodash.isObject = isObject;
        lodash.isObjectLike = isObjectLike;
        lodash.isString = isString;
        lodash.isSymbol = isSymbol;
        lodash.isTypedArray = isTypedArray;
        lodash.stubFalse = stubFalse;
        lodash.noop = noop;
        lodash.now = now;
        lodash.toFinite = toFinite;
        lodash.toInteger = toInteger;
        lodash.toNumber = toNumber;

        /*------------------------------------------------------------------------*/

        /**
         * The semantic version number.
         *
         * @static
         * @memberOf _
         * @type {string}
         */
        lodash.VERSION = VERSION;

        // Assign default placeholders.
        arrayEach(['bind', 'partial'], function(methodName) {
            lodash[methodName].placeholder = lodash;
        });

        /*--------------------------------------------------------------------------*/

        // Some AMD build optimizers, like r.js, check for condition patterns like:
        if (typeof define == 'function' && typeof define.amd == 'object' && define.amd) {
            // Expose Lodash on the global object to prevent errors when Lodash is
            // loaded by a script tag in the presence of an AMD loader.
            // See http://requirejs.org/docs/errors.html#mismatch for more details.
            // Use `_.noConflict` to remove Lodash from the global object.
            root._ = lodash;

            // Define as an anonymous module so, through path mapping, it can be
            // referenced as the "underscore" module.
            define(function() {
                return lodash;
            });
        }
        // Check for `exports` after `define` in case a build optimizer adds it.
        else if (freeModule) {
            // Export for Node.js.
            (freeModule.exports = lodash)._ = lodash;
            // Export for CommonJS support.
            freeExports._ = lodash;
        }
        else {
            // Export to the global object.
            root._ = lodash;
        }
        return lodash; }.call(this));

    /*!
     * @overview es6-promise - a tiny implementation of Promises/A+.
     * @copyright Copyright (c) 2014 Yehuda Katz, Tom Dale, Stefan Penner and contributors (Conversion to ES6 API by Jake Archibald)
     * @license   Licensed under MIT license
     *            See https://raw.githubusercontent.com/jakearchibald/es6-promise/master/LICENSE
     * @version   2.0.1
     */
    (function() {
        "use strict";

        function $$utils$$objectOrFunction(x) {
            return typeof x === 'function' || (typeof x === 'object' && x !== null);
        }

        function $$utils$$isFunction(x) {
            return typeof x === 'function';
        }

        function $$utils$$isMaybeThenable(x) {
            return typeof x === 'object' && x !== null;
        }

        var $$utils$$_isArray;

        if (!Array.isArray) {
            $$utils$$_isArray = function(x) {
                return Object.prototype.toString.call(x) === '[object Array]';
            };
        } else {
            $$utils$$_isArray = Array.isArray;
        }

        var $$utils$$isArray = $$utils$$_isArray;
        var $$utils$$now = Date.now || function() { return new Date().getTime(); };
        function $$utils$$F() { }

        var $$utils$$o_create = (Object.create || function(o) {
            if (arguments.length > 1) {
                throw new Error('Second argument not supported');
            }
            if (typeof o !== 'object') {
                throw new TypeError('Argument must be an object');
            }
            $$utils$$F.prototype = o;
            return new $$utils$$F();
        });

        var $$asap$$len = 0;

        var $$asap$$default = function asap(callback, arg) {
            $$asap$$queue[$$asap$$len] = callback;
            $$asap$$queue[$$asap$$len + 1] = arg;
            $$asap$$len += 2;
            if ($$asap$$len === 2) {
                // If len is 1, that means that we need to schedule an async flush.
                // If additional callbacks are queued before the queue is flushed, they
                // will be processed by this flush that we are scheduling.
                $$asap$$scheduleFlush();
            }
        };

        var $$asap$$browserGlobal = (typeof window !== 'undefined') ? window : {};
        var $$asap$$BrowserMutationObserver = $$asap$$browserGlobal.MutationObserver || $$asap$$browserGlobal.WebKitMutationObserver;

        // test for web worker but not in IE10
        var $$asap$$isWorker = typeof Uint8ClampedArray !== 'undefined' &&
            typeof importScripts !== 'undefined' &&
            typeof MessageChannel !== 'undefined';

        // node
        function $$asap$$useNextTick() {
            return function() {
                process.nextTick($$asap$$flush);
            };
        }

        function $$asap$$useMutationObserver() {
            var iterations = 0;
            var observer = new $$asap$$BrowserMutationObserver($$asap$$flush);
            var node = document.createTextNode('');
            observer.observe(node, { characterData: true });

            return function() {
                node.data = (iterations = ++iterations % 2);
            };
        }

        // web worker
        function $$asap$$useMessageChannel() {
            var channel = new MessageChannel();
            channel.port1.onmessage = $$asap$$flush;
            return function() {
                channel.port2.postMessage(0);
            };
        }

        function $$asap$$useSetTimeout() {
            return function() {
                setTimeout($$asap$$flush, 1);
            };
        }

        var $$asap$$queue = new Array(1000);

        function $$asap$$flush() {
            for (var i = 0; i < $$asap$$len; i += 2) {
                var callback = $$asap$$queue[i];
                var arg = $$asap$$queue[i + 1];

                callback(arg);

                $$asap$$queue[i] = undefined;
                $$asap$$queue[i + 1] = undefined;
            }

            $$asap$$len = 0;
        }

        var $$asap$$scheduleFlush;

        // Decide what async method to use to triggering processing of queued callbacks:
        if (typeof process !== 'undefined' && {}.toString.call(process) === '[object process]') {
            $$asap$$scheduleFlush = $$asap$$useNextTick();
        } else if ($$asap$$BrowserMutationObserver) {
            $$asap$$scheduleFlush = $$asap$$useMutationObserver();
        } else if ($$asap$$isWorker) {
            $$asap$$scheduleFlush = $$asap$$useMessageChannel();
        } else {
            $$asap$$scheduleFlush = $$asap$$useSetTimeout();
        }

        function $$$internal$$noop() { }
        var $$$internal$$PENDING = void 0;
        var $$$internal$$FULFILLED = 1;
        var $$$internal$$REJECTED = 2;
        var $$$internal$$GET_THEN_ERROR = new $$$internal$$ErrorObject();

        function $$$internal$$selfFullfillment() {
            return new TypeError("You cannot resolve a promise with itself");
        }

        function $$$internal$$cannotReturnOwn() {
            return new TypeError('A promises callback cannot return that same promise.');
        }

        function $$$internal$$getThen(promise) {
            try {
                return promise.then;
            } catch (error) {
                $$$internal$$GET_THEN_ERROR.error = error;
                return $$$internal$$GET_THEN_ERROR;
            }
        }

        function $$$internal$$tryThen(then, value, fulfillmentHandler, rejectionHandler) {
            try {
                then.call(value, fulfillmentHandler, rejectionHandler);
            } catch (e) {
                return e;
            }
        }

        function $$$internal$$handleForeignThenable(promise, thenable, then) {
            $$asap$$default(function(promise) {
                var sealed = false;
                var error = $$$internal$$tryThen(then, thenable, function(value) {
                    if (sealed) { return; }
                    sealed = true;
                    if (thenable !== value) {
                        $$$internal$$resolve(promise, value);
                    } else {
                        $$$internal$$fulfill(promise, value);
                    }
                }, function(reason) {
                    if (sealed) { return; }
                    sealed = true;

                    $$$internal$$reject(promise, reason);
                }, 'Settle: ' + (promise._label || ' unknown promise'));

                if (!sealed && error) {
                    sealed = true;
                    $$$internal$$reject(promise, error);
                }
            }, promise);
        }

        function $$$internal$$handleOwnThenable(promise, thenable) {
            if (thenable._state === $$$internal$$FULFILLED) {
                $$$internal$$fulfill(promise, thenable._result);
            } else if (promise._state === $$$internal$$REJECTED) {
                $$$internal$$reject(promise, thenable._result);
            } else {
                $$$internal$$subscribe(thenable, undefined, function(value) {
                    $$$internal$$resolve(promise, value);
                }, function(reason) {
                    $$$internal$$reject(promise, reason);
                });
            }
        }

        function $$$internal$$handleMaybeThenable(promise, maybeThenable) {
            if (maybeThenable.constructor === promise.constructor) {
                $$$internal$$handleOwnThenable(promise, maybeThenable);
            } else {
                var then = $$$internal$$getThen(maybeThenable);

                if (then === $$$internal$$GET_THEN_ERROR) {
                    $$$internal$$reject(promise, $$$internal$$GET_THEN_ERROR.error);
                } else if (then === undefined) {
                    $$$internal$$fulfill(promise, maybeThenable);
                } else if ($$utils$$isFunction(then)) {
                    $$$internal$$handleForeignThenable(promise, maybeThenable, then);
                } else {
                    $$$internal$$fulfill(promise, maybeThenable);
                }
            }
        }

        function $$$internal$$resolve(promise, value) {
            if (promise === value) {
                $$$internal$$reject(promise, $$$internal$$selfFullfillment());
            } else if ($$utils$$objectOrFunction(value)) {
                $$$internal$$handleMaybeThenable(promise, value);
            } else {
                $$$internal$$fulfill(promise, value);
            }
        }

        function $$$internal$$publishRejection(promise) {
            if (promise._onerror) {
                promise._onerror(promise._result);
            }

            $$$internal$$publish(promise);
        }

        function $$$internal$$fulfill(promise, value) {
            if (promise._state !== $$$internal$$PENDING) { return; }

            promise._result = value;
            promise._state = $$$internal$$FULFILLED;

            if (promise._subscribers.length === 0) {
            } else {
                $$asap$$default($$$internal$$publish, promise);
            }
        }

        function $$$internal$$reject(promise, reason) {
            if (promise._state !== $$$internal$$PENDING) { return; }
            promise._state = $$$internal$$REJECTED;
            promise._result = reason;

            $$asap$$default($$$internal$$publishRejection, promise);
        }

        function $$$internal$$subscribe(parent, child, onFulfillment, onRejection) {
            var subscribers = parent._subscribers;
            var length = subscribers.length;

            parent._onerror = null;

            subscribers[length] = child;
            subscribers[length + $$$internal$$FULFILLED] = onFulfillment;
            subscribers[length + $$$internal$$REJECTED] = onRejection;

            if (length === 0 && parent._state) {
                $$asap$$default($$$internal$$publish, parent);
            }
        }

        function $$$internal$$publish(promise) {
            var subscribers = promise._subscribers;
            var settled = promise._state;

            if (subscribers.length === 0) { return; }

            var child, callback, detail = promise._result;

            for (var i = 0; i < subscribers.length; i += 3) {
                child = subscribers[i];
                callback = subscribers[i + settled];

                if (child) {
                    $$$internal$$invokeCallback(settled, child, callback, detail);
                } else {
                    callback(detail);
                }
            }

            promise._subscribers.length = 0;
        }

        function $$$internal$$ErrorObject() {
            this.error = null;
        }

        var $$$internal$$TRY_CATCH_ERROR = new $$$internal$$ErrorObject();

        function $$$internal$$tryCatch(callback, detail) {
            try {
                return callback(detail);
            } catch (e) {
                $$$internal$$TRY_CATCH_ERROR.error = e;
                return $$$internal$$TRY_CATCH_ERROR;
            }
        }

        function $$$internal$$invokeCallback(settled, promise, callback, detail) {
            var hasCallback = $$utils$$isFunction(callback),
                value, error, succeeded, failed;

            if (hasCallback) {
                value = $$$internal$$tryCatch(callback, detail);

                if (value === $$$internal$$TRY_CATCH_ERROR) {
                    failed = true;
                    error = value.error;
                    value = null;
                } else {
                    succeeded = true;
                }

                if (promise === value) {
                    $$$internal$$reject(promise, $$$internal$$cannotReturnOwn());
                    return;
                }

            } else {
                value = detail;
                succeeded = true;
            }

            if (promise._state !== $$$internal$$PENDING) {
                // noop
            } else if (hasCallback && succeeded) {
                $$$internal$$resolve(promise, value);
            } else if (failed) {
                $$$internal$$reject(promise, error);
            } else if (settled === $$$internal$$FULFILLED) {
                $$$internal$$fulfill(promise, value);
            } else if (settled === $$$internal$$REJECTED) {
                $$$internal$$reject(promise, value);
            }
        }

        function $$$internal$$initializePromise(promise, resolver) {
            try {
                resolver(function resolvePromise(value) {
                    $$$internal$$resolve(promise, value);
                }, function rejectPromise(reason) {
                    $$$internal$$reject(promise, reason);
                });
            } catch (e) {
                $$$internal$$reject(promise, e);
            }
        }

        function $$$enumerator$$makeSettledResult(state, position, value) {
            if (state === $$$internal$$FULFILLED) {
                return {
                    state: 'fulfilled',
                    value: value
                };
            } else {
                return {
                    state: 'rejected',
                    reason: value
                };
            }
        }

        function $$$enumerator$$Enumerator(Constructor, input, abortOnReject, label) {
            this._instanceConstructor = Constructor;
            this.promise = new Constructor($$$internal$$noop, label);
            this._abortOnReject = abortOnReject;

            if (this._validateInput(input)) {
                this._input = input;
                this.length = input.length;
                this._remaining = input.length;

                this._init();

                if (this.length === 0) {
                    $$$internal$$fulfill(this.promise, this._result);
                } else {
                    this.length = this.length || 0;
                    this._enumerate();
                    if (this._remaining === 0) {
                        $$$internal$$fulfill(this.promise, this._result);
                    }
                }
            } else {
                $$$internal$$reject(this.promise, this._validationError());
            }
        }

        $$$enumerator$$Enumerator.prototype._validateInput = function(input) {
            return $$utils$$isArray(input);
        };

        $$$enumerator$$Enumerator.prototype._validationError = function() {
            return new Error('Array Methods must be provided an Array');
        };

        $$$enumerator$$Enumerator.prototype._init = function() {
            this._result = new Array(this.length);
        };

        var $$$enumerator$$default = $$$enumerator$$Enumerator;

        $$$enumerator$$Enumerator.prototype._enumerate = function() {
            var length = this.length;
            var promise = this.promise;
            var input = this._input;

            for (var i = 0; promise._state === $$$internal$$PENDING && i < length; i++) {
                this._eachEntry(input[i], i);
            }
        };

        $$$enumerator$$Enumerator.prototype._eachEntry = function(entry, i) {
            var c = this._instanceConstructor;
            if ($$utils$$isMaybeThenable(entry)) {
                if (entry.constructor === c && entry._state !== $$$internal$$PENDING) {
                    entry._onerror = null;
                    this._settledAt(entry._state, i, entry._result);
                } else {
                    this._willSettleAt(c.resolve(entry), i);
                }
            } else {
                this._remaining--;
                this._result[i] = this._makeResult($$$internal$$FULFILLED, i, entry);
            }
        };

        $$$enumerator$$Enumerator.prototype._settledAt = function(state, i, value) {
            var promise = this.promise;

            if (promise._state === $$$internal$$PENDING) {
                this._remaining--;

                if (this._abortOnReject && state === $$$internal$$REJECTED) {
                    $$$internal$$reject(promise, value);
                } else {
                    this._result[i] = this._makeResult(state, i, value);
                }
            }

            if (this._remaining === 0) {
                $$$internal$$fulfill(promise, this._result);
            }
        };

        $$$enumerator$$Enumerator.prototype._makeResult = function(state, i, value) {
            return value;
        };

        $$$enumerator$$Enumerator.prototype._willSettleAt = function(promise, i) {
            var enumerator = this;

            $$$internal$$subscribe(promise, undefined, function(value) {
                enumerator._settledAt($$$internal$$FULFILLED, i, value);
            }, function(reason) {
                enumerator._settledAt($$$internal$$REJECTED, i, reason);
            });
        };

        var $$promise$all$$default = function all(entries, label) {
            return new $$$enumerator$$default(this, entries, true /* abort on reject */, label).promise;
        };

        var $$promise$race$$default = function race(entries, label) {
            /*jshint validthis:true */
            var Constructor = this;

            var promise = new Constructor($$$internal$$noop, label);

            if (!$$utils$$isArray(entries)) {
                $$$internal$$reject(promise, new TypeError('You must pass an array to race.'));
                return promise;
            }

            var length = entries.length;

            function onFulfillment(value) {
                $$$internal$$resolve(promise, value);
            }

            function onRejection(reason) {
                $$$internal$$reject(promise, reason);
            }

            for (var i = 0; promise._state === $$$internal$$PENDING && i < length; i++) {
                $$$internal$$subscribe(Constructor.resolve(entries[i]), undefined, onFulfillment, onRejection);
            }

            return promise;
        };

        var $$promise$resolve$$default = function resolve(object, label) {
            /*jshint validthis:true */
            var Constructor = this;

            if (object && typeof object === 'object' && object.constructor === Constructor) {
                return object;
            }

            var promise = new Constructor($$$internal$$noop, label);
            $$$internal$$resolve(promise, object);
            return promise;
        };

        var $$promise$reject$$default = function reject(reason, label) {
            /*jshint validthis:true */
            var Constructor = this;
            var promise = new Constructor($$$internal$$noop, label);
            $$$internal$$reject(promise, reason);
            return promise;
        };

        var $$es6$promise$promise$$counter = 0;

        function $$es6$promise$promise$$needsResolver() {
            throw new TypeError('You must pass a resolver function as the first argument to the promise constructor');
        }

        function $$es6$promise$promise$$needsNew() {
            throw new TypeError("Failed to construct 'Promise': Please use the 'new' operator, this object constructor cannot be called as a function.");
        }

        var $$es6$promise$promise$$default = $$es6$promise$promise$$Promise;

        /**
         Promise objects represent the eventual result of an asynchronous operation. The
         primary way of interacting with a promise is through its `then` method, which
         registers callbacks to receive either a promise’s eventual value or the reason
         why the promise cannot be fulfilled.

         Terminology
         -----------

         - `promise` is an object or function with a `then` method whose behavior conforms to this specification.
         - `thenable` is an object or function that defines a `then` method.
         - `value` is any legal JavaScript value (including undefined, a thenable, or a promise).
         - `exception` is a value that is thrown using the throw statement.
         - `reason` is a value that indicates why a promise was rejected.
         - `settled` the final resting state of a promise, fulfilled or rejected.

         A promise can be in one of three states: pending, fulfilled, or rejected.

         Promises that are fulfilled have a fulfillment value and are in the fulfilled
         state.  Promises that are rejected have a rejection reason and are in the
         rejected state.  A fulfillment value is never a thenable.

         Promises can also be said to *resolve* a value.  If this value is also a
         promise, then the original promise's settled state will match the value's
         settled state.  So a promise that *resolves* a promise that rejects will
         itself reject, and a promise that *resolves* a promise that fulfills will
         itself fulfill.


         Basic Usage:
         ------------

         ```js
         var promise = new Promise(function(resolve, reject) {
            // on success
            resolve(value);

            // on failure
            reject(reason);
          });

         promise.then(function(value) {
            // on fulfillment
          }, function(reason) {
            // on rejection
          });
         ```

         Advanced Usage:
         ---------------

         Promises shine when abstracting away asynchronous interactions such as
         `XMLHttpRequest`s.

         ```js
         function getJSON(url) {
            return new Promise(function(resolve, reject){
              var xhr = new XMLHttpRequest();

              xhr.open('GET', url);
              xhr.onreadystatechange = handler;
              xhr.responseType = 'json';
              xhr.setRequestHeader('Accept', 'application/json');
              xhr.send();

              function handler() {
                if (this.readyState === this.DONE) {
                  if (this.status === 200) {
                    resolve(this.response);
                  } else {
                    reject(new Error('getJSON: `' + url + '` failed with status: [' + this.status + ']'));
                  }
                }
              };
            });
          }

         getJSON('/posts.json').then(function(json) {
            // on fulfillment
          }, function(reason) {
            // on rejection
          });
         ```

         Unlike callbacks, promises are great composable primitives.

         ```js
         Promise.all([
         getJSON('/posts'),
         getJSON('/comments')
         ]).then(function(values){
            values[0] // => postsJSON
            values[1] // => commentsJSON

            return values;
          });
         ```

         @class Promise
         @param {function} resolver
         Useful for tooling.
         @constructor
         */
        function $$es6$promise$promise$$Promise(resolver) {
            this._id = $$es6$promise$promise$$counter++;
            this._state = undefined;
            this._result = undefined;
            this._subscribers = [];

            if ($$$internal$$noop !== resolver) {
                if (!$$utils$$isFunction(resolver)) {
                    $$es6$promise$promise$$needsResolver();
                }

                if (!(this instanceof $$es6$promise$promise$$Promise)) {
                    $$es6$promise$promise$$needsNew();
                }

                $$$internal$$initializePromise(this, resolver);
            }
        }

        $$es6$promise$promise$$Promise.all = $$promise$all$$default;
        $$es6$promise$promise$$Promise.race = $$promise$race$$default;
        $$es6$promise$promise$$Promise.resolve = $$promise$resolve$$default;
        $$es6$promise$promise$$Promise.reject = $$promise$reject$$default;

        $$es6$promise$promise$$Promise.prototype = {
            constructor: $$es6$promise$promise$$Promise,

            /**
             The primary way of interacting with a promise is through its `then` method,
             which registers callbacks to receive either a promise's eventual value or the
             reason why the promise cannot be fulfilled.

             ```js
             findUser().then(function(user){
                // user is available
              }, function(reason){
                // user is unavailable, and you are given the reason why
              });
             ```

             Chaining
             --------

             The return value of `then` is itself a promise.  This second, 'downstream'
             promise is resolved with the return value of the first promise's fulfillment
             or rejection handler, or rejected if the handler throws an exception.

             ```js
             findUser().then(function (user) {
                return user.name;
              }, function (reason) {
                return 'default name';
              }).then(function (userName) {
                // If `findUser` fulfilled, `userName` will be the user's name, otherwise it
                // will be `'default name'`
              });

             findUser().then(function (user) {
                throw new Error('Found user, but still unhappy');
              }, function (reason) {
                throw new Error('`findUser` rejected and we're unhappy');
              }).then(function (value) {
                // never reached
              }, function (reason) {
                // if `findUser` fulfilled, `reason` will be 'Found user, but still unhappy'.
                // If `findUser` rejected, `reason` will be '`findUser` rejected and we're unhappy'.
              });
             ```
             If the downstream promise does not specify a rejection handler, rejection reasons will be propagated further downstream.

             ```js
             findUser().then(function (user) {
                throw new PedagogicalException('Upstream error');
              }).then(function (value) {
                // never reached
              }).then(function (value) {
                // never reached
              }, function (reason) {
                // The `PedgagocialException` is propagated all the way down to here
              });
             ```

             Assimilation
             ------------

             Sometimes the value you want to propagate to a downstream promise can only be
             retrieved asynchronously. This can be achieved by returning a promise in the
             fulfillment or rejection handler. The downstream promise will then be pending
             until the returned promise is settled. This is called *assimilation*.

             ```js
             findUser().then(function (user) {
                return findCommentsByAuthor(user);
              }).then(function (comments) {
                // The user's comments are now available
              });
             ```

             If the assimliated promise rejects, then the downstream promise will also reject.

             ```js
             findUser().then(function (user) {
                return findCommentsByAuthor(user);
              }).then(function (comments) {
                // If `findCommentsByAuthor` fulfills, we'll have the value here
              }, function (reason) {
                // If `findCommentsByAuthor` rejects, we'll have the reason here
              });
             ```

             Simple Example
             --------------

             Synchronous Example

             ```javascript
             var result;

             try {
                result = findResult();
                // success
              } catch(reason) {
                // failure
              }
             ```

             Errback Example

             ```js
             findResult(function(result, err){
                if (err) {
                  // failure
                } else {
                  // success
                }
              });
             ```

             Promise Example;

             ```javascript
             findResult().then(function(result){
                // success
              }, function(reason){
                // failure
              });
             ```

             Advanced Example
             --------------

             Synchronous Example

             ```javascript
             var author, books;

             try {
                author = findAuthor();
                books  = findBooksByAuthor(author);
                // success
              } catch(reason) {
                // failure
              }
             ```

             Errback Example

             ```js

             function foundBooks(books) {

              }

             function failure(reason) {

              }

             findAuthor(function(author, err){
                if (err) {
                  failure(err);
                  // failure
                } else {
                  try {
                    findBoooksByAuthor(author, function(books, err) {
                      if (err) {
                        failure(err);
                      } else {
                        try {
                          foundBooks(books);
                        } catch(reason) {
                          failure(reason);
                        }
                      }
                    });
                  } catch(error) {
                    failure(err);
                  }
                  // success
                }
              });
             ```

             Promise Example;

             ```javascript
             findAuthor().
             then(findBooksByAuthor).
             then(function(books){
                  // found books
              }).catch(function(reason){
                // something went wrong
              });
             ```

             @method then
             @param {Function} onFulfilled
             @param {Function} onRejected
             Useful for tooling.
             @return {Promise}
             */
            then: function(onFulfillment, onRejection) {
                var parent = this;
                var state = parent._state;

                if (state === $$$internal$$FULFILLED && !onFulfillment || state === $$$internal$$REJECTED && !onRejection) {
                    return this;
                }

                var child = new this.constructor($$$internal$$noop);
                var result = parent._result;

                if (state) {
                    var callback = arguments[state - 1];
                    $$asap$$default(function() {
                        $$$internal$$invokeCallback(state, child, callback, result);
                    });
                } else {
                    $$$internal$$subscribe(parent, child, onFulfillment, onRejection);
                }

                return child;
            },

            /**
             `catch` is simply sugar for `then(undefined, onRejection)` which makes it the same
             as the catch block of a try/catch statement.

             ```js
             function findAuthor(){
                throw new Error('couldn't find that author');
              }

             // synchronous
             try {
                findAuthor();
              } catch(reason) {
                // something went wrong
              }

             // async with promises
             findAuthor().catch(function(reason){
                // something went wrong
              });
             ```

             @method catch
             @param {Function} onRejection
             Useful for tooling.
             @return {Promise}
             */
            'catch': function(onRejection) {
                return this.then(null, onRejection);
            }
        };

        var $$es6$promise$polyfill$$default = function polyfill() {
            var local;

            if (typeof global !== 'undefined') {
                local = global;
            } else if (typeof window !== 'undefined' && window.document) {
                local = window;
            } else {
                local = self;
            }

            var es6PromiseSupport =
                "Promise" in local &&
                    // Some of these methods are missing from
                    // Firefox/Chrome experimental implementations
                "resolve" in local.Promise &&
                "reject" in local.Promise &&
                "all" in local.Promise &&
                "race" in local.Promise &&
                    // Older version of the spec had a resolver object
                    // as the arg rather than a function
                (function() {
                    var resolve;
                    new local.Promise(function(r) { resolve = r; });
                    return $$utils$$isFunction(resolve);
                }());

            if (!es6PromiseSupport) {
                local.Promise = $$es6$promise$promise$$default;
            }
        };

        var es6$promise$umd$$ES6Promise = {
            'Promise': $$es6$promise$promise$$default,
            'polyfill': $$es6$promise$polyfill$$default
        };

        /* global define:true module:true window: true */
        if (typeof define === 'function' && define['amd']) {
            define(function() { return es6$promise$umd$$ES6Promise; });
        } else if (typeof module !== 'undefined' && module['exports']) {
            module['exports'] = es6$promise$umd$$ES6Promise;
        } else if (typeof this !== 'undefined') {
            this['ES6Promise'] = es6$promise$umd$$ES6Promise;
        }
    }).call(window);

    window.ES6Promise.polyfill();

    /**
     * Console polyfill
     */
    var consoleOverride = (function() {
        var overrideFn = function(con) {
            con || (con = {});
            con.assert = con.clear = con.count = con.debug = con.dir = con.dirxml = con.error = con.exception =
                con.group = con.groupCollapsed = con.groupEnd = con.info = con.log = con.markTimeline = con.profile =
                con.profileEnd = con.table = con.time = con.timeEnd = con.timeStamp = con.trace = con.warn = function() { };

            return con;
        };
        if (!window.console) {
            window.console = overrideFn();
        }
        return overrideFn;
    }).call(window);

    (function() {
        "use strict";

        var inStudio = !!(window.LIVEBALL && window.LIVEBALL.inStudio);
        if (inStudio) {
            var inStudioLabs;
            if (window.LIVEBALL.App) {
                inStudioLabs = window.LIVEBALL.App.features.labs;
            } else {
                inStudioLabs = window.LIVEBALL.features && window.LIVEBALL.features.labs;
            }
        }

        window.ixp = window.ixp || {};
        var runtime = window.ixp.runtime = window.ixp.runtime || {};
        runtime.fn = runtime.fn || {};
        runtime.inStudio = inStudio;
        runtime.inStudioLabs = inStudioLabs;


        runtime.interactionsConfig = {
            BULK_SIZE: 50,
            AJAX_INTERVAL: 2 * 60 * 60
        };

        runtime.interactionManager = {};

        // Queue should be scoped if used elsewhere
        var readyQueue = [];
        var isReady = false;
        runtime.ready = function(fn, context) {
            if (typeof fn !== 'function') {
                throw new Error('runtime.ready requires the callback to be a function');
            }

            var func = !context ? fn : function() { fn.call(context); };

            if (isReady) {
                if (_.isFunction(window.requestAnimationFrame)) {
                    window.requestAnimationFrame(func);
                } else {
                    setTimeout(func, 0);
                }
                return;
            }

            readyQueue.push(func);
        };

        var constants = runtime.constants = runtime.constants || {};
        runtime.constants.screenSizes =  runtime.constants.screenSizes || {};
        runtime.constants.screenSizes =  {
            xs: { maxWidth: 767 }, 
            sm: { minWidth: 768, maxWidth: 991 },
            md: { minWidth: 992, maxWidth: 1199 },
            lg: { minWidth: 1200 }
        };

        runtime.flags = runtime.flags || {};

        var parseQueryString = function() {
            var qs = {};
            var match,
                pl = /\+/g, // Regex for replacing addition symbol with a space
                search = /([^&=]+)=?([^&]*)/g,
                decode = function(s) {
                    try {
                        return decodeURIComponent(s.replace(pl, ' '));
                    }
                    catch (ex) {
                        console.error(ex);
                        return null;
                    }
                },
                query = window.location.search.substring(1);

            while (match = search.exec(query)) {
                qs[decode(match[1])] = decode(match[2]);
            }
            return qs;
        };

        runtime.queryString = parseQueryString();

        var isHttps = (window.location.protocol === 'https:');

        runtime.flags.https = isHttps;

        if (runtime.queryString['debug'] === 'true' ||
            runtime.queryString['_ion_debug'] === 'true') {
            runtime.flags.debug = true;
        }

        var inDebug = !!runtime.flags.debug;

        runtime.console = !inDebug ? consoleOverride() : window.console;

        runtime.log = function() {
            if (inDebug) {
                if (typeof runtime.console.log === 'function') {
                    runtime.console.log.apply(runtime.console, arguments);
                } else {
                    runtime.console.log(Array.prototype.slice.call(arguments));
                }
            }
        };

        if (inStudio) {
            runtime.studio = {
                app: window.LIVEBALL.App,
                labs: !!inStudioLabs
            };
        }

        // runtime URLs
        runtime.paths = runtime.paths || {};
        (function (paths) {
            var cdnUrls = {
                https: 'https://files.ioninteractive.com/',
                http: 'http://files.ioninteractive.com/'
            };
            var trimFrontSlash = function(str) {
                return str.replace(/^\//g, '');
            };
            var trimEndSlash = function(str) {
                return str.replace(/\/$/g, '');
            };
            var trimSlashes = function(str) {
                return trimFrontSlash(trimEndSlash(str));
            };
            paths.join = function() {
                var args = Array.prototype.slice.call(arguments, 0);
                var path = [];
                for (var i = 0, len = args.length; i < len; i++) {
                    var part = args[i];
                    if (typeof part !== 'string') {
                        throw new Error('Arguments to path.join must be strings');
                    }
                    if (part === '/') {
                        continue;
                    }
                    if (i === 0) {
                        part = trimEndSlash(part);
                    } else {
                        part = trimSlashes(part);
                    }
                    path = path.concat(part.split('/'));
                }
                return path.join('/');
            };
            paths.getCdnPath = function() {
                var cdnPath = isHttps ? cdnUrls.https : cdnUrls.http;
                if (!arguments.length) {
                    return cdnPath;
                }
                var args = Array.prototype.slice.call(arguments, 0);
                args = [cdnPath].concat(args);

                var newPath = paths.join.apply(this, args);
                if (!runtime.flags.debug) {
                    return newPath;
                }
                return newPath.replace('.min', '');
            };
        }).call(this, runtime.paths);

        // runtime.dependencies
        runtime.dependencies = runtime.dependencies || {};
        var components = runtime.components = runtime.components || {};
        components.dependencies = components.dependencies || {};
        components.dependencies._ = _;

        (function(dependencies, componentDeps) {
            var modules = {};
            var loading = {};
            if (runtime.flags.debug) {
                dependencies._modules = modules;
                dependencies._loading = loading;
            }

            var compareVersions = function(v1, v2) {
                var v1Split = v1.split('.');
                var v2Split = v2.split('.');

                while (v1Split.length < v2Split.length) {
                    v1Split.push('0');
                }
                while (v2Split.length < v1Split.length) {
                    v2Split.push('0');
                }

                for (var i = 0, len = v1Split.length; i < len; i++) {
                    var num1 = Number(v1Split[i]);
                    var num2 = Number(v2Split[i]);

                    if (num1 === num2) {
                        continue;
                    } else if (num1 > num2) {
                        return 1;
                    } else {
                        return -1;
                    }
                }

                return 0;
            };

            var getSortedInstancesInfo = function(instances, getVersion) {
                if (instances instanceof Array) {
                    var infos = [];
                    for (var i = 0, len = instances.length; i < len; i++) {
                        var instance = instances[i];
                        infos.push({
                            version: getVersion(instance),
                            module: instance
                        });
                    }

                    if (infos.length > 1) {
                        infos.sort(function(i1, i2) {
                            return -(compareVersions(i1.version, i2.version));
                        });
                    }

                    return infos;
                }

                return [];
            };

            var loadScript = function(url, options) {
                var script = document.createElement('script');
                var head = document.head || document.getElementsByTagName('head')[0];

                var loaded = false;
                var load = options.load;
                options.load = function() {
                    if (loaded) {
                        return;
                    }
                    loaded = true;

                    load.apply(this, arguments);
                };

                // This is for IE8/9? support
                script.onreadystatechange = function() {
                    // IE8
                    if (script.readyState === 'loaded' || script.readyState === 'complete') {
                        options.load();
                        script.onreadystatechange = null;
                    }
                };

                script.onload = options.load;
                script.onerror = options.error;

                if (options.integrity) {
                    script.integrity = options.integrity;
                }

                if (options.crossOrigin) {
                    script.crossOrigin = options.crossOrigin;
                }

                script.src = url;
                head.appendChild(script);
            };

            var moduleLocator = function(module, defineOpts, requireOpts) {
                var instances = module.instances;

                // Find all available instances if we don't have any
                if (!instances.length && typeof defineOpts.findInstances === 'function') {
                    // getInstancesInfo returns [{ version: '1.0', module: <instance> }] sorted by descending version
                    instances = module.instances = getSortedInstancesInfo(defineOpts.findInstances(), defineOpts.getInstanceVersion);
                }

                var minVer = requireOpts.minVer || requireOpts.version;
                var maxVer = requireOpts.maxVer || requireOpts.upToVersion;
                var checkVersion = !!(minVer || maxVer);

                // If no version check and we have only 1 instance - return it
                if (!checkVersion && instances.length === 1) {
                    return instances[0].module;
                }

                if (instances.length) {
                    // If no version check return the latest version (descendent sorted so 0 is latest)
                    if (!checkVersion) {
                        return instances[0].module;
                    }

                    var instance;
                    for (var i = 0, len = instances.length; i < len; i++) {
                        instance = instances[i];
                        if ((!minVer || compareVersions(instance.version, minVer) >= 0) &&
                            (!maxVer || compareVersions(instance.version, maxVer) < 0)) {
                            return instance.module;
                        }
                    }
                }

                return null;
            };

            var moduleLoader = function(name, defineOpts, requireOpts) {
                if (typeof requireOpts === 'function') {
                    var doneFn = requireOpts;
                    requireOpts = {
                        done: doneFn
                    };
                } else {
                    requireOpts || (requireOpts = {});
                }

                var url;

                var module = modules[name];
                if (requireOpts.sync === true) {
                    return moduleLocator(module, defineOpts, requireOpts);
                }

                var promise = new Promise(function(resolve, reject) {
                    var instances = module.instances;
                    var found = moduleLocator(module, defineOpts, requireOpts);
                    if (found) {
                        resolve(found);
                        return;
                    }

                    // Load module
                    url = requireOpts.url || defineOpts.url;
                    if (!url) {
                        reject(new Error('No module instances found'));
                        return;
                    }

                    // Make sure we aren't already trying to load the same module
                    if (loading[url]) {
                        if (runtime.flags.debug) {
                            console.log('queuing module url=' + url, requireOpts);
                        }

                        loading[url].then(resolve)['catch'](reject);
                        return;
                    }

                    if (runtime.flags.debug) {
                        console.log('loading module url=' + url, requireOpts);
                    }

                    loadScript(url, {
                        integrity: requireOpts.integrity || defineOpts.integrity,
                        crossOrigin: requireOpts.crossOrigin || defineOpts.crossOrigin,

                        load: function() {
                            delete loading[url];

                            try {
                                var instance = defineOpts.getInstanceExport();
                                instances.push({
                                    version: defineOpts.getInstanceVersion(instance),
                                    module: instance
                                });

                                if (instances.length > 1) {
                                    instances.sort(function(i1, i2) {
                                        return -(compareVersions(i1.version, i2.version));
                                    });
                                }

                                resolve(instance);
                            }
                            catch (ex) {
                                reject(ex);
                            }
                        },

                        error: function(err) {
                            delete loading[url];

                            err = new Error('failed to load script: url=' + url);
                            reject(err);
                        }
                    });
                });

                // If we have a url and aren't already loading, then add our promise
                if (url && !loading[url]) {
                    loading[url] = promise;
                }

                if (typeof requireOpts.done === 'function') {
                    promise.then(function(instance) {
                        requireOpts.done(instance);
                    });
                }

                if (typeof requireOpts.fail === 'function') {
                    promise['catch'](function(err) {
                        requireOpts.fail(err);
                    });
                }

                return promise;
            };

            /**
             * Defines a module
             * @param {string} name
             * @param {object} options
             */
            dependencies.define = function(name, options) {
                if (!name || typeof name !== 'string') {
                    throw new Error('(string) module name is required');
                }

                options || (options = {});
                if (typeof options.getInstanceExport !== 'function' ||
                    typeof options.getInstanceVersion !== 'function') {
                    throw new Error('options must contain getInstanceExport and getInstanceVersion functions');
                }

                name = name.toLowerCase();

                if (modules[name]) {
                    throw new Error('module ' + name + ' has already been defined');
                }

                modules[name] = {
                    loader: function(requireOpts) {
                        return moduleLoader(name, options, requireOpts);
                    },
                    instances: []
                };
            };

            /**
             * @callback requireDone
             * @param {object} [module] - desired module if successful
             */

            /**
             * @callback requireFail
             * @param {object} [error] - error information if unsuccessful
             */

            /**
             * @typedef requireOptions
             * @type {object}
             * @property {requireDone} done - callback
             * @property {requireFail} fail - callback
             * @property {string} [url] - (optional) url of the desired module if loading is required
             * @property {string} [minVer] - (optional) minVer (or minimum version) of the desired module
             * @property {string} [maxVer] - (optional) exclusive maximum version of the desired module
             */

            /**
             * Resolves a module
             * @param {string} name
             * @param {requireOptions|function} options
             */
            dependencies.require = function(name, options) {
                if (!name || typeof name !== 'string') {
                    throw new Error('(string) module name is required');
                }

                name = name.toLowerCase();

                var module = modules[name];
                if (!module) {
                    throw new Error('module ' + name + ' has not been defined');
                }
                return module.loader(options);
            };

            dependencies.define('jQuery', {
                url: runtime.dependencies.jQueryUrl,
                integrity: runtime.dependencies.jQueryIntegrity,
                crossOrigin: runtime.dependencies.jQueryCrossOrigin,

                findInstances: function() {
                    var instances = [];

                    // in components if loaded
                    if (componentDeps && componentDeps.jQuery && componentDeps.jQuery.v1_11) {
                        instances.push(componentDeps.jQuery.v1_11);
                    }

                    // in global scope if loaded
                    if (window.jQuery) {
                        instances.push(window.jQuery);
                    }

                    if (window.$ && (!window.jQuery || window.jQuery && window.jQuery !== window.$)) {
                        instances.push(window.$);
                    }

                    return instances;
                },

                getInstanceExport: function() {
                    return window.jQuery.noConflict(true);
                },

                getInstanceVersion: function(instance) {
                    return instance.fn.jquery;
                }
            });
        }).call(this, runtime.dependencies, components.dependencies);

        // Start loading jquery as soon as possible
        // Accept any on-page jQuery from 1.11.1 up to (but not including) 4.x so the
        // runtime can reuse a jQuery 3.x instance instead of double-loading.
        var $promise = runtime.dependencies.require('jQuery', {
            minVer: '1.11.1',
            maxVer: '4'
        });

        // Interactions Manager
        var initInteractionManager = function($) {
            if (window.ixp.runtime.initInteractionManager) {
                window.ixp.runtime.initInteractionManager.call(window.ixp.runtime, $, _);
            } else {
                setTimeout(initInteractionManager, 200, $);
            }
        };
        $promise.then(initInteractionManager);

        // runtime.events
        runtime.events = runtime.events || {};
        (function(events) {
            var subscriptions = {};
            if (runtime.flags.debug) {
                events._subscriptions = subscriptions;
            }

            var triggerEvents = function(listeners, args) {
                var ev,
                    i = -1,
                    l = listeners.length,
                    a1,
                    a2,
                    a3;

                switch (args.length) {
                    case 0:
                        while (++i < l) {
                            (ev = listeners[i]).fn.call(ev.ctx);
                        }
                        return;
                    case 1:
                        a1 = args[0];
                        while (++i < l) {
                            (ev = listeners[i]).fn.call(ev.ctx, a1);
                        }
                        return;
                    case 2:
                        a1 = args[0];
                        a2 = args[1];
                        while (++i < l) {
                            (ev = listeners[i]).fn.call(ev.ctx, a1, a2);
                        }
                        return;
                    case 3:
                        a1 = args[0];
                        a2 = args[1];
                        a3 = args[2];
                        while (++i < l) {
                            (ev = listeners[i]).fn.call(ev.ctx, a1, a2, a3);
                        }
                        return;
                    default:
                        args = args || [];
                        while (++i < l) {
                            (ev = listeners[i]).fn.apply(ev.ctx, args);
                        }
                        return;
                }
            };

            events.on = function(context, name, callback) {
                if (!context || !name || !callback) {
                    throw new Error('context, name, and callback are required');
                }

                name = name.toLowerCase();

                var listeners = subscriptions[name] || (subscriptions[name] = []);
                listeners.push({ ctx: context, fn: callback });
                return this;
            };

            events.once = function(context, name, callback) {
                if (!context || !name || !callback) {
                    throw new Error('context, name, and callback are required');
                }

                var me = this;

                var once = function() {
                    me.off(name, once);
                    callback.apply(this, arguments);
                };
                return this.on(context, name, once);
            };

            events.off = function(context, name, callback) {
                if (!context) {
                    throw new Error('context is required');
                }

                var names = name ? [name] : _.keys(subscriptions);
                for (var i = 0, namesLen = names.length; i < namesLen; i++) {
                    name = names[i];
                    name = name.toLowerCase();

                    // Bail out if there are no listeners stored
                    var listeners = subscriptions[name];
                    if (!listeners) {
                        continue;
                    }

                    // Find any remaining listeners
                    var remaining = [];
                    for (var j = 0, listenersLen = listeners.length; j < listenersLen; j++) {
                        var event = listeners[j];
                        if (context !== event.ctx ||
                            (callback && callback !== event.fn)) {
                            remaining.push(event);
                        }
                    }

                    // Replace listeners if there are any remaining.  Otherwise, clean up
                    if (remaining.length) {
                        subscriptions[name] = remaining;
                    } else {
                        delete subscriptions[name];
                    }
                }

                return this;
            };

            events.trigger = function(sender, name) {
                var args;
                if (typeof sender === 'string') {
                    args = Array.prototype.slice.call(arguments, 1);
                    name = sender;
                    sender = undefined;
                } else {
                    args = Array.prototype.slice.call(arguments, 2);
                }

                if (!name || !subscriptions) {
                    return false;
                }

                name = name.toLowerCase();

                var listeners = subscriptions[name];
                if (!listeners) {
                    return false;
                }

                if (sender) {
                    var filtered = [];
                    var i = -1,
                        l = listeners.length,
                        e;
                    while (++i < l) {
                        if ((e = listeners[i]).ctx !== sender) {
                            filtered.push(e);
                        }
                    }
                    listeners = filtered;
                }

                triggerEvents(listeners, args);
                return true;
            };
        }).call(this, runtime.events);

        var getRespondentId = function () {
            var compositeIdParts = (runtime.context.id || '').split('$');

            if (compositeIdParts.length === 3) {
                return compositeIdParts[2];
            }
            return 0;
        };

        var promiseQueue;
        var ajax = function($, options) {
            var config = {
                url: options.url,
                async: true,
                data: options.type === 'DELETE' ? undefined : JSON.stringify(options.data),
                dataType: 'json',
                contentType: 'application/json; charset=utf-8',
                type: options.type || 'POST',
                cache: false,
                success: options.success && $.proxy(options.success, this),
                error: options.error && $.proxy(options.error, this)
            };

            options.always = options.always || $.proxy(options.always || function() { }, this);

            var promise;
            if (options.queue === false) {
                promise = Promise.resolve($.ajax(config).always(options.always));
            } else {
                if (promiseQueue) {
                    var next = function() {
                        return Promise.resolve($.ajax(config).always(options.always));
                    };
                    promise = promiseQueue.then(next, next);
                } else {
                    promise = Promise.resolve($.ajax(config).always(options.always));
                }
                promiseQueue = promise;
            }

            // Uncomment to simulate longer ajax times
            //return new Promise(function(resolve) {
            //    setTimeout(resolve, 10000);
            //}).then(promise);

            return promise;
        };

        runtime.constants.promise = runtime.constants.promise || {};
        runtime.constants.promise = {
            cancellationError: 'Promise has been cancelled'
        };

        var $ajax = function(options) {
            return $promise.then(function($) {
                return ajax($, options);
            });
        };

        var findElementByIxpAnchor = function(anchor) {
            if (!_.isFunction(document.querySelectorAll)) {
                return null;
            }

            var anchorMatch = anchor.toLowerCase();
            var els = document.querySelectorAll(runtime.constants.anchor.attrSelector);
            var el;
            for (var i = els.length - 1; i >= 0; i--) {
                el = els[i];
                if (el.getAttribute(runtime.constants.anchor.attr).toLowerCase() === anchorMatch) {
                    return el;
                }
            }
            return null;
        };

        // runtime apis
        (function() {
            runtime.constants.anchor = runtime.constants.anchor || {};
            runtime.constants.anchor = {
                attr: 'data-ixp-anchor',
                attrSelector: '[data-ixp-anchor]'
            };

            runtime.runScriptlet = function(scriptletId, options) {
                options || (options = {});

                var ee = { scriptletId: scriptletId, options: options, cancel: false };
                runtime.events.trigger('runtime:runscriptlet:starting', ee);
                if (ee.cancel) return;

                options = ee.options || options;

                var promise = $ajax({
                    url: '/admin/api/v1/scriptlets/' + scriptletId + '/run',
                    data: {
                        scriptletId: scriptletId,
                        token: runtime.context.token,
                        tokenId: getRespondentId(),
                        wait: !!options.wait
                    },
                    queue: options.queue
                });

                ee = { scriptletId: scriptletId, options: options, promise: promise };
                runtime.events.trigger('runtime:runscriptlet:completed', ee);

                return promise;
            };

            runtime.runIntegration = function(integrationId, options) {
                options || (options = {});

                var ee = { integrationId: integrationId, options: options, cancel: false };
                runtime.events.trigger('runtime:runintegration:starting', ee);
                if (ee.cancel) return;

                options = ee.options || options;

                var promise = $ajax({
                    url: '/admin/api/v1/integrations/' + integrationId + '/run',
                    data: {
                        integrationId: integrationId,
                        token: runtime.context.token,
                        tokenId: getRespondentId(),
                        wait: !!options.wait
                    },
                    queue: options.queue
                });

                ee = { integrationId: integrationId, options: options, promise: promise };
                runtime.events.trigger('runtime:runintegration:completed', ee);

                return promise;
            };
            
            var isScreenSizeXS = function() {
                return window.innerWidth <= constants.screenSizes.xs.maxWidth;
            };

            var isScreenSizeSM = function() {
                return window.innerWidth >= constants.screenSizes.sm.minWidth && window.innerWidth <= constants.screenSizes.sm.maxWidth;
            };

            var isScreenSizeMD = function() {
                return window.innerWidth >= constants.screenSizes.md.minWidth && window.innerWidth <= constants.screenSizes.md.maxWidth;
            };
            
            var isScreenSizeLG = function() {
                return window.innerWidth >= constants.screenSizes.lg.minWidth;
            };

            runtime.isHiddenByCSS = function(element) {
                if (element.hasClass('ixp-grid-hidden-xs') && isScreenSizeXS()) 
                    return true;
                
                if (element.hasClass('ixp-grid-hidden-sm') && isScreenSizeSM()) 
                    return true;
                
                if (element.hasClass('ixp-grid-hidden-md') && isScreenSizeMD()) 
                    return true;
                
                if (element.hasClass('ixp-grid-hidden-lg') && isScreenSizeLG())
                    return true;

                return false
            };

            runtime.convert = function(options) {
                options || (options = {});

                var ee = { options: options, cancel: false };
                runtime.events.trigger('runtime:convert:starting', ee);
                if (ee.cancel) return;

                options = ee.options || options;

                var promise = $ajax({
                    url: '/outside/convert.ashx?lb3id=' + runtime.context.id + '&v=1&rnd=' + parseInt(31777 * Math.random()),
                    type: 'GET',
                    queue: options.queue
                });

                ee = { options: options, promise: promise };
                runtime.events.trigger('runtime:convert:completed', ee);

                return promise;
            };

            runtime.tag = function(tag, options) {
                options || (options = {});

                if (!tag) {
                    throw new Error('Tag cannot be empty');
                }

                var ee = { tag: tag, options: options, cancel: false };
                runtime.events.trigger('runtime:tag:starting', ee);
                if (ee.cancel) return;

                tag = ee.tag || tag;
                options = ee.options || options;

                var promise = $ajax({
                    url: '/outside/tag.ashx?lb3id=' + runtime.context.id + '&tag=' + encodeURIComponent(tag) + '&v=1&rnd=' + parseInt(31777 * Math.random()),
                    type: 'GET',
                    queue: options.queue
                });

                ee = { tag: tag, options: options, promise: promise };
                runtime.events.trigger('runtime:tag:completed', ee);

                return promise;
            };

            var getParentComponentData = function($el, $) {
                var $parents = $el.parents('[data-ixp-component-root]');
                if (!$parents.length) {
                    return [];
                }
                var parentData = [];

                $parents.each(function() {
                    var $parent = $(this);
                    var id = $parent.data('ixp-component-root');
                    var data = {
                        element: $parent,
                        id: id
                    };

                    parentData.push(data);
                });

                return parentData.reverse();
            };

            var getScrollToOffsetTop = function(location) {
                var scrollAdjustment = 0;

                var stickyStatuses = runtime.components.sendCommand && runtime.components.sendCommand('status', 'sticky_container');
                if (stickyStatuses && stickyStatuses.length) {
                    for (var i = 0, len = stickyStatuses.length; i < len; i++) {
                        var stickyStatus = stickyStatuses[i].data;
                        if (stickyStatus.sticksTo === 'top' && (stickyStatus.isSticking || (location && stickyStatus.scrollTop < location))) {
                            scrollAdjustment += stickyStatus.offset + stickyStatus.height;
                        }
                    }
                }

                return scrollAdjustment;
            };

            var scrollToCore = function($target, data, $) {
                var resolvePromise;

                var onScrolled = function() {
                    runtime.events.trigger(runtime.constants.visibility.events.recalculate, {});
                    runtime.events.trigger('runtime:scrollto:scrolled', data);
                    resolvePromise();
                };

                var location = $target.offset().top;
                if (data.preventOffset !== true) {
                    location -= getScrollToOffsetTop(location);
                }

                if (data.inViewport !== undefined) {
                    var isInViewport = runtime.fn.isInViewport($target[0], data.inViewport);
                    if (isInViewport) {
                        return new Promise(function(resolve) {
                            resolvePromise = resolve;
                            _.defer(onScrolled);
                        });
                    }
                }

                if (data.speed === 0) {
                    return new Promise(function(resolve) {
                        resolvePromise = resolve;
                        $('html, body').stop().scrollTop(location);
                        _.defer(onScrolled);
                    });
                } else {
                    return new Promise(function(resolve) {
                        resolvePromise = resolve;
                        var scrollingEl = document.scrollingElement || document.documentElement;
                        $(scrollingEl).stop().animate({
                            'scrollTop': location
                        }, {
                            duration: data.speed,
                            easing: 'swing',
                            always: _.debounce(onScrolled, 17)
                        });
                    });
                }
            };

            runtime.scrollTo = function(anchorOrElement, options) {
                options || (options = {});
                $promise.then(function($) {
                    var anchor, $target;
                    if (!_.isObject(anchorOrElement)) {
                        anchor = anchorOrElement;
                        $target = $(findElementByIxpAnchor(anchor));
                    } else {
                        $target = $(anchorOrElement);
                        anchor = $target.attr(runtime.constants.anchor.attr);
                    }

                    if (!$target.length) {
                        return;
                    }

                    var targetEl = $target[0];
                    var data = {
                        anchor: anchor,
                        element: targetEl,
                        speed: (options.speed && parseInt(options.speed, 10)) || 0,
                        updateUrlHash: (anchor !== undefined) && (options.updateUrlHash === undefined ? true : options.updateUrlHash),
                        markAsConverted: (anchor !== undefined) && (options.markAsConverted === undefined ? false : options.markAsConverted),
                        inViewport: options.inViewport,
                        preventOffset: false,
                        getOffsetTop: getScrollToOffsetTop
                    };

                    var ee = _.extend(data, { cancel: false });
                    runtime.events.trigger('runtime:scrollto:scrolling', ee);

                    if (ee.cancel) {
                        return;
                    }

                    data.speed = ee.speed;
                    data.updateUrlHash = ee.updateUrlHash;
                    data.markAsConverted = ee.markAsConverted;
                    data.inViewport = ee.inViewport;

                    var resolveCompletionPromise;
                    data.completionPromise = new Promise(function(resolve, reject) {
                        resolveCompletionPromise = resolve;
                    });

                    if (data.updateUrlHash) {
                        runtime.events.trigger('runtime:scrollto:hash:changing', { hash: anchor });
                        window.location.hash = '#' + anchor;
                    }

                    if (data.markAsConverted) {
                        runtime.convert();
                    }

                    var promiseChain = Promise.resolve();
                    var preventDefault = false;
                    var newTarget;

                    var parentComponents = getParentComponentData($target, $);
                    if (parentComponents.length && runtime.components.sendCommand) {
                        var promises = [];

                        for (var i = 0, len = parentComponents.length; i < len; i++) {
                            var results = runtime.components.sendCommand('scrollto', parentComponents[i].id, data);
                            if (results) {
                                for (var j = 0, resultLen = results.length; j < resultLen; j++) {
                                    var result = results[j];

                                    if (result.data.preventDefault) {
                                        preventDefault = true;
                                    }

                                    if (result.data.preventOffset) {
                                        data.preventOffset = true;
                                    }

                                    if (result.data.element) {
                                        newTarget = data.element = result.data.element;
                                    }

                                    data.inViewport = result.data.inViewport;

                                    promises.push(result.data.promise);
                                }
                            }
                        }

                        if (promises.length) {
                            promiseChain = promiseChain.then(function() {
                                return Promise.all(promises);
                            });
                        }
                    }

                    if (!preventDefault) {
                        if (newTarget) {
                            $target = $(newTarget);
                        }
                        promiseChain = promiseChain.then(function() {
                            return scrollToCore($target, data, $);
                        });
                    }

                    if (resolveCompletionPromise) {
                        promiseChain.then(resolveCompletionPromise);
                    }

                    return promiseChain;
                });
            };

            // var INTERACTION_URL = 'https://ion-interactions.free.beeceptor.com';  // TODO move to constants
            // console url
            // https://beeceptor.com/console/ion-interactions-2

            runtime.uploadInteractions = function(interactions, options) {
                if (!interactions.length) {
                    return;
                }
                options || (options = {});

                var ee = { options: options, cancel: false };
                runtime.events.trigger('runtime:uploadinteractions:starting', ee);
                if (ee.cancel) return;

                options = ee.options || options;

                // TODO uncomment after interaction feature implementation
                // var promise = $ajax({
                //     url: INTERACTION_URL,
                //     data: {
                //         isBeacon: false,
                //         interactions: interactions,
                //         token: runtime.context.token,
                //         tokenId: getRespondentId()
                //     },
                //     queue: false
                // });

                // Mocked API call
                var promise = Promise.resolve();

                ee = { options: options, promise: promise };
                runtime.events.trigger('runtime:uploadinteractions:completed', ee);

                return promise;
            };
        }).call(this);

        // runtime.data
        runtime.data = runtime.data || {};
        (function(data) {
            // Private respondent model
            var model = {};

            /**
             * @typedef setOptions
             * @type {object}
             * @property {bool} [replace] - (optional) to override the entire model
             */

            /**
             * Sets all or part of the runtime data-model
             * @param {string} key - key in the model
             * @param {object|string} value - value to set
             * @param {setOptions} [options]
             */
            var setModel = function(key, value, options) {
                if (!key || !value || typeof key !== 'string') {
                    throw new Error('(string) key, and value are required');
                }

                key = key.toLowerCase();

                if (typeof value === 'string') {
                    value = { value: value };
                }

                options || (options = {});

                if (!model[key] || options.replace) {
                    model[key] = value;
                } else {
                    model[key] = _.extend(model[key], value);
                }
            };

            // runtime.data.model - public respondent model
            data.model = data.model || {};

            /**
             * Retrieves all or part of the runtime data-model
             * @param {string} [key] - (optional) key to specific data in the model
             */
            data.model.get = function(key) {
                if (!key) {
                    return _.extend({}, model);
                }

                key = key.toLowerCase();

                return model[key];
            };

            var parseDataField = function(dataField) {
                if (!dataField) {
                    return null;
                }

                if (dataField.indexOf('{') > -1) {
                    try {
                        var field = JSON.parse(dataField);
                        return { id: field.id, key: field.name };
                    }
                    catch (ex) {
                        throw new Error('Data field contains invalid JSON');
                    }
                }

                var separator = dataField.indexOf('\\');
                if (separator > -1) {
                    return { id: 0, key: dataField.substring(separator + 2) };
                } else {
                    return { id: 0, key: dataField };
                }
            };

            var triggerDataEvents = function(instanceId, data, saving) {
                data = (data instanceof Array) ? data : [data];

                var saved = [];
                var failed = [];

                var eventName = (saving ? ':saving' : ':saved');

                var df,
                    sd;
                for (var i = 0, len = data.length; i < len; i++) {
                    df = data[i];

                    if (saving || df.validation === 'valid') {
                        sd = {
                            key: df.key,
                            value: df.value
                        };
                        saved.push(sd);

                        runtime.events.trigger('runtime:data:' + df.key + eventName, { instanceId: instanceId, data: sd });
                    } else {
                        var current = model[df.key];
                        sd = {
                            key: df.key,
                            value: current && current.value,
                            failedValue: df.value,
                            validation: df.validation
                        };
                        failed.push(sd);

                        runtime.events.trigger('runtime:data:' + df.key + ':failed', { instanceId: instanceId, data: sd });
                    }
                }

                var ee = {
                    instanceId: instanceId,
                    data: saved
                };
                if (!saving) {
                    ee.failed = failed;
                }
                runtime.events.trigger('runtime:data' + eventName, ee);
            };

            var updateDataModel = function(data) {
                if (!data || !data.length) {
                    return;
                }

                var df;
                for (var i = 0, len = data.length; i < len; i++) {
                    df = data[i];

                    if (df.validation === 'valid') {
                        setModel(df.key, { value: df.value });
                    }
                }
            };

            var saveDataCore = function($, options) {
                var instanceId = options.data.instanceId;

                var success = _.isFunction(options.success) && options.success;
                options.success = function(response) {
                    var data = response && (response instanceof Array) ? response : [response];
                    updateDataModel(data);

                    if (success) {
                        try {
                            success.apply(this, arguments);
                        } catch (ex) { }
                    }

                    if (data && data.length) {
                        triggerDataEvents(instanceId, data);
                    }
                };

                var error = _.isFunction(options.error) && options.error;
                options.error = function(xhr) {
                    var data;
                    if (xhr.status === 409) {
                        var response = JSON.parse(xhr.responseText);
                        data = response && response.responseStatus && response.responseStatus.data;
                        if (data) {
                            data = (data instanceof Array) ? data : [data];
                            updateDataModel(data);
                        }
                    }

                    if (error) {
                        try {
                            error.apply(this, arguments);
                        } catch (ex) { }
                    }

                    if (data && data.length) {
                        triggerDataEvents(instanceId, data);
                    }
                };

                return ajax($, options);
            };

            data.save = function(options) {
                if (inStudio || (!options.submit && !options.data)) {
                    return Promise.resolve();
                }

                if (!options.data && options.submit) {
                    options.data = [];
                }

                var urlSlug = '';
                var dataToSave,
                    requestType,
                    dataField;

                var isBulkSave = options.data instanceof Array;
                if (isBulkSave) {
                    urlSlug = options.submit ? '/submit' : '/bulk';

                    var pair,
                        fields = [];
                    for (var i = 0, len = options.data.length; i < len; i++) {
                        pair = options.data[i];
                        dataField = parseDataField(pair.dataField);
                        if (dataField) {
                            fields.push({
                                fieldId: dataField.id,
                                key: dataField.key,
                                value: pair.value,
                                metadata: pair.metadata,
                                method: pair.method
                            });
                        }
                    }

                    if (fields.length || options.submit) {
                        dataToSave = {
                            data: fields,
                            token: runtime.context.token,
                            instanceId: options.instanceId
                        };
                    }
                } else {
                    dataField = parseDataField(options.data.dataField);
                    if (dataField) {
                        dataToSave = {
                            key: dataField.key,
                            fieldId: dataField.id,
                            value: options.data.value,
                            metadata: options.data.metadata,
                            token: runtime.context.token,
                            instanceId: options.instanceId
                        };

                        // NOTE: Passing data using the query string to avoid a DELETE call with a body (which proxy servers can have a problem with as it is undefined by the HTTP spec)
                        if (options.data.method && options.data.method.toUpperCase() === 'DELETE') {
                            urlSlug += '?token=' + runtime.context.token;
                            if (dataField.key) {
                                urlSlug += '&key=' + dataField.key;
                            }
                            if (dataField.id) {
                                urlSlug += '&fieldId=' + dataField.id;
                            }

                            requestType = 'DELETE';
                            dataToSave.value = undefined;
                        }

                    }
                }

                if (!dataToSave && requestType !== 'DELETE') {
                    if (typeof options.always === 'function') {
                        options.always.call(this);
                    }
                    return Promise.resolve();
                }

                triggerDataEvents(dataToSave.instanceId, dataToSave, true);

                var respondentId = getRespondentId();
                var config = {
                    url: '/admin/api/v1/respondents/' + respondentId + '/data' + urlSlug,
                    data: dataToSave,
                    success: options.success,
                    error: options.error,
                    always: options.always,
                    type: requestType
                };

                return $promise.then(function($) {
                    return saveDataCore.call(data, $, config);
                });
            };

            data['delete'] = function(options) {
                if (!options.data) {
                    return Promise.resolve();
                }

                options.data.method = 'DELETE';
                return data.save(options);
            };

            runtime.events.on(data, 'runtime:data:registered', function(e) {
                setModel(e.key, e.value);
            });
        }).call(this, runtime.data);

        // dom manipulation/events
        var dom = {};
        (function(dom) {
            var listenerMap = {};

            var passiveSupported = false;

            try {
                var options = Object.defineProperty({}, "passive", {
                    get: function() {
                        passiveSupported = true;
                    }
                });

                window.addEventListener("test", null, options);
            } catch (err) { }

            dom.on = function(el, eventNames, fn, options) {
                var callback = fn;
                options || (options = {});

                if (!el.addEventListener && el.attachEvent) {
                    callback = function() {
                        fn.call(el);
                    };

                    listenerMap[eventNames] = callback;
                }

                var names = eventNames.split(' ');
                for (var i = 0, len = names.length; i < len; i++) {
                    if (el.addEventListener) {
                        el.addEventListener(names[i], callback, options.passive && passiveSupported ? { passive: true } : false);
                    } else if (el.attachEvent) {
                        el.attachEvent("on" + names[i], callback);
                    }
                }
            };

            dom.off = function(el, eventNames, fn) {
                var callback = fn;
                if (!el.addEventListener && el.attachEvent) {
                    callback = listenerMap[eventNames] || fn;
                }

                var names = eventNames.split(' ');
                for (var i = 0, len = names.length; i < len; i++) {
                    if (el.removeEventListener) {
                        el.removeEventListener(names[i], callback, false);
                    } else if (el.detachEvent) {
                        el.detachEvent("on" + names[i], callback);
                    }
                }
            };

            dom.isSvgElement = function(el) {
                return el.nodeType === 1 && el.nodeName === 'svg';
            };

            dom.getClassName = function(el) {
                if (!el.className) {
                    return '';
                }

                if (this.isSvgElement(el)) {
                    return el.className.baseVal;
                }

                return el.className;
            };

            dom.addClass = function(el, className) {
                if (!this.hasClass(el, className)) {
                    if (this.isSvgElement(el)) {
                        el.className.baseVal += (' ' + className);
                    } else {
                        el.className += (' ' + className);
                    }
                }
            };

            dom.hasClass = function(el, className) {
                className = ' ' + className + ' ';
                return el.nodeType === 1 && (' ' + this.getClassName(el) + ' ').replace(/[\n\t]/g, ' ').indexOf(className) >= 0;
            };

            dom.removeClass = function(el, className) {
                var elementClassName = this.getClassName(el);
                var current = el.nodeType === 1 && (elementClassName ? (' ' + elementClassName + ' ').replace(/[\n\t]/g, ' ') : '');
                var isSvg = this.isSvgElement(el);

                if (current) {
                    while (current.indexOf(' ' + className + ' ') >= 0) {
                        current = current.replace(' ' + className + ' ', ' ');
                    }

                    if (isSvg) {
                        el.className.baseVal = current.replace(/^\s+|\s+$/g, '');
                    } else {
                        el.className = current.replace(/^\s+|\s+$/g, '');
                    }
                }
            };

            dom.ready = function(callback) {
                /*
                 jQuery's document.ready/$(function(){}) should
                 you wish to use a cross-browser DOMReady solution
                 without opting for a library.

                 Demo: http://jsfiddle.net/8j85t/

                 usage:
                 $(function(){
                 // your code
                 });

                 Parts: jQuery project, Diego Perini, Lucent M.
                 This version: Addy Osmani, David Dallet
                 */
                
                var isReady = false,
                    readyBound = false,
                    callbackQueue = [];

                var registerOrRunCallback = function(callback) {
                    if (typeof callback === "function") {
                        callbackQueue.push(callback);
                    }
                };

                var DOMReadyCallback = function() {
                    while (callbackQueue.length) {
                        (callbackQueue.shift())();
                    }
                    registerOrRunCallback = function(callback) {
                        callback();
                    };
                };

                // The ready event handler
                var DOMContentLoaded = function() {
                    if (document.addEventListener) {
                        document.removeEventListener("DOMContentLoaded", DOMContentLoaded, false);
                    } else {
                        // we're here because readyState !== "loading" in oldIE
                        // which is good enough for us to call the dom ready!
                        document.detachEvent("onreadystatechange", DOMContentLoaded);
                    }
                    DOMReady();
                };

                var DOMReady = function() {
                    // Make sure that the DOM is not already loaded
                    if (!isReady) {
                        // Make sure body exists, at least, in case IE gets a little overzealous (ticket #5443).
                        if (!document.body) {
                            return setTimeout(DOMReady, 1);
                        }
                        // Remember that the DOM is ready
                        isReady = true;
                        // If there are functions bound, to execute
                        DOMReadyCallback();
                        // Execute all of them
                    }
                };

                // /ready()
                var bindReady = function() {
                    var toplevel = false;

                    if (readyBound) {
                        return;
                    }
                    readyBound = true;

                    // Catch cases where ready is called after the
                    // browser event has already occurred.
                    if (document.readyState !== "loading") {
                        DOMReady();
                    }

                    // Mozilla, Opera and webkit nightlies currently support this event
                    if (document.addEventListener) {
                        // Use the handy event callback
                        document.addEventListener("DOMContentLoaded", DOMContentLoaded, false);
                        // A fallback to window.onload, that will always work
                        window.addEventListener("load", DOMContentLoaded, false);
                        // If IE event model is used
                    } else if (document.attachEvent) {
                        // ensure firing before onload,
                        // maybe late but safe also for iframes
                        document.attachEvent("onreadystatechange", DOMContentLoaded);
                        // A fallback to window.onload, that will always work
                        window.attachEvent("onload", DOMContentLoaded);
                        // If IE and not a frame
                        // continually check to see if the document is ready
                        try {
                            toplevel = window.frameElement == null;
                        }
                        catch (e) {
                        }
                        if (document.documentElement.doScroll && toplevel) {
                            doScrollCheck();
                        }
                    }
                };

                // Handle when the DOM is ready

                // The DOM ready check for Internet Explorer
                var doScrollCheck = function() {
                    if (isReady) {
                        return;
                    }
                    try {
                        // If IE is used, use the trick by Diego Perini
                        // http://javascript.nwbox.com/IEContentLoaded/
                        document.documentElement.doScroll("left");
                    }
                    catch (error) {
                        setTimeout(doScrollCheck, 1);
                        return;
                    }
                    // and execute any waiting functions
                    DOMReady();
                };

                registerOrRunCallback(callback);
                bindReady();
            };
        }).call(this, dom);

        if (dom.hasClass(document.documentElement, 'ixp-prevent-animations')) {
            runtime.flags.preventAnimations = true;
        }

        // scroll visibility / parallax
        (function(querySelectorAll, dom) {
            var visStates;
            var visCssMap = {};

            constants.visibility = (function(visibility) {
                var scrollPrefix = 'ixp-scroll-';

                var css = visibility.css = visibility.css || {};
                css.awareness = scrollPrefix + 'aware';
                css.inOnce = scrollPrefix + 'in-once';
                css.seen = scrollPrefix + 'seen';

                var microthemes = css.microthemes = css.microthemes || {};
                microthemes.awareness = 'ixp-scroll-aware-util';
                microthemes.inOnce = 'ixp-scroll-in-once-util';

                var states = css.states = css.states || {};
                states.shown = scrollPrefix + 'in';
                states.hidden = scrollPrefix + 'out';
                states.entering = scrollPrefix + 'enter';
                states.leaving = scrollPrefix + 'leave';

                var events = visibility.events = visibility.events || {};
                var visPrefix = 'visibility:';
                events.recalculate = visPrefix + 'recalculate';
                events.recalculating = visPrefix + 'recalculating';
                events.change = visPrefix + 'change';

                states = visibility.states = visibility.states || {};
                states.shown = 'shown';
                states.hidden = 'hidden';
                states.entering = 'entering';
                states.leaving = 'leaving';

                visibility.awarenessDelay = 250;
                if (runtime.flags.preventAnimations) {
                    visibility.awarenessSelector = '.' + css.awareness + '.' + css.microthemes.awareness + ', .' + css.awareness + '.' + css.microthemes.inOnce;
                } else {
                    visibility.awarenessSelector = '.' + css.awareness;
                }

                visStates = states;

                visCssMap[states.shown] = css.states.shown;
                visCssMap[states.hidden] = css.states.hidden;
                visCssMap[states.entering] = css.states.entering;
                visCssMap[states.leaving] = css.states.leaving;

                return visibility;
            }).call(this, constants.visibility || {});

            constants.parallax = (function(parallax) {
                var css = parallax.css = parallax.css || {};
                css.parallax = 'ixp-parallax';

                parallax.parallaxSelector = '.' + parallax.css.parallax;

                return parallax;
            }).call(this, constants.parallax || {});


            var getBoundingClientRect = function(el, options) {
                options || (options = {});

                var rect = el.getBoundingClientRect();
                // Needed to support IE8
                if (rect.height === undefined || rect.width === undefined) {
                    rect = {
                        top: rect.top,
                        left: rect.left,
                        bottom: rect.bottom,
                        right: rect.right,
                        height: rect.bottom - rect.top,
                        width: rect.right - rect.left
                    };
                }
                if (options.viewport) {
                    rect = {
                        top: rect.top + (options.viewport.scrollTop || 0),
                        left: rect.left + (options.viewport.scrollLeft || 0),
                        bottom: rect.bottom + (options.viewport.scrollTop || 0),
                        right: rect.right + (options.viewport.scrollLeft || 0),
                        height: rect.bottom - rect.top,
                        width: rect.right - rect.left
                    };
                }
                if (options.round) {
                    rect = {
                        top: Math.ceil(rect.top),
                        left: Math.ceil(rect.left),
                        bottom: Math.ceil(rect.bottom),
                        right: Math.ceil(rect.right),
                        height: Math.ceil(rect.height),
                        width: Math.ceil(rect.width)
                    };
                }

                return rect;
            };

            var isInViewport = function(el, options) {
                options || (options = {});

                var rect = getBoundingClientRect(el, { round: true, viewport: options.viewport });

                // Needed to support IE8
                var windowHeight = Math.ceil((options.viewport && options.viewport.windowHeight) || window.innerHeight || document.documentElement.clientHeight);
                var windowWidth = Math.ceil((options.viewport && options.viewport.windowWidth) || window.innerWidth || document.documentElement.clientWidth);

                if (options.tolerance == null) {
                    if ((window.getComputedStyle && window.getComputedStyle(el, null).getPropertyValue('visibility')) === 'hidden') {
                        return visStates.hidden;
                    }

                    // [MRM] There's a slight chance this could break scroll:always. If so, options needs to know
                    // whether we're checking once or always, and only check the second clause if it's scroll:once
                    if (rect.height >= windowHeight || ((rect.height * 2) > windowHeight && rect.top > 0)) {
                        if ((rect.top > -rect.height) && (rect.top < windowHeight)) {
                            return visStates.shown;
                        } else {
                            return visStates.hidden;
                        }
                    }

                    if (((rect.top >= 0) && (rect.bottom < windowHeight)) &&
                        ((rect.left >= 0) && (rect.left < windowWidth))) {
                        return visStates.shown;
                    } else if (rect.top >= windowHeight || rect.bottom <= 0 || rect.left >= windowWidth || rect.right <= 0) {
                        return visStates.hidden;
                    } else {
                        if (options.direction === 'up') {
                            if ((rect.top > -(rect.height / 2)) && ((rect.top + (rect.height / 2)) < windowHeight)) {
                                return options.element.state == visStates.shown ? visStates.shown : visStates.entering;
                            } else if ((rect.top > -rect.height) && (rect.top < windowHeight)) {
                                return options.element.state == visStates.hidden ? visStates.hidden : visStates.leaving;
                            }
                        } else {
                            if ((rect.top > -(rect.height / 2)) && ((rect.top + (rect.height / 2)) < windowHeight)) {
                                return options.element.state == visStates.shown ? visStates.shown : visStates.entering;
                            } else if ((rect.top > -rect.height) && (rect.top < windowHeight)) {
                                return !options.element.state || options.element.state == visStates.hidden ? visStates.hidden : visStates.leaving;
                            }
                        }
                    }

                    return false;
                }

                var inView = false;
                if (rect.height >= windowHeight) {
                    options.tolerance = 0;
                }

                if (options.tolerance === 1) {
                    inView = (rect.top >= 0) && (rect.bottom <= windowHeight);
                } else if (options.tolerance < 1 && options.tolerance > 0) {
                    var divisor = 1 / options.tolerance;
                    var heightDivided = (rect.height / divisor);
                    inView = (rect.top > -heightDivided) && ((rect.top + heightDivided) < windowHeight);
                } else {
                    inView = (rect.top > -rect.height) && (rect.top < windowHeight);
                }

                if (inView && options.edge) {
                    if (options.edge === 'top') {
                        inView = (rect.top >= 0);
                    } else if (options.edge === 'bottom') {
                        inView = (rect.bottom < windowHeight);
                    }
                }

                return inView;
            };

            runtime.fn.isInViewport = isInViewport;

            if (!querySelectorAll) {
                return;
            }

            var appRoot = inStudio ? window.LIVEBALL : undefined;

            var scrollAwareElements = [],
                parallaxElements = [];

            var getScrollAwareElements = function() {
                var visibility = constants.visibility;
                var els = querySelectorAll.call(document, visibility.awarenessSelector);
                if (!els.length) {
                    return [];
                }

                var el,
                    once,
                    results = [];

                for (var i = 0, len = els.length; i < len; ++i) {
                    el = els[i];
                    once = dom.hasClass(el, visibility.css.inOnce);
                    if (!once || !dom.hasClass(el, visibility.css.seen)) {
                        results.push({ el: el, once: once });
                    }
                }
                return results;
            };

            var getParallaxElements = function() {
                var parallax = constants.parallax;
                var els = querySelectorAll.call(document, parallax.parallaxSelector);
                if (!els.length) {
                    return [];
                }

                var regex = new RegExp('\\b' + parallax.css.parallax + '-(\\d+)');

                var el,
                    match,
                    results = [];

                for (var i = 0, len = els.length; i < len; ++i) {
                    el = els[i];
                    match = el.className.match(regex);

                    results.push({
                        el: el,
                        // Linear
                        //speed: 1 - ((match && match[1] && parseInt(match[1])) || 400) / 1000,
                        // Exponential
                        speed: 1 / (((match && match[1] && parseInt(match[1])) || 400) / 100)
                    });
                }
                return results;
            };

            var getElements = function() {
                scrollAwareElements = getScrollAwareElements();
                parallaxElements = getParallaxElements();
            };

            var inCalculateVisibility = null,
                inCalculateParallax = null,
                lastScrollTop = 0;

            var animationEndEvents = 'webkitAnimationEnd oanimationend MSAnimationEnd animationend';

            var getScrollTop = function(options) {
                if (options != null && options.scrollTop != null && !isNaN(options.scrollTop)) {
                    return options.scrollTop;
                }
                return (window.pageYOffset || document.documentElement.scrollTop || 0) - (document.documentElement.clientTop || 0);
            };

            var calculateVisibility = function(options) {
                options || (options = {});

                var scrollTop = getScrollTop(options.viewport);
                var delta = lastScrollTop - scrollTop;
                if (delta !== 0 || options.force) {
                    var vae,
                        currentState,
                        state;


                    for (var i = 0, len = scrollAwareElements.length; i < len; ++i) {
                        vae = scrollAwareElements[i];

                        currentState = isInViewport(vae.el, { element: vae, direction: delta > 0 ? 'up' : 'down', viewport: options.viewport });

                        vae.state = currentState;

                        if (dom.hasClass(vae.el, visCssMap[currentState])) {
                            continue;
                        }

                        for (state in visStates) {
                            if (visStates.hasOwnProperty(state)) {
                                dom.removeClass(vae.el, visCssMap[visStates[state]]);
                            }
                        }

                        dom.addClass(vae.el, visCssMap[currentState]);

                        if (currentState === visStates.shown && !vae.seen) {
                            vae.seen = true;
                            dom.addClass(vae.el, constants.visibility.css.seen);

                            if (vae.once) {
                                scrollAwareElements.splice(i, 1);
                                i--;
                                len = scrollAwareElements.length;

                                if (vae.el.addEventListener) {
                                    (function(el) {
                                        var onAnimationEnd = function(e) {
                                            if (e.target === el) {
                                                dom.off(el, animationEndEvents, onAnimationEnd);
                                                dom.removeClass(el, constants.visibility.css.states.shown);
                                            }
                                        };
                                        dom.on(el, animationEndEvents, onAnimationEnd, { passive: true });
                                    }).call(this, vae.el);
                                }
                            }
                        }

                        runtime.events.trigger(constants.visibility.events.change, {
                            el: vae.el,
                            once: vae.once,
                            seen: vae.seen,
                            state: currentState
                        });
                    }
                }

                lastScrollTop = scrollTop;
                inCalculateVisibility = null;
            };

            var backgroundImageRegex = /url\(\s*["']?([^"']+)["']?\s*\)/;

            var getNormalizedBackgroundPosition = function(el) {
                var pos = el.style.backgroundPosition.split(' ');
                if (pos.length === 1) {
                    pos[0] = el.style.backgroundPositionX;
                    pos[1] = el.style.backgroundPositionY;
                }

                for (var i = 0, len = pos.length; i < len; i++) {
                    switch (pos[i]) {
                        case 'left':
                            pos[i] = '0%';
                            break;
                        case 'right':
                            pos[i] = '100%';
                            break;
                        case 'top':
                            pos[i] = '0%';
                            break;
                        case 'bottom':
                            pos[i] = '100%';
                            break;
                        case 'center':
                            pos[i] = '50%';
                            break;
                    }
                }

                pos[0] = pos[0] || '0%';
                return pos;
            };

            var getParallaxImageDimensions = function(callback, pe, height, delta, divisor) {
                var match = pe.el.style.backgroundImage.match(backgroundImageRegex);
                if (match) {
                    var img = new Image();

                    img.onload = function() {
                        callback.call(this, pe, height, delta, divisor);
                    };

                    img.src = match[1];
                }
                return !!match;
            };

            var calculateParallaxWithImageDimensions = function(pe, height, delta, divisor) {
                pe.y = (height - this.height) / divisor;
                if (delta) {
                    pe.el.style.backgroundPosition = pe.x + ' ' + (pe.y + delta) + 'px';
                }
            };

            var calculateParallax = function(options) {
                options || (options = {});

                var scrollTop = getScrollTop(options.viewport);

                var pe,
                    delta,
                    rect;

                for (var i = 0, len = parallaxElements.length; i < len; ++i) {
                    pe = parallaxElements[i];

                    if (!isInViewport(pe.el, { tolerance: 0, viewport: options.viewport })) {
                        continue;
                    }

                    delta = -scrollTop * pe.speed;
                    rect = getBoundingClientRect(pe.el);

                    // Save info about the element the first time it is visible
                    if (pe.bgPos === undefined) {
                        var backgroundPosition = getNormalizedBackgroundPosition(pe.el);
                        pe.bgPos = backgroundPosition;
                        pe.x = backgroundPosition[0];

                        var y = backgroundPosition[1];
                        if (y.charAt(y.length - 1) === '%') {
                            y = parseInt(y, 10);
                            if (y === 0) {
                                pe.y = 0;
                            } else {
                                var divisor = 100 / y;
                                pe.y = rect.height / divisor;
                                if (getParallaxImageDimensions(calculateParallaxWithImageDimensions, pe, rect.height, delta, divisor)) {
                                    continue;
                                }
                            }
                        } else {
                            pe.y = parseFloat(y);
                        }

                        if (delta) {
                            pe.el.style.backgroundPosition = pe.x + ' ' + (pe.y + delta) + 'px';
                        }

                        continue;
                    }

                    pe.el.style.backgroundPosition = pe.x + ' ' + (pe.y + delta) + 'px';
                }

                inCalculateParallax = null;
            };

            var calculate = function(e, options) {
                if (!scrollAwareElements.length && !parallaxElements.length) {
                    return;
                }

                options || (options = {});

                var ee = {
                    options: options,
                    cancel: false
                };

                runtime.events.trigger(constants.visibility.events.recalculating, ee);

                if (ee.cancel) {
                    return;
                }

                var force = options.force;
                if ((force || !inCalculateVisibility) && scrollAwareElements.length) {
                    if (inCalculateVisibility) {
                        clearTimeout(inCalculateVisibility);
                    }

                    inCalculateVisibility = setTimeout(function() {
                        calculateVisibility({ force: force, viewport: options.viewport });
                    }, options.delay || constants.visibility.awarenessDelay);
                }

                if ((force || !inCalculateParallax) && parallaxElements.length) {
                    if (inCalculateParallax) {
                        clearTimeout(inCalculateParallax);
                    }

                    inCalculateParallax = setTimeout(function() {
                        calculateParallax({ viewport: options.viewport });
                    }, 1);
                }
            };

            var recalculate = function(options) {
                options || (options = {});

                if (options.reset) {
                    getElements();
                }
                calculate(null, { force: true, delay: options.delay || 1, viewport: options.viewport });
            };

            var updateScreenResolution = function () {

                if (inStudio) return;

                var respondentId = getRespondentId();

                if (!!parseInt(respondentId, 10)) {
                    var screenResolution = window.screen.width + 'X' + window.screen.height;

                    $ajax({
                        url: '/admin/api/v1/respondents/screenresolution/' + respondentId,
                        data: { screenResolution: screenResolution },
                        type: 'PUT',
                        queue: false
                    });
                }
            } 

            dom.ready(function() {
                updateScreenResolution();
                getElements();

                if (!scrollAwareElements.length && !parallaxElements.length && !appRoot) {
                    return;
                }

                dom.on(window, 'scroll', calculate, { passive: true });

                dom.on(window, 'resize', function(e) {
                    calculate(e, { force: true });
                }, { passive: true });

                runtime.events.on(window, constants.visibility.events.recalculate, recalculate);

                calculate(null, { force: true });

                if (appRoot) {
                    var parallaxRegex = new RegExp('\\b' + constants.parallax.css.parallax + '\\b');
                    var scrollAwareRegex = new RegExp('\\b' + constants.visibility.css.awareness + '\\b');

                    // Wait for the app to finish intialization
                    var intervalId = window.setInterval(function() {
                        if (appRoot.App && appRoot.App.vent) {
                            window.clearInterval(intervalId);

                            var recalcTimeout;

                            appRoot.App.vent.subscribe('style:changed:background style:changed:customClass element:microtheme:applied element:microtheme:removed', function(e) {
                                if (e && e.classes) {
                                    if (!e.classes.match(scrollAwareRegex) && !e.classes.match(parallaxRegex)) {
                                        return;
                                    }
                                }

                                // Reset any modified background positions
                                var pe;
                                for (var i = 0, len = parallaxElements.length; i < len; ++i) {
                                    pe = parallaxElements[i];
                                    if (pe.bgPos) {
                                        pe.el.style.backgroundPosition = pe.bgPos.join(' ');
                                    }
                                }
                                parallaxElements = [];

                                if (recalcTimeout) {
                                    window.clearTimeout(recalcTimeout);
                                    recalcTimeout = null;
                                }

                                recalcTimeout = setTimeout(function() {
                                    recalculate({ reset: true });
                                }, 250);
                            });
                        }
                    }, 500);
                }
            });

        }).call(this, document.querySelectorAll, dom);

        // runtime interaction events
        (function() {
            runtime.constants.interaction = runtime.constants.interaction || {};
            runtime.constants.interaction = {
                attrSelector: '[data-ixp-interaction]',
                triggerAttrSelector: '[data-ixp-interaction-trigger]'
            };

            var runAction = function(action) {
                var targetAndName = (action.name).toLowerCase();
                var index = targetAndName.lastIndexOf(':');
                var target = targetAndName.substring(0, index);
                var name = targetAndName.substring(index + 1);

                try {
                    action.data || (action.data = {});
                    if (target === 'runtime') {
                        switch (name) {
                            case 'tag':
                                return runtime.tag(action.data.tag, action.data.options);
                            case 'convert':
                                return runtime.convert(action.data.options);
                            case 'scrollto':
                                return runtime.scrollTo(action.data.anchor, action.data.options);
                        }
                        return Promise.reject();
                    }

                    var result = runtime.components.sendCommand && runtime.components.sendCommand(name, target, action.data);
                    return Promise.resolve(result);

                } catch (e) {
                    return Promise.reject(e);
                }
            };

            var stopPolling = false;

            $promise.then(function($) {
                runtime.ready(function() {
                    var $document = $(document);

                    var onContainerLinkClick = function(e) {
                        var eventTarget = e.target || e.srcElement;
                        if (eventTarget !== this && eventTarget.href && eventTarget.href !== eventTarget.src) {
                            return;
                        }

                        var $container = $(this);
                        var url = $container.attr('data-ixp-container-link-url');
                        if (!url) {
                            return;
                        }

                        var target = $container.attr('data-ixp-container-link-target');
                        switch (target) {
                            case '_blank':
                                window.open(url, '_blank');
                                break;
                            case '_parent':
                                try {
                                    window.parent.location.assign(url);
                                } catch (parentError) {
                                    window.parent.location.replace(url);
                                }
                                break;
                            case '_top':
                                try {
                                    window.top.location.assign(url);
                                } catch (topError) {
                                    window.top.location.replace(url);
                                }
                                break;
                            default:
                                window.location.assign(url);
                                break;
                        }

                        e.cancelBubble = true;
                        if (e.originalEvent) {
                            e.originalEvent.cancelBubble = true;
                        }
                        e.stopPropagation();
                        e.preventDefault();
                        return false;
                    };

                    $document.on('click.ixp-runtime', '[data-ixp-container-link-url]', onContainerLinkClick);

                    var revealLoadedImageParent = function(image) {
                        if (!image || !image.parentNode) {
                            return;
                        }

                        image.parentNode.style.opacity = 1;
                        image.removeAttribute('data-ixp-csp-parent-opacity-on-load');
                    };

                    if (document.addEventListener) {
                        document.addEventListener('load', function(e) {
                            var target = e.target || e.srcElement;
                            if (target && target.getAttribute && target.getAttribute('data-ixp-csp-parent-opacity-on-load') !== null) {
                                revealLoadedImageParent(target);
                            }
                        }, true);
                    }

                    $('[data-ixp-csp-parent-opacity-on-load]').each(function() {
                        if (this.complete) {
                            revealLoadedImageParent(this);
                        }
                    });

                    $document.on('change.ixp-runtime', 'select[data-ixp-csp-navigate-on-change]', function() {
                        var option = this.options && this.options[this.selectedIndex];
                        if (option) {
                            window.location.assign(option.value);
                        }
                    });

                    var getSubmitNavigation = function(e) {
                        if (e.ixpSubmitNavigation) {
                            return e.ixpSubmitNavigation;
                        }
                        if (e.type !== 'click' || e.isDefaultPrevented()) {
                            return null;
                        }

                        var $submit = $(e.target).closest('a[data-ixp-behavior-form-submit][href]');
                        if (!$submit.length) {
                            return null;
                        }

                        var trackingOnly = true;
                        $(e.target).parents().addBack().filter('[data-ixp-interaction-trigger~=":click"]').each(function() {
                            var data;
                            try {
                                data = JSON.parse($(this).attr('data-ixp-interaction') || '{}');
                            } catch (reason) {
                                trackingOnly = false;
                                return;
                            }
                            var actions = data.click || [];
                            for (var actionIndex = 0; actionIndex < actions.length; actionIndex++) {
                                var name = (actions[actionIndex].name || '').toLowerCase();
                                if (name !== 'runtime:tag' && name !== 'runtime:convert') {
                                    trackingOnly = false;
                                }
                            }
                        });
                        if (!trackingOnly) {
                            return null;
                        }

                        var navigation = { promises: [], cancelled: false };
                        var preventDefault = e.preventDefault;
                        var originalEvent = e.originalEvent;
                        var preventOriginalDefault = originalEvent && originalEvent.preventDefault;
                        preventDefault.call(e);
                        e.preventDefault = function() {
                            navigation.cancelled = true;
                            preventDefault.call(this);
                        };
                        if (preventOriginalDefault) {
                            originalEvent.preventDefault = function() {
                                navigation.cancelled = true;
                                preventOriginalDefault.call(this);
                            };
                        }
                        e.ixpSubmitNavigation = navigation;

                        var restoreEvent = function() {
                            e.preventDefault = preventDefault;
                            if (preventOriginalDefault) {
                                originalEvent.preventDefault = preventOriginalDefault;
                            }
                        };

                        var trackingTimeout;
                        Promise.resolve().then(function() {
                            return Promise.race([
                                Promise.all(navigation.promises),
                                new Promise(function(resolve) {
                                    trackingTimeout = window.setTimeout(function() {
                                        console.warn('Submit click tracking timed out');
                                        resolve();
                                    }, 2000);
                                })
                            ]);
                        }).then(function() {
                            window.clearTimeout(trackingTimeout);
                            restoreEvent();
                            if (navigation.cancelled) {
                                return;
                            }
                            var link = $submit[0].cloneNode(false);
                            link.removeAttribute('onclick');
                            link.click();
                        })['catch'](function(reason) {
                            window.clearTimeout(trackingTimeout);
                            restoreEvent();
                            console.warn(reason);
                        });

                        return navigation;
                    };

                    var onActionTrigger = function(e) {
                        var $trigger = $(this);

                        var data = $trigger.attr('data-ixp-interaction');
                        if (!data) {
                            return;
                        }
                        data = JSON.parse(data);

                        var eventType = e.type;
                        var actions = data[eventType];
                        if (!actions || !actions.length) {
                            return;
                        }

                        var submitNavigation = getSubmitNavigation(e);
                        if (eventType.toLowerCase() === 'click' && !submitNavigation) {
                            e.preventDefault();
                        }

                        var promise;

                        for (var i = 0, len = actions.length; i < len; i++) {
                            var action = actions[i];
                            if (action.queue === false) {
                                var parallel = Promise.resolve(runAction(action))['catch'](function(reason) {
                                    console.warn(reason);
                                });
                                if (submitNavigation) {
                                    submitNavigation.promises.push(parallel);
                                }
                            } else {
                                if (promise) {
                                    promise = promise.then(_.partial(runAction, action));
                                } else {
                                    promise = Promise.resolve(runAction(action));
                                }
                            }
                        }

                        if (promise) {
                            var completion = promise['catch'](function(reason) {
                                console.warn(reason);
                            });
                            if (submitNavigation) {
                                submitNavigation.promises.push(completion);
                            }
                        }
                    };

                    var events = ['click', 'hover'];
                    for (var i = 0, len = events.length; i < len; i++) {
                        var evt = events[i];
                        $document.on(evt + '.ixp-runtime', '[data-ixp-interaction-trigger~=":' + evt + '"]', onActionTrigger);
                    }

                    var scrollToHash = {};
                    var onHasChanging = function(e) {
                        var hash = e.hash && e.hash.toLowerCase();
                        if (hash && window.location.hash.toLowerCase() !== ('#' + hash)) {
                            scrollToHash[hash] = true;
                        }
                    };

                    runtime.events.on(runtime, 'runtime:scrollto:hash:changing', onHasChanging);
                    runtime.events.on(runtime, 'runtime:scrollto:ignore:hash:changing', onHasChanging);

                    var scrollTo = _.debounce(runtime.scrollTo, 100);

                    var onHashChange = function() {
                        var hash = window.location.hash;
                        if (hash) {
                            if (hash.indexOf('#') === 0) {
                                hash = hash.substring(1);
                            }

                            var hashMatch = hash.toLowerCase();
                            if (scrollToHash[hashMatch]) {
                                scrollToHash[hashMatch] = undefined;
                            } else {
                                scrollTo(hash);
                            }
                        }
                    };

                    $(window).on('hashchange.ixp-runtime', onHashChange);

                    // A click on a link to the hash already in the URL doesn't fire hashchange, so scroll to its anchor
                    // here, once the click has run its course and only if nothing prevented the navigation
                    var onSameHashLinkClick = function(e) {
                        if (!this.hash || this.href !== window.location.href || (this.target && this.target !== '_self') ||
                            e.which > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                            return;
                        }

                        var originalEvent = e.originalEvent;
                        _.defer(function() {
                            if (!e.isDefaultPrevented() && !(originalEvent && originalEvent.defaultPrevented)) {
                                onHashChange();
                            }
                        });
                    };

                    $document.on('click.ixp-runtime', 'a[href*="#"]', onSameHashLinkClick);

                    $(function() {
                        stopPolling = true;
                        if (runtime.flags.nativeAnchorSupport) {
                            $('a[data-ixp-hidden-anchor]').remove();
                        }
                    });
                });
            });

            (function() {
                var ensureScrollToElement = function(hash) {
                    var target = findElementByIxpAnchor(hash);
                    if (target) {
                        runtime.scrollTo(target, { updateUrlHash: true });
                        return;
                    }

                    if (stopPolling) {
                        return;
                    }

                    if (_.isFunction(window.requestAnimationFrame)) {
                        window.requestAnimationFrame(function() {
                            ensureScrollToElement(hash);
                        });
                    } else {
                        setTimeout(function() {
                            ensureScrollToElement(hash);
                        }, 10);
                    }
                };

                var hash = window.location.hash;
                if (hash) {
                    if (hash.indexOf('#') === 0) {
                        hash = hash.substring(1);
                    }

                    if (runtime.anchors && runtime.anchors[hash.toLowerCase()]) {
                        ensureScrollToElement(hash);

                        window.addEventListener('load', function () {
                            ensureScrollToElement(hash);
                        });
                    }
                }
            }).call(this);
        }).call(this);

        (function() {
            isReady = true;

            if (readyQueue.length === 0) {
                return;
            }

            for (var i = 0, len = readyQueue.length; i < len; i++) {
                try {
                    readyQueue[i]();
                } catch (e) {
                    console.error(e);
                }
            }
        }).call(this);

        // window.navigator.sendBeacon polyfill
        (function () {
            if ('navigator' in this && 'sendBeacon' in this.navigator) {
                return;
            }
            if (!('navigator' in this)) {
                this.navigator = {}
            }
            this.navigator.sendBeacon = sendBeacon.bind(this);
            function sendBeacon (url, data) {
                var xhr = ('XMLHttpRequest' in this)
                    ? new XMLHttpRequest()
                    : new ActiveXObject('Microsoft.XMLHTTP');

                xhr.open('POST', url, false); // Set 'false' for prevent browser canceling when tab closed
                xhr.withCredentials = true;
                xhr.setRequestHeader('Accept', '*/*');

                if (_.isString(data)) {
                    xhr.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');
                    xhr.responseType = 'text';
                } else if (data instanceof Blob && data.type) {
                    xhr.setRequestHeader('Content-Type', data.type);
                }

                try {
                    xhr.send(data)
                } catch (error) {
                    return false
                }

                return true
            }

        }).call(window);
    })();
})(window, document);

/*
* Track user interactions
*/
(function (window, runtime, config) {

    runtime.initInteractionManager = function ($, _) {
        var interactionManager = runtime.interactionManager || {};

        var $document = $(document);
        var that = this;
        var interactionsStorage = {
            store: [],
            isSending: false,
            lastIndex: null,
            set: function (interaction) {
                if (that.inStudio) {
                    return;
                }
                if (_.isArray(interaction)) {
                    this.store.concat(interaction)
                } else {
                    this.store.push(interaction);
                }
                this.onChange();
            },
            onChange: function () {
                if (this.store.length >= config.BULK_SIZE) {
                    this.uploadInteractions();
                }
            },
            uploadInteractions: function () {
                if (!this.store.length || this.isSending) {
                    return;
                }
                this.isSending = true;
                this.lastIndex = this.store.length;
                var self = this;
                runtime
                    .uploadInteractions(this.store.slice(0, this.lastIndex))
                    .then(
                    // _.noop, // TODO uncomment after interaction feature implementation
                    function (xhr) {
                        // if (xhr.status === 200) {
                        //     // Clear part of store if success
                        //     self.store.splice(0, self.lastIndex);
                        // }
                        self.store.splice(0, self.lastIndex);
                        self.isSending = false;
                        self.lastIndex = null;
                    }
                );
            },
            getAllInteractions: function () {
                return this.store.slice();
            },
            getNotSentInteractions: function () {
                return this.store.slice(this.lastIndex || 0);
            }
        };

        config = _.defaults(config || {}, {
            BULK_SIZE: 25,
            AJAX_INTERVAL: 0.5 * 60 * 60,
            GLOBAL_EVENTS: [
                {
                    eventName: 'mouseover',
                    throttle: 100,
                    debounce: false,
                    fn: function () {
                        // TODO global mouseover handler
                    }
                },
                {
                    eventName: 'click',
                    throttle: false,
                    debounce: false,
                    fn: function () {
                        // TODO global click handler
                    }
                }
            ]
        });

        interactionManager.handlers = {
            _handlers: {},

            defaultHandler: {
                fn: function () {
                    if (runtime.flags.debug) {
                        console.warn('Default handler was not defined');
                    }
                },
                options: {
                    throttle: false,
                    debounce: false
                }
            },

            getHandler: function (eventName) {
                var handlerObj = this._handlers[eventName];

                return handlerObj
                    ? _.bind(handlerObj.fn, handlerObj.ctx)
                    : this.defaultHandler;

            },

            wrapHandler: function (handlerFn, options) {
                // Wrap handler into throttle, debounce func.
                if (options.throttle) {
                    return _.throttle(handlerFn, Number(options.throttle));
                }
                if (options.debounce) {
                    return _.debounce(handlerFn, Number(options.debounce));
                }
                return handlerFn;
            },

            registerHandler: function (handlerFn, eventDefinition, ctx) {
                // Register handler for specific events or for components
                if (!eventDefinition || !eventDefinition.eventName) {
                    throw TypeError('EventDefinition should be an object with eventName fields')
                }

                this._handlers[eventDefinition.eventName] = {
                    fn: this.wrapHandler(handlerFn, eventDefinition),
                    ctx: ctx
                };
            }
        };

        interactionManager.pushInteraction = function (interaction) {
            interactionsStorage.set(interaction);
            return interactionManager;
        };

        interactionManager.getAllInteractions = function () {
            return interactionsStorage.getAllInteractions();
        };
        interactionManager.getNotSentInteractions = function () {
            return interactionsStorage.getNotSentInteractions();
        };

        if (!this.inStudio) {
            initGlobalEvents.call(this);
            initAutoSend.call(this);
        }

        function initGlobalEvents() {
            config.GLOBAL_EVENTS.forEach(function (globalEvent) {

                interactionManager.handlers.registerHandler(globalEvent.fn, globalEvent, null);

                // Bind by event name; passing the definition object made jQuery treat it as an
                // events map and bind its string properties as handlers (a silent no-op on
                // jQuery 1.x, a strict-mode TypeError on jQuery 3.x).
                $document.on(globalEvent.eventName, interactionManager.handlers.getHandler(globalEvent.eventName))
            })
        }

        function initAutoSend() {
            setInterval(_.bind(interactionsStorage.uploadInteractions, interactionsStorage), config.AJAX_INTERVAL);
        }
    }

})(window, window.ixp.runtime, window.ixp.runtime.interactionsConfig);

/**
 * Component registry, base-class, and support library
 */
(function(window, runtime, undefined) {

    /* Modernizr 2.8.3 (Custom Build) | MIT & BSD
     * Build: http://modernizr.com/download/#-rgba-cssanimations-csstransforms-csstransforms3d-csstransitions-canvas-input-inputtypes-touch-mq-cssclasses-teststyles-testprop-testallprops-hasevent-prefixes-domprefixes-css_calc-css_mediaqueries-css_pointerevents-cssclassprefix:ixp!mzr!
     */
    var modernizr = (function(window, document, undefined) {

        var version = '2.8.3',

            Modernizr = {},

            enableClasses = true,

            docElement = document.documentElement,

            mod = 'modernizr',
            modElem = document.createElement(mod),
            mStyle = modElem.style,

            inputElem = document.createElement('input'),

            smile = ':)',

            toString = {}.toString,

            prefixes = ' -webkit- -moz- -o- -ms- '.split(' '),


            omPrefixes = 'Webkit Moz O ms',

            cssomPrefixes = omPrefixes.split(' '),

            domPrefixes = omPrefixes.toLowerCase().split(' '),


            tests = {},
            inputs = {},
            attrs = {},

            classes = [],

            slice = classes.slice,

            featureName,


            injectElementWithStyles = function(rule, callback, nodes, testnames) {

                var style, ret, node, docOverflow,
                    div = document.createElement('div'),
                    body = document.body,
                    fakeBody = body || document.createElement('body');

                if (parseInt(nodes, 10)) {
                    while (nodes--) {
                        node = document.createElement('div');
                        node.id = testnames ? testnames[nodes] : mod + (nodes + 1);
                        div.appendChild(node);
                    }
                }

                style = ['&#173;', '<style id="s', mod, '">', rule, '</style>'].join('');
                div.id = mod;
                (body ? div : fakeBody).innerHTML += style;
                fakeBody.appendChild(div);
                if (!body) {
                    fakeBody.style.background = '';
                    fakeBody.style.overflow = 'hidden';
                    docOverflow = docElement.style.overflow;
                    docElement.style.overflow = 'hidden';
                    docElement.appendChild(fakeBody);
                }

                ret = callback(div, rule);
                if (!body) {
                    fakeBody.parentNode.removeChild(fakeBody);
                    docElement.style.overflow = docOverflow;
                } else {
                    div.parentNode.removeChild(div);
                }

                return !!ret;

            },

            testMediaQuery = function(mq) {

                var matchMedia = window.matchMedia || window.msMatchMedia;
                if (matchMedia) {
                    return matchMedia(mq) && matchMedia(mq).matches || false;
                }

                var bool;

                injectElementWithStyles('@media ' + mq + ' { #' + mod + ' { position: absolute; } }', function(node) {
                    bool = (window.getComputedStyle ?
                        getComputedStyle(node, null) :
                        node.currentStyle)['position'] == 'absolute';
                });

                return bool;

            },


            isEventSupported = (function() {

                var TAGNAMES = {
                    'select': 'input', 'change': 'input',
                    'submit': 'form', 'reset': 'form',
                    'error': 'img', 'load': 'img', 'abort': 'img'
                };

                function isEventSupported(eventName, element) {

                    element = element || document.createElement(TAGNAMES[eventName] || 'div');
                    eventName = 'on' + eventName;

                    var isSupported = eventName in element;

                    if (!isSupported) {
                        if (!element.setAttribute) {
                            element = document.createElement('div');
                        }
                        if (element.setAttribute && element.removeAttribute) {
                            element.setAttribute(eventName, '');
                            isSupported = is(element[eventName], 'function');

                            if (!is(element[eventName], 'undefined')) {
                                element[eventName] = undefined;
                            }
                            element.removeAttribute(eventName);
                        }
                    }

                    element = null;
                    return isSupported;
                }
                return isEventSupported;
            })(),


            _hasOwnProperty = ({}).hasOwnProperty, hasOwnProp;

        if (!is(_hasOwnProperty, 'undefined') && !is(_hasOwnProperty.call, 'undefined')) {
            hasOwnProp = function(object, property) {
                return _hasOwnProperty.call(object, property);
            };
        }
        else {
            hasOwnProp = function(object, property) {
                return ((property in object) && is(object.constructor.prototype[property], 'undefined'));
            };
        }


        if (!Function.prototype.bind) {
            Function.prototype.bind = function bind(that) {

                var target = this;

                if (typeof target != "function") {
                    throw new TypeError();
                }

                var args = slice.call(arguments, 1),
                    bound = function() {

                        if (this instanceof bound) {

                            var F = function() { };
                            F.prototype = target.prototype;
                            var self = new F();

                            var result = target.apply(
                                self,
                                args.concat(slice.call(arguments))
                            );
                            if (Object(result) === result) {
                                return result;
                            }
                            return self;

                        } else {

                            return target.apply(
                                that,
                                args.concat(slice.call(arguments))
                            );

                        }

                    };

                return bound;
            };
        }

        function setCss(str) {
            mStyle.cssText = str;
        }

        function setCssAll(str1, str2) {
            return setCss(prefixes.join(str1 + ';') + (str2 || ''));
        }

        function is(obj, type) {
            return typeof obj === type;
        }

        function contains(str, substr) {
            return !!~('' + str).indexOf(substr);
        }

        function testProps(props, prefixed) {
            for (var i in props) {
                var prop = props[i];
                if (!contains(prop, "-") && mStyle[prop] !== undefined) {
                    return prefixed == 'pfx' ? prop : true;
                }
            }
            return false;
        }

        function testDOMProps(props, obj, elem) {
            for (var i in props) {
                var item = obj[props[i]];
                if (item !== undefined) {

                    if (elem === false) return props[i];

                    if (is(item, 'function')) {
                        return item.bind(elem || obj);
                    }

                    return item;
                }
            }
            return false;
        }

        function testPropsAll(prop, prefixed, elem) {

            var ucProp = prop.charAt(0).toUpperCase() + prop.slice(1),
                props = (prop + ' ' + cssomPrefixes.join(ucProp + ' ') + ucProp).split(' ');

            if (is(prefixed, "string") || is(prefixed, "undefined")) {
                return testProps(props, prefixed);

            } else {
                props = (prop + ' ' + (domPrefixes).join(ucProp + ' ') + ucProp).split(' ');
                return testDOMProps(props, prefixed, elem);
            }
        }



        tests['canvas'] = function() {
            var elem = document.createElement('canvas');
            return !!(elem.getContext && elem.getContext('2d'));
        };

        tests['touch'] = function() {
            var bool;

            if (('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch) {
                bool = true;
            } else {
                injectElementWithStyles(['@media (', prefixes.join('touch-enabled),('), mod, ')', '{#modernizr{top:9px;position:absolute}}'].join(''), function(node) {
                    bool = node.offsetTop === 9;
                });
            }

            return bool;
        }; tests['rgba'] = function() {
            setCss('background-color:rgba(150,255,150,.5)');

            return contains(mStyle.backgroundColor, 'rgba');
        };

        tests['cssanimations'] = function() {
            return testPropsAll('animationName');
        };



        tests['csstransforms'] = function() {
            return !!testPropsAll('transform');
        };


        tests['csstransforms3d'] = function() {

            var ret = !!testPropsAll('perspective');

            if (ret && 'webkitPerspective' in docElement.style) {

                injectElementWithStyles('@media (transform-3d),(-webkit-transform-3d){#modernizr{left:9px;position:absolute;height:3px;}}', function(node, rule) {
                    ret = node.offsetLeft === 9 && node.offsetHeight === 3;
                });
            }
            return ret;
        };


        tests['csstransitions'] = function() {
            return testPropsAll('transition');
        };



        function webforms() {
            Modernizr['input'] = (function(props) {
                for (var i = 0, len = props.length; i < len; i++) {
                    attrs[props[i]] = !!(props[i] in inputElem);
                }
                if (attrs.list) {
                    attrs.list = !!(document.createElement('datalist') && window.HTMLDataListElement);
                }
                return attrs;
            })('autocomplete autofocus list placeholder max min multiple pattern required step'.split(' '));
            Modernizr['inputtypes'] = (function(props) {

                for (var i = 0, bool, inputElemType, defaultView, len = props.length; i < len; i++) {

                    inputElem.setAttribute('type', inputElemType = props[i]);
                    bool = inputElem.type !== 'text';

                    if (bool) {

                        inputElem.value = smile;
                        inputElem.style.cssText = 'position:absolute;visibility:hidden;';

                        if (/^range$/.test(inputElemType) && inputElem.style.WebkitAppearance !== undefined) {

                            docElement.appendChild(inputElem);
                            defaultView = document.defaultView;

                            bool = defaultView.getComputedStyle &&
                            defaultView.getComputedStyle(inputElem, null).WebkitAppearance !== 'textfield' &&
                            (inputElem.offsetHeight !== 0);

                            docElement.removeChild(inputElem);

                        } else if (/^(search|tel)$/.test(inputElemType)) {
                        } else if (/^(url|email)$/.test(inputElemType)) {
                            bool = inputElem.checkValidity && inputElem.checkValidity() === false;

                        } else {
                            bool = inputElem.value != smile;
                        }
                    }

                    inputs[props[i]] = !!bool;
                }
                return inputs;
            })('search tel url email datetime date month week time datetime-local number range color'.split(' '));
        }
        for (var feature in tests) {
            if (hasOwnProp(tests, feature)) {
                featureName = feature.toLowerCase();
                Modernizr[featureName] = tests[feature]();

                classes.push((Modernizr[featureName] ? '' : 'no-') + featureName);
            }
        }

        Modernizr.input || webforms();


        Modernizr.addTest = function(feature, test) {
            if (typeof feature == 'object') {
                for (var key in feature) {
                    if (hasOwnProp(feature, key)) {
                        Modernizr.addTest(key, feature[key]);
                    }
                }
            } else {

                feature = feature.toLowerCase();

                if (Modernizr[feature] !== undefined) {
                    return Modernizr;
                }

                test = typeof test == 'function' ? test() : test;

                if (typeof enableClasses !== "undefined" && enableClasses) {
                    docElement.className += " ixp-mzr-" + (test ? '' : 'no-') + feature;
                }
                Modernizr[feature] = test;

            }

            return Modernizr;
        };


        setCss('');
        modElem = inputElem = null;


        Modernizr._version = version;

        Modernizr._prefixes = prefixes;
        Modernizr._domPrefixes = domPrefixes;
        Modernizr._cssomPrefixes = cssomPrefixes;

        Modernizr.mq = testMediaQuery;

        Modernizr.hasEvent = isEventSupported;

        Modernizr.testProp = function(prop) {
            return testProps([prop]);
        };

        Modernizr.testAllProps = testPropsAll;


        Modernizr.testStyles = injectElementWithStyles; docElement.className = docElement.className.replace(/(^|\s)no-js(\s|$)/, '$1$2') +

        (enableClasses ? " ixp-mzr-js ixp-mzr-" + classes.join(" ixp-mzr-") : '');

        return Modernizr;

    })(this, this.document);
    (function(Modernizr) {
        // Method of allowing calculated values for length units, i.e. width: calc(100%-3em) http://caniuse.com/#search=calc
        // By @calvein

        Modernizr.addTest('csscalc', function() {
            var prop = 'width:';
            var value = 'calc(10px);';
            var el = document.createElement('div');

            el.style.cssText = prop + Modernizr._prefixes.join(value + prop);

            return !!el.style.length;
        });

        // developer.mozilla.org/en/CSS/pointer-events

        // Test and project pages:
        // ausi.github.com/Feature-detection-technique-for-pointer-events/
        // github.com/ausi/Feature-detection-technique-for-pointer-events/wiki
        // github.com/Modernizr/Modernizr/issues/80


        Modernizr.addTest('pointerevents', function() {
            var element = document.createElement('x'),
                documentElement = document.documentElement,
                getComputedStyle = window.getComputedStyle,
                supports;
            if (!('pointerEvents' in element.style)) {
                return false;
            }
            element.style.pointerEvents = 'auto';
            element.style.pointerEvents = 'x';
            documentElement.appendChild(element);
            supports = getComputedStyle &&
            getComputedStyle(element, '').pointerEvents === 'auto';
            documentElement.removeChild(element);
            return !!supports;
        });

        Modernizr.addTest('mediaqueries', Modernizr.mq('only all'));

        Modernizr.addTest('mobiledevice', function() {
            var isMobileDevice = (function(a) {
                return (
                    /(android|bb\d+|meego).+mobile|android|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od|ad)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) ||
                    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))
                    );
            })(navigator.userAgent || navigator.vendor || window.opera);

            return isMobileDevice;
        });

        Modernizr.addTest('ios', function () {
            if (window.MSStream) {
                return false;
            }
            return /iPad|iPhone|iPod/.test(navigator.userAgent);
        });
    })(modernizr);

    runtime.components = runtime.components || {};
    var _ = runtime.components.dependencies._;

    (function(components) {
        "use strict";

        components.constants = components.constants || {};

        components.constants.merge = function(constants) {
            constants = _.defaults(constants, components.constants);
            constants.extensibility = _.defaults(constants.extensibility || {}, components.constants.extensibility || {});
            return constants;
        };

        // Contains all the components instances
        components.instances = components.instances || {};

        // Contains all the components ctor functions
        components.registry = components.registry || {};

        components.instancesByType = components.instancesByType || {};

        components.addInstanceId = function(name, instanceId) {
            var instances = components.instancesByType[name];
            if (!instances) {
                instances = components.instancesByType[name] = {};
            }
            instances[instanceId] = true;
        };

        components.getInstance = function(instanceId) {
            return components.instances[instanceId];
        };

        components.registerExtensibility = function(type, constants) {
            components.constants.extensibility || (components.constants.extensibility = {});
            components.constants.extensibility[type] = _.extend(components.constants.extensibility[type] || {}, constants);
        };

        components.dependencies = components.dependencies || {};
        components.dependencies.jQuery = components.dependencies.jQuery || {};
        components.dependencies.Modernizr = modernizr;

        components.sendCommand = function(command, target, options, sender) {
            if (!sender || typeof sender === 'string') {
                sender = undefined;
            }

            options = (options && _.extend({}, options)) || {};

            var results = [];
            if (!_.isFunction(options.done)) {
                options.done = function(e) {
                    results.push(e);
                }
            }

            if (command[0] !== ':') {
                command = ':' + command;
            }

            runtime.events.trigger(sender, 'command:' + target + command, options);

            if (results.length === 0) {
                return undefined;
            }
            return results;
        };

        // Keep this for backward compatibility with old components
        window.LBUI = components;
        window.LBUI.plugins = components.registry;

        var inStudio = !!runtime.studio;
        var inStudioLabs = runtime.studio && runtime.studio.labs;
        var inDebug = runtime.flags.debug;

        /**
         * ComponentBase - base class for all general components
         */
        components.ComponentBase = (function() {
            var constants = components.constants;
            components.constants = _.extend(constants, {
                components: {
                    studio: {
                        data: {
                            hidden: 'data-liveball-is-element-hidden',
                            passthrough: 'data-liveball-element-is-passthrough'
                        },
                        events: {
                            reflow: 'component:reflow'
                        }
                    },
                    data: {
                        componentRoot: 'data-ixp-component-root',
                        behaviorPrefix: 'data-ixp-behavior-',
                        behaviorScopePrefix: 'data-ixp-behavior-scope-',
                        behaviorScopeOwner: 'data-ixp-behavior-scope-owner'
                    }
                },
                params: {
                    countTowardsBounced: 'count_interactions_towards_bounced'
                }
            });

            runtime.components.registerExtensibility('all', {
                commands: {},

                events: {
                    initializing: ':initializing',
                    initialized: ':initialized'
                }
            });

            var getBuilder = function(behaviors) {
                behaviors || (behaviors = {});

                var builder = {
                    PendingContainerChildId: LIVEBALL.App.constants.PendingContainerChildId,

                    scaffold: function(type, elementName) {
                        var element = LIVEBALL.App.componentsManager.getScaffoldElement(type);

                        var Component = components.registry[type];
                        if (_.isFunction(Component)) {
                            var component = Component.prototype;
                            if (_.isFunction(component.getScaffolding)) {
                                var scaffolding = component.getScaffolding(type, { builder: builder });
                                if (scaffolding) {
                                    element.content.scaffolding = scaffolding.scaffolding;
                                    if (scaffolding.elementName) {
                                        element.content.elementName = scaffolding.elementName;
                                    }

                                    var customClass = scaffolding.style && scaffolding.style.customClass;
                                    if (customClass) {
                                        element.style.customClass = customClass;
                                    }

                                    var htmlAttributes = scaffolding.content && scaffolding.content.htmlAttributes;
                                    if (htmlAttributes) {
                                        element.content.htmlAttributes = htmlAttributes;
                                    }
                                }
                            }
                        }
                        if (element && elementName) {
                            element.elementName = elementName;
                        }

                        return element;
                    },

                    scaffoldContainer: function(elementName) {
                        return builder.scaffold('container', elementName);
                    },

                    scaffoldLink: function(content, elementName) {
                        var link = builder.scaffold('link', elementName);
                        link.content.text = content;
                        link.content.isHtml = true;
                        return link;
                    },

                    scaffoldText: function(content, elementName) {
                        var text = builder.scaffold('text', elementName);
                        text.content.text = content;
                        text.content.isHtml = true;
                        return text;
                    },

                    getDefaultTheme: function(groupName, fallbackCssClass) {
                        var group = {};
                        group[groupName] = fallbackCssClass || '';
                        return builder.getDefaultThemes(group);
                    },

                    getDefaultThemes: function(groups) {
                        var defaultClasses = [];

                        // Make a copy since we modify it during the loop
                        groups = _.extend({}, groups);
                        var groupCount = _.keys(groups).length;

                        var byClass = LIVEBALL.themes.byClass;
                        for (var key in byClass) {
                            if (byClass.hasOwnProperty(key)) {
                                var theme = byClass[key];
                                if (theme['default'] && theme.group) {
                                    var themeGroup = theme.group.toLowerCase();

                                    var group = groups[themeGroup];
                                    if (group !== undefined) {
                                        delete groups[themeGroup];

                                        defaultClasses.push(theme.classes);
                                        if (groupCount === defaultClasses.length) {
                                            break;
                                        }
                                    }
                                }
                            }
                        }

                        var fallbackCssClass;
                        for (var g in groups) {
                            if (groups.hasOwnProperty(g)) {
                                fallbackCssClass = groups[g];
                                if (fallbackCssClass) {
                                    defaultClasses.push(fallbackCssClass);
                                }
                            }
                        }

                        return defaultClasses.join(' ');
                    },

                    getBehaviorToken: function(behaviorName) {
                        return behaviors[behaviorName];
                    },

                    addBehaviors: function(type, token) {
                        var byAttribute = LIVEBALL.behaviors.byAttribute;
                        for (var key in byAttribute) {
                            if (byAttribute.hasOwnProperty(key)) {
                                var value = byAttribute[key];
                                if (value.owner && value.owner.key === type) {
                                    behaviors[key] = token;
                                }
                            }
                        }
                    }
                };

                return builder;
            };

            var ComponentBase = function(type, instanceId, el, $, options) {
                options || (options = {});

                this.componentType = type;
                this.instanceId = instanceId;
                this.$ = $;
                this._ = _;

                this.eventNamespace = '.' + this.instanceId;
                this.$el = (el instanceof this.$) ? el : this.$(el);
                this.inStudio = inStudio;
                this.inStudioLabs = inStudioLabs;

                var me = this;

                if (inStudio) {
                    this.paramMap = {};
                }

                var value;
                var parameters = options.parameters || {};
                delete options.parameters;

                for (var param in parameters) {
                    if (parameters.hasOwnProperty(param)) {
                        value = parameters[param];
                        this[param] = value;

                        if (inStudio) {
                            switch (typeof value) {
                                case 'boolean':
                                    this.paramMap[param] = _.bind(function(val) {
                                        me[this.param] = (val === true || val === 'true');
                                    }, { param: param });
                                    break;
                                default:
                                    this.paramMap[param] = _.bind(function(val) {
                                        me[this.param] = val;
                                    }, { param: param });
                                    break;
                            }
                        }
                    }
                }

                this.__initialize(options);

                this.listenToCommands();

                if (inStudio) {
                    this.studioInitialize(options);

                    var paramMapOverrides = this.getParamMapOverrides();

                    if (paramMapOverrides) {
                        this.paramMap = _.extend(this.paramMap, paramMapOverrides);
                    }
                }

                var initialized;
                this.$(document).ready(function() {
                    if (initialized) {
                        return;
                    }

                    try {
                        me.__onDocumentReady();
                        initialized = true;

                        if (me.initlizedPromise) {
                            me.initlizedPromise.then(function() {
                                me.triggerExtensibility(constants.extensibility.all.events.initialized, undefined, { any: true });
                            });
                        } else {
                            me.triggerExtensibility(constants.extensibility.all.events.initialized, undefined, { any: true });
                        }
                    } catch (ex) {
                        console.error('Error readying component: ' + me.componentType + ':' + me.instanceId, ex);
                    }
                });
            };

            ComponentBase.prototype.__initialize = function(options) {
                this.initializing = true;
                try {
                    this.initialize(options);
                } catch (ex) {
                    console.error('Error initializing component: ' + this.componentType + ':' + this.instanceId, ex);
                }
                finally {
                    this.initializing = false;
                }
            };

            ComponentBase.prototype.initialize = function(options) {
            };

            var clickContextHandler = function(clickContext, e) {
                clickContext.onclick.call(clickContext.el, e);
            };

            var findCurrentTargetMatch = function(e, listToSearch) {
                var found;
                for (var i = 0, len = listToSearch.length; i < len; i++) {
                    var obj = listToSearch[i];
                    if (e.currentTarget == obj.el) {
                        found = obj;
                        break;
                    }
                }
                return found;
            };

            ComponentBase.prototype.onBehavior = function($el, selector, callback, eventName, options) {
                eventName || (eventName = 'click' + this.eventNamespace);
                callback = _.bind(callback, this);
                options = _.defaults(options || {}, { delegate: true });

                selector = this.getBehaviorSelector(selector);

                var clickContexts = [];
                this.$(selector).each(function() {
                    if (!this.onclick) {
                        return;
                    }

                    clickContexts.push({
                        el: this,
                        onclick: this.onclick,
                        handler: null
                    });
                    this.onclick = function() { return true; };
                });

                if (clickContexts.length === 0) {
                    if (options.delegate) {
                        $el.on(eventName, selector, function(e) {
                            callback(e);
                        });
                    } else {
                        $el.find(selector).on(eventName, function (e) {
                            callback(e);
                        });
                    }

                    return;
                }

                $el.on(eventName, selector, function(e) {
                    callback(e);
                    if (e.isDefaultPrevented()) {
                        return;
                    }

                    var found = findCurrentTargetMatch(e, clickContexts);
                    if (!found) {
                        return;
                    }

                    if (!found.handler) {
                        found.handler = _.debounce(clickContextHandler, 1);
                    }

                    found.handler(found, e);
                });
            };

            ComponentBase.prototype.listenToCommands = function() {
                var commands = this.getCommandMap();
                if (!commands) {
                    return;
                }

                for (var name in commands) {
                    if (Object.prototype.hasOwnProperty.call(commands, name)) {
                        var cmd = commands[name];
                        runtime.events.on(this, 'command:' + this.componentType + name, cmd.fn);
                        runtime.events.on(this, 'command:' + this.instanceId + name, cmd.fn);
                        if (_.isFunction(this.onListenToCommand)) {
                            this.onListenToCommand(name, cmd);
                        }
                    }
                }
            };

            ComponentBase.prototype.getCommandMap = function() {
                return this.__getCommandMap();
            };

            ComponentBase.prototype.getBuilder = function (options) {
                options || (options = {});

                var builder = options.builder || getBuilder(options.behaviors);

                return builder;
            };

            ComponentBase.prototype.__getCommandMap = function() {
                return _.isFunction(this.onGetCommandMap) && this.onGetCommandMap();
            };

            ComponentBase.prototype.studioInitialize = function(options) {
            };

            ComponentBase.prototype.getParamMapOverrides = function() {
                return undefined;
            };

            ComponentBase.prototype.__onDocumentReady = function(options) {
                return this.onDocumentReady(options);
            };

            ComponentBase.prototype.onDocumentReady = function(options) {
                return true;
            };

            ComponentBase.prototype.destroy = function() {
                // Remove namespaced bindings
                if (this.$el && this.$el.length) {
                    this.$el.off(this.eventNamespace);
                }

                runtime.events.off(this);

                try {
                    this.onDestroy();
                }
                catch (ex) {
                    console.error(ex);
                }
            };

            ComponentBase.prototype.onDestroy = function() {
            };

            ComponentBase.prototype.saveData = function(options) {
                if (this.inStudio) {
                    return;
                }

                options.instanceId = this.instanceId;
                options.success = options.success && _.bind(options.success, this);
                options.error = options.error && _.bind(options.error, this);
                options.always = options.always && _.bind(options.always, this);
                runtime.data.save(options);
            };

            ComponentBase.prototype.submitData = function(options) {
                options.submit = true;
                this.saveData(options);
            };

            ComponentBase.prototype.getBehaviorSelector = function(behavior) {
                var behaviorSelector = '[' + constants.components.data.behaviorPrefix + behavior + ']';
                var behaviorScopeSelector = '[' + constants.components.data.behaviorScopePrefix + behavior + '="' + this.instanceId + '"]';
                return behaviorSelector + behaviorScopeSelector;
            };

            ComponentBase.prototype.getBehaviorSelectorWithoutScope = function(behavior) {
                return '[' + constants.components.data.behaviorPrefix + behavior + ']';
            };

            ComponentBase.prototype.isDirectDescendant = function(el, descendantSelector) {
                var $el = (el instanceof this.$) ? el : this.$(el);
                var $parentEl = $el.closest(descendantSelector);
                return ($parentEl.length && ($parentEl.attr('data-ixp-component-root') === this.instanceId || $parentEl.attr('data-liveball-rmw-root') === this.instanceId));
            };

            ComponentBase.prototype.filterChildren = function(selector, descendantSelector) {
                var me = this;
                return this.$el
                    .find(selector)
                    .filter(function() {
                        return me.isDirectDescendant(this, descendantSelector);
                    });
            };

            ComponentBase.prototype.recalculateVisibility = function(options) {
                options || (options = {});

                if (this._visibilityRecalcTimer) {
                    clearInterval(this._visibilityRecalcTimer);
                    delete this._visibilityRecalcTimer;
                }

                if (options.delay) {
                    this._visibilityRecalcTimer = setTimeout(function() {
                        runtime.events.trigger(runtime.constants.visibility.events.recalculate, {});
                    }, options.delay);
                } else {
                    runtime.events.trigger(runtime.constants.visibility.events.recalculate, {});
                }
            };

            ComponentBase.prototype.sendCommand = function(command, target, options, sender) {
                if (!sender || typeof sender === 'string') {
                    sender = undefined;
                }

                runtime.events.trigger(sender, 'command:' + target + command, options);
            };

            ComponentBase.prototype.sendCommandAsync = function(command, target, options, sender) {
                _.defer(function(me) {
                    me.sendCommand(command, target, options, me);
                }, this);
            };

            ComponentBase.prototype._getDataEnvelope = function(data) {
                return {
                    type: this.componentType,
                    instanceId: this.instanceId,
                    element: this.$el[0],
                    data: data || {}
                };
            };

            ComponentBase.prototype.getExtensibilityEnvelope = function(data) {
                return this._getDataEnvelope(data);
            };

            ComponentBase.prototype.triggerExtensibility = function(evt, data, options) {
                options || (options = {});

                if (this.inStudio && !_.isFunction(options.done)) {
                    return;
                }

                var e = this._getDataEnvelope(data);

                var triggered = false;

                if (_.isFunction(options.done)) {
                    try {
                        triggered = true;
                        options.done.call(this, e);
                    }
                    catch (ex) {
                        console.error(ex);
                    }
                }

                if (evt && !this.inStudio) {
                    try {
                        triggered = runtime.events.trigger('all', evt, e);
                    }
                    catch (ex) {
                        console.error(ex);
                    }

                    if (options.any) {
                        try {
                            triggered = runtime.events.trigger('any' + evt, e) || triggered;
                        }
                        catch (ex) {
                            console.error(ex);
                        }
                    }

                    try {
                        triggered = runtime.events.trigger(this.componentType + evt, e) || triggered;
                    }
                    catch (ex) {
                        console.error(ex);
                    }

                    try {
                        triggered = runtime.events.trigger(this.instanceId + evt, e) || triggered;
                    }
                    catch (ex) {
                        console.error(ex);
                    }

                    if (_.isFunction(this.onTriggerCustomExtensibility)) {
                        try {
                            triggered = this.onTriggerCustomExtensibility(evt, e) || triggered;
                        }
                        catch (ex) {
                            console.error(ex);
                        }
                    }
                }

                if (triggered && data && e.data) {
                    _.extend(data, e.data);
                }
            };

            var _getStyleRuleCss = function(selector, declarations) {
                if (!selector || !declarations) {
                    return null;
                }

                var rules = [];

                for (var i = 0, j = declarations.length; i < j; i++) {
                    var declaration = declarations[i];
                    rules.push(declaration.property + ':' + declaration.value + ';');
                }

                if (!rules.length) {
                    return null;
                }
                return selector + '{' + rules.join(' ') + '}';
            };

            ComponentBase.prototype.addOrUpdateStyleBlock = function(rules) {
                /* [ {
                 selector: "div",
                 declarations: [ { property: "property", value: "value" } ]
                 }
                 ]
                 */

                var css = '';

                if (rules) {
                    for (var i = 0, j = rules.length; i < j; i++) {
                        var cssRule = _getStyleRuleCss(rules[i].selector, rules[i].declarations);
                        if (cssRule) {
                            css += cssRule;
                        }
                    }
                }

                if (!css && this.$styleBlock) {
                    this.$styleBlock.remove();
                    this.$styleBlock = null;
                    return;
                }

                var $block = this.$('<style type="text/css">' + css + '</style>');
                if (this.$styleBlock) {
                    this.$styleBlock = $block.replaceAll(this.$styleBlock);
                } else {
                    this.$styleBlock = $block.appendTo('head');
                }
            };

            ComponentBase.prototype.log = function() {
                if (inDebug) {
                    var args = [this.componentType + ':' + this.instanceId].concat(Array.prototype.slice.call(arguments));
                    runtime.log.apply(runtime, args);
                }
            };

            ComponentBase.prototype.pushInteraction = function (interaction) {
                if (this.inStudio) {
                    return;
                }
                // Assign common fields to interaction data
                var _interaction = _.assign({
                    ts: Date.now(),
                    componentType: this.componentType,
                    instanceId: this.instanceId,
                    params: {
                        countTowardsBounced: this.isCountTowardsBouncedAllowed()
                    }
                }, interaction);

                runtime.interactionManager.pushInteraction(_interaction);
            };

            ComponentBase.prototype.isCountTowardsBouncedAllowed = function () {
                // overwrite in component where needed
                if (constants.params.countTowardsBounced in this) {
                    return this[constants.params.countTowardsBounced];
                }
                return undefined;
            };

            if (inStudio) {

                ComponentBase.prototype.ensureStudioIsLoaded = function(callback) {
                    if (_.isFunction(callback)) {
                        if (this.studioApp) {
                            callback.call(this);
                        } else {
                            runtime.events.once(this, this.instanceId + ':studio:initialized', callback);
                        }
                    }
                };

                ComponentBase.prototype.saveParameter = function(name, value, callback) {
                    callback || (callback = function() { });

                    var triggerSaveEvent = function() {
                        this.studioApp.componentsManager.trigger('component:parameter:save', {
                            instanceId: this.instanceId,
                            name: name,
                            value: value,
                            onCompleted: callback
                        });
                    };

                    this.ensureStudioIsLoaded(triggerSaveEvent);
                };

                ComponentBase.prototype.deleteParameter = function(name, callback) {
                    callback || (callback = function() { });

                    var triggerDeleteEvent = function() {
                        this.studioApp.componentsManager.trigger('component:parameter:delete', {
                            instanceId: this.instanceId,
                            name: name,
                            onCompleted: callback
                        });
                    };
                    this.ensureStudioIsLoaded(triggerDeleteEvent);
                };

                ComponentBase.prototype.addChildren = function (selector, scaffolding, callback) {
                    callback || (callback = function () { });

                    var triggerAddChildrenEvent = function () {
                        this.studioApp.componentsManager.trigger('component:element:add', {
                            instanceId: this.instanceId,
                            selector: selector,
                            scaffolding: scaffolding,
                            onCompleted: callback
                        });
                    };

                    this.ensureStudioIsLoaded(triggerAddChildrenEvent);
                };

                ComponentBase.prototype.removeChildren = function (element, callback) {
                    callback || (callback = function () { });

                    var triggerRemoveChildrenEvent = function () {
                        this.studioApp.componentsManager.trigger('component:element:remove', {
                            instanceId: this.instanceId,
                            element: element,
                            onCompleted: callback
                        });
                    };

                    this.ensureStudioIsLoaded(triggerRemoveChildrenEvent);
                };

                ComponentBase.prototype.updateChildrenContent = function (selector, data, callback) {
                    callback || (callback = function () { });
                    data = data || {};

                    var triggerUpdateChildrenEvent = function () {
                        this.studioApp.componentsManager.trigger('component:element:update', {
                            instanceId: this.instanceId,
                            selector: selector,
                            data: data,
                            onCompleted: callback
                        });
                    };

                    this.ensureStudioIsLoaded(triggerUpdateChildrenEvent);
                };

                ComponentBase.prototype.set = function(paramName, content) {
                    var param = this.paramMap[paramName];
                    if (param) {
                        param.call(this, content);
                        return true;
                    }

                    return false;
                };

                ComponentBase.prototype.onStudioRegistationComplete = function(app) {
                    this.studioApp = app;
                    _.defer(function(me) {
                        runtime.events.trigger(me.instanceId + ':studio:initialized');
                    }, this);
                    this.studioApp.componentsManager.on("app:state:changed", function(newApp) {
                        this.studioApp = newApp;
                        this.onStudioStateChanged();
                    }, this);

                    this.onListenToStudioEvents(this.studioApp.componentsManager);
                };

                ComponentBase.prototype.onListenToStudioEvents = function(events) {
                };

                ComponentBase.prototype.onStudioStateChanged = function() {
                };

                ComponentBase.prototype.getScaffolding = function(scaffoldingKey, options) {
                    options || (options = {});

                    if (arguments.length === 1 && _.isObject(arguments[0])) {
                        options = scaffoldingKey;
                        scaffoldingKey = '';
                    }

                    var scaffolding = null;
                    if (_.isFunction(this.onGetScaffolding)) {
                        var builder = options.builder || getBuilder(options.behaviors);
                        scaffolding = this.onGetScaffolding(scaffoldingKey, builder, options);
                    }

                    return scaffolding;
                };

                ComponentBase.prototype.getWrapping = function(type, options) {
                    options || (options = {});

                    var wrapping = null;
                    if (_.isFunction(this.onGetWrapping)) {
                        wrapping = this.onGetWrapping(type, getBuilder(options.behaviors), options);
                    }

                    return wrapping;
                };

                ComponentBase.prototype.getCssClassesToPreserve = function() {
                    return this.onGetCssClassesToPreserve();
                };

                ComponentBase.prototype.onGetCssClassesToPreserve = function() {
                    return null;
                };
            }

            return ComponentBase;
        }).call(this);

        /**
         * InputComponentBase - base class for all input components
         */
        components.InputComponentBase = (function() {
            var constants = components.constants;
            components.constants = _.extend(constants, {
                inputs: {
                    formShadowSelector: '.ixp-component-form-shadow',
                    formSelector: '.ixp-component-form[' + constants.components.data.componentRoot + ']',
                    formFieldSelector: '.ixp-component-form-field[' + constants.components.data.componentRoot + ']',
                    css: {
                        validationPrefix: 'ixp-input-validation-',
                        input: 'ixp-input',
                        required: 'ixp-input-required',
                        validated: 'ixp-input-validated'
                    },
                    data: {
                        inputDataField: 'data-ixp-input-data-field',
                        inputShadowField: 'data-ixp-shadow-data-field',
                        inputType: 'data-ixp-input-type',
                        state: 'data-ixp-input-state',
                        submitUrl: 'data-ixp-input-submit-url'
                    },
                    events: {
                        handled: 'ixp-handled:',
                        inputChanged: ':input:changed',
                        inputSaved: ':input:saved',
                        inputStateChanged: ':input:state:changed',
                        formSubmitting: ':form:submitting'
                    },
                    states: {
                        valid: 'valid',
                        invalid: 'invalid',
                        incomplete: 'incomplete',
                        pending: 'pending',
                        unknown: 'unknown'
                    }
                }
            });

            runtime.components.registerExtensibility('inputs', {
                commands: {
                    focus: ':focus',
                    value: ':value',
                    status: ':status'
                },

                events: {
                    saving: ':saving',
                    saved: ':saved',
                    status: ':status'
                    //focused: ':focused' // this would be nice - but requires dom event hooks in each input I think
                }
            });

            var cssClassesToPreserve;
            if (inStudio) {
                cssClassesToPreserve = [
                    components.constants.inputs.css.required,
                    components.constants.inputs.css.validated,
                    components.constants.inputs.css.validationPrefix + components.constants.inputs.states.valid,
                    components.constants.inputs.css.validationPrefix + components.constants.inputs.states.invalid,
                    components.constants.inputs.css.validationPrefix + components.constants.inputs.states.incomplete,
                    components.constants.inputs.css.validationPrefix + components.constants.inputs.states.pending,
                    components.constants.inputs.css.validationPrefix + components.constants.inputs.states.unknown
                ];
            }

            var InputComponentBase = function() {
                components.ComponentBase.prototype.constructor.apply(this, arguments);

                if (this.paramMap) {
                    if (_.isFunction(this.paramMap.required)) {
                        var requiredFn = this.paramMap.required;
                        this.paramMap.required = function(val) {
                            requiredFn.call(this, val);
                            this.$el.toggleClass(constants.inputs.css.required, this.required);
                            this.validate(this.state.value);
                        }
                    }

                    if (_.isFunction(this.paramMap.dataField)) {
                        var dataFieldFn = this.paramMap.dataField;
                        this.paramMap.dataField = function(val) {
                            dataFieldFn.call(this, val);
                            this.setInStudioDataFieldValidation();
                        }
                    }

                    if (_.isFunction(this.paramMap.dataSet)) {
                        var dataSetFn = this.paramMap.dataSet;
                        this.paramMap.dataSet = function(val) {
                            dataSetFn.call(this, val);

                            if (_.isString(this.dataSet)) {
                                var dataSet = this.dataSet.trim();
                                if (!dataSet.length) {
                                    this.dataSet = null;
                                } else if (dataSet[0] === '{' || dataSet[0] === '[') {
                                    try {
                                        this.dataSet = JSON.parse(dataSet);
                                    } catch (ignore) {
                                    }
                                }
                            }

                            this.setInStudioDataFieldValidation();
                        }
                    }
                }

                if (!this.inStudio) {
                    runtime.events.on(this, this.instanceId + constants.inputs.events.inputSaved, this.onInputSaved);

                    this.onListenToInputEvents();
                }
            };

            InputComponentBase.prototype = _.create(components.ComponentBase.prototype, { constructor: InputComponentBase });

            InputComponentBase.prototype.__initialize = function(options) {
                this.initializing = true;

                try {
                    this.$form = this.$el.closest(constants.inputs.formSelector);
                    this.formInstanceId = this.$form.attr(constants.components.data.componentRoot);

                    this.dataContext = options.dataContext;
                    if (this.dataContext) {
                        this._updateRuntimeDataModel(this.dataContext);
                    }

                    this.dependentDataContext = options.dependentDataContext;
                    if (this.dependentDataContext) {
                        this._updateRuntimeDataModel(this.dependentDataContext);
                    }

                    this.state = {
                        instanceId: this.instanceId,
                        dataField: this.dataField,
                        validation: this.dataContext.validation.hasServerPattern
                                        ? constants.inputs.states.unknown
                                        : constants.inputs.states.valid,
                        required: !!this.required
                    };

                    this.$el.toggleClass(constants.inputs.css.required, this.required);

                    components.ComponentBase.prototype.__initialize.apply(this, arguments);

                    // Since the base resets this flag - set it again to cover the methods below
                    this.initializing = true;

                    if (this.inStudio) {
                        this.setInStudioDataFieldValidation();
                    } else {
                        this._registerDataField();
                        if (this.dependentDataContext) {
                            runtime.events.on(this, 'runtime:data:' + this.dependentDataContext.name + ':saved', this._onDependentDataSaved);
                        }
                    }
                }
                finally {
                    this.initializing = false;
                }
            };

            InputComponentBase.prototype._getDataEnvelope = function(data) {
                var envelope = components.ComponentBase.prototype._getDataEnvelope.apply(this, arguments);

                var inputContext = {
                    formInstanceId: this.formInstanceId
                };

                if (this.dataContext) {
                    inputContext.dataId = this.dataContext.id;
                    inputContext.dataName = this.dataContext.name;

                    if (this.defaultValue !== null) {
                        inputContext.defaultValue = this.defaultValue;
                        inputContext.hasDefaultValue = true;
                    } else {
                        inputContext.defaultValue = this.dataContext.defaultValue;
                        inputContext.hasDefaultValue = this.dataContext.hasDefaultValue;
                    }
                }

                envelope.inputContext = inputContext;
                return envelope;
            };

            InputComponentBase.prototype.onTriggerCustomExtensibility = function(evt, e) {
                var dataName = this.dataContext && this.dataContext.name;
                if (dataName) {
                    runtime.events.trigger(this.componentType + '|' + dataName.toLowerCase() + evt, e)
                }
            },

            InputComponentBase.prototype.onListenToCommand = function(name, command) {
                var dataName = this.dataContext && this.dataContext.name;
                if (dataName) {
                    runtime.events.on(this, 'command:' + this.componentType + '|' + dataName.toLowerCase() + name, command.fn);
                }
            },

            InputComponentBase.prototype.onDataSaved = function(e) {
            },

            InputComponentBase.prototype._onDataSaved = function(e) {
                if (this.inStudio || !e.data) {
                    return;
                }

                var value = this.getValue();

                if (this.dataContext) {
                    this.dataContext.value = e.data.value;
                }

                this.$shadowInput.val(e.data.value);

                if (e.instanceId !== this.instanceId && (!value || value !== e.data.value)) {
                    this.setValue(e.data.value, { save: false, trigger: false });
                }

                this.onDataSaved({ key: e.data.key, value: e.data.value });
            };

            InputComponentBase.prototype.onDependentDataSaved = function(e) {
            },

            InputComponentBase.prototype._onDependentDataSaved = function(e) {
                if (this.inStudio || !e.data) {
                    return;
                }

                if (this.dependentDataContext) {
                    this.dependentDataContext.value = e.data.value;
                }

                this.onDependentDataSaved({ key: e.data.key, value: e.data.value });
            },

            InputComponentBase.prototype._updateRuntimeDataModel = function(data) {
                if (!data.hasValue) {
                    return;
                }

                runtime.events.trigger(this, 'runtime:data:registered', {
                    key: data.name,
                    value: {
                        value: data.value
                    }
                });
            };

            InputComponentBase.prototype.onRegisterDataField = function() {
                var attrs = {};

                var nameAttr = this.inputAttrName || this.dataContext.name;
                if (nameAttr) {
                    attrs.name = nameAttr;
                }

                if (this.inputAttrId) {
                    attrs.id = this.inputAttrId;
                }

                return attrs;
            };

            InputComponentBase.prototype._registerDataField = function() {
                if (this.inStudio || !this.dataContext.name) {
                    return;
                }

                this.$el.attr(constants.inputs.data.inputDataField, this.dataContext.name);

                var $shadowForm = this.$form.find(constants.inputs.formShadowSelector).first();

                var fieldDataAttr = constants.inputs.data.inputShadowField + '="' + this.dataContext.name + '"';
                var fieldSelector = '[' + fieldDataAttr + ']';
                var $shadowInput = $shadowForm.find(fieldSelector);

                if (!$shadowInput.length) {
                    $shadowInput = this.$('<input type="hidden" ' + fieldDataAttr + ' />').appendTo($shadowForm);

                    // TODO: [ECA] Seems like this should move and be done all the time
                    this._updateRuntimeDataModel(this.dataContext);

                    $shadowInput.val(this.getValue());
                }

                var attrs = this.onRegisterDataField();
                if (attrs) {
                    $shadowInput.attr(attrs);
                }
                this.$shadowInput = $shadowInput;

                runtime.events.on(this, 'runtime:data:' + this.dataContext.name + ':saved', this._onDataSaved);
            };

            InputComponentBase.prototype.getCommandMap = function() {
                var commandMap = {};
                commandMap[constants.extensibility.inputs.commands.focus] = { fn: this._onCommandFocus };
                commandMap[constants.extensibility.inputs.commands.value] = { fn: this._onCommandValue };
                commandMap[constants.extensibility.inputs.commands.status] = { fn: this._onCommandStatus };

                return _.extend(commandMap, this.__getCommandMap() || {});
            };

            InputComponentBase.prototype._onCommandFocus = function() {
                this.setFocus();
            };

            InputComponentBase.prototype._onCommandValue = function(options) {
                var hasValue = false;
                if (options.hasOwnProperty('value')) {
                    hasValue = true;
                    this.setValue(options.value, { trigger: true });
                }

                if (_.isFunction(options.done)) {
                    var data = { value: this.getValue() };
                    if (hasValue) {
                        // Using == here on purpose to coerse the values (string vs ints, etc)
                        data.success = data.value == options.value;
                    }

                    var e = this._getDataEnvelope(data);
                    try {
                        options.done.call(this, e);
                    }
                    catch (ex) {
                        console.error(ex);
                    }
                }
            };

            InputComponentBase.prototype._onCommandStatus = function(options) {
                var ee = {
                    value: this.state.value,
                    hasValue: this.state.hasValue,
                    required: this.state.required,
                    validation: this.state.validation
                };
                this.triggerExtensibility(constants.extensibility.inputs.events.status, ee, { done: options.done });
            };

            var inIframe = function() {
                try {
                    return window.self !== window.top;
                }
                catch (ex) {
                    return true;
                }
            };

            var inFullPageSlider = function() {
                var elements = this.$el.closest('.ixp-full-page-slider');
                return elements.length;
            };

            InputComponentBase.prototype.__onDocumentReady = function() {
                if (!this.inStudio && this.autofocus) {
                    _.delay(function(me) {
                        me.setFocus();
                    }, inIframe() || inFullPageSlider.call(this) ? 500 : 1, this);
                }

                return this.onDocumentReady();
            };

            InputComponentBase.prototype.onListenToInputEvents = function() {
            };


            InputComponentBase.prototype.setFocus = function() {
            };

            InputComponentBase.prototype.getValue = function() {
                throw new Error('getValue not implemented');
            };

            InputComponentBase.prototype.onSetValue = function(value) {
                throw new Error('onSetValue not implemented');
            };

            InputComponentBase.prototype.setValue = function(value, options) {
                options = _.defaults(options || {}, { save: true });

                if (!this.validateValue(value)) {
                    return;
                }

                var setValuePromise = this.onSetValue(value, options);

                if (this.initializing) {
                    this.initlizedPromise = setValuePromise;
                    return;
                }

                if (!setValuePromise) {
                    this.onInputChanged({}, { save: !!options.save, trigger: !!options.trigger, submitting: options.submitting });
                    return;
                }

                setValuePromise.then(_.bind(function() {
                    this.onInputChanged({}, { save: !!options.save, trigger: !!options.trigger, submitting: options.submitting });
                }, this));
            };

            InputComponentBase.prototype.isValueEmpty = function(value) {
                return (value == null || value === '');
            };

            InputComponentBase.prototype.isSaveRequired = function(value) {
                // NOTE: [ECA]  using coersion == on purpose here to deal with strings vs numbers (since all data fields are stored a strings)
                if (value == this.dataContext.value) {
                    return this.state.validation === constants.inputs.states.unknown ||
                        this.state.validation === constants.inputs.states.invalid;
                }

                return true;
            };

            InputComponentBase.prototype.validate = function(value, validationState) {
                if (value === undefined) {
                    value = this.getValue();
                }
                validationState = validationState || this.state.validation;

                var valid = true;
                if (!this.inStudio) {
                    valid = this.onValidate(value, validationState);
                    if (!valid) {
                        validationState = constants.inputs.states.invalid;
                    }
                }
                this.setInputState(value, validationState);

                return valid;
            };

            InputComponentBase.prototype.onValidate = function(value, validationState) {
                return this.validateValue(value);
            };

            InputComponentBase.prototype.validateValue = function(value) {
                var valid = this.onValidateValue(value);

                if (valid && this.dataContext.validation.maxLength) {
                    if (value != null && value.toString().length > this.dataContext.validation.maxLength) {
                        return false;
                    }
                }

                return valid;
            };

            InputComponentBase.prototype.onValidateValue = function(value) {
                return true;
            };

            InputComponentBase.prototype.isIncomplete = function(value) {
                return this.state.required && this.isValueEmpty(value);
            };

            var _applyInputValidationStateCss = function($el, validation) {
                if (validation === constants.inputs.states.pending) {
                    $el.addClass(constants.inputs.css.validationPrefix + validation);
                } else {
                    for (var state in constants.inputs.states) {
                        if (constants.inputs.states.hasOwnProperty(state)) {
                            $el.toggleClass(constants.inputs.css.validationPrefix + state, validation === state);
                        }
                    }
                }
            };

            InputComponentBase.prototype.setInputState = function(value, validationState) {
                if (this.inStudio) {
                    this.state.dataField = this.dataField;
                    this.state.validation = this.dataContext.validation.hasServerPatter
                                                ? constants.inputs.states.unknown
                                                : constants.inputs.states.valid;
                    this.state.required = !!this.required;
                } else if (this.isIncomplete(value)) {
                    validationState = constants.inputs.states.incomplete;
                }

                this.state.value = value;
                this.state.validation = validationState;
                this.state.hasValue = !this.isValueEmpty(value);
                var saveOptions = this.isSaveRequired(value);
                this.state.requiresSave = !!saveOptions;
                this.state.saveMethod = (saveOptions && saveOptions.method) || undefined;

                this.$el.attr(constants.inputs.data.state, JSON.stringify(this.state));

                _applyInputValidationStateCss(this.$el, this.state.validation);

                var initializing = this.inStudio ? false : this.initializing;
                if (!initializing && validationState !== constants.inputs.states.pending) {
                    if (this.inStudio) {
                        this.$el.toggleClass(constants.inputs.css.validated, validationState === constants.inputs.states.invalid);
                    } else {
                        this.$el.addClass(constants.inputs.css.validated);
                    }
                    runtime.events.trigger(this.instanceId + constants.inputs.events.inputStateChanged, { instanceId: this.instanceId });
                }
            };

            InputComponentBase.prototype.setInStudioDataFieldValidation = function() {
                if (!this.inStudio) {
                    return;
                }

                var requiresDataSet = typeof this.dataSet !== 'undefined';
                var isStudioValid = !!(this.dataField && (requiresDataSet ? this.dataSet : true));

                this.validate(this.state.value, isStudioValid ? constants.inputs.states.valid : constants.inputs.states.invalid);
            };

            InputComponentBase.prototype.onInputSaved = function(e) {
                if (e.validation === constants.inputs.states.valid) {
                    this.dataContext.hasValue = e.value != null;
                    this.dataContext.value = e.value;
                }
                this.setInputState(e.value, e.validation);

                var ee = {
                        value: this.state.value,
                        hasValue: this.state.hasValue,
                        required: this.state.required,
                        validation: this.state.validation,
                        cancelable: false
                    };
                this.triggerExtensibility(constants.extensibility.inputs.events.saving, _.extend({}, ee));
                this.triggerExtensibility(constants.extensibility.inputs.events.saved, _.extend(ee, { success: true }));
            };

            InputComponentBase.prototype.onInputChanged = function(e, options) {
                options = _.defaults(options || {}, { save: true, trigger: true });

                if (!options.submitting && options.save) {
                    var form = components.getInstance(this.formInstanceId);
                    if (form && form.instance && form.instance.saveMode === 'onsubmit') {
                        options.save = false;
                    }
                }

                var value = this.getValue();
                var metadata = _.isFunction(this.getMetadata) && this.getMetadata();

                if (!options.save || !this.isSaveRequired(value)) {
                    if (this.state.validation !== constants.inputs.states.valid &&
                        this.state.validation !== constants.inputs.states.unknown) {
                        this.validate(value, this.dataContext.validation.hasServerPattern
                                                 ? constants.inputs.states.unknown
                                                 : constants.inputs.states.valid);
                    } else {
                        // Make sure our internal state is properly up to date
                        this.setInputState(value, this.state.validation);
                    }

                    if (options.trigger) {
                        this.triggerInputChanged();
                    }

                    return;
                }

                if (value === this.state.value &&
                    this.state.validation === constants.inputs.states.pending) {
                    return;
                }

                var validationState = constants.inputs.states.pending;
                if (options.submitting) {
                    validationState = this.dataContext.validation.hasServerPattern
                                          ? constants.inputs.states.unknown
                                          : constants.inputs.states.valid;
                }

                if (!this.validate(value, validationState)) {
                    if (options.trigger) {
                        this.triggerInputChanged();
                    }
                    return;
                }

                // If the form is in the process of being submitted don't bother saving
                if (options.submitting) {
                    return;
                }

                var ee = {
                        value: value,
                        hasValue: !this.isValueEmpty(value),
                        required: this.state.required,
                        validation: this.state.validation,
                        cancelable: true,
                        cancel: false
                    };
                this.triggerExtensibility(constants.extensibility.inputs.events.saving, ee);

                if (ee.cancel) {
                    if (this.state.validation !== constants.inputs.states.valid &&
                        this.state.validation !== constants.inputs.states.unknown) {
                        this.validate(value, this.dataContext.validation.hasServerPattern
                                             ? constants.inputs.states.unknown
                                             : constants.inputs.states.valid);
                    } else {
                        // Make sure our internal state is properly up to date
                        this.setInputState(value, this.state.validation);
                    }

                    if (options.trigger) {
                        this.triggerInputChanged();
                    }

                    return;
                }

                var success = false;
                this.saveData({
                    data: {
                        value: value,
                        metadata: metadata ? JSON.stringify(metadata) : null,
                        dataField: this.dataField,
                        method: this.state.saveMethod
                    },
                    type: this.state.saveMethod,
                    success: function(response) {
                        success = true;
                        this.dataContext.hasValue = response.value != null;
                        this.dataContext.value = response.value;
                        this.setInputState(value, constants.inputs.states.valid);
                    },
                    error: function(xhr) {
                        this.setInputState(value, xhr.status === 409
                                                      ? constants.inputs.states.invalid
                                                      : constants.inputs.states.unknown);
                    },
                    always: function() {
                        this.triggerInputChanged();

                        ee = {
                            value: this.state.value,
                            hasValue: this.state.hasValue,
                            required: this.state.required,
                            validation: this.state.validation,
                            success: success
                        };
                        this.triggerExtensibility(constants.extensibility.inputs.events.saved, ee);
                    }
                });
            };

            InputComponentBase.prototype.triggerInputChanged = function() {
                var e = {
                    value: this.state.value,
                    hasValue: this.state.hasValue,
                    required: this.state.required,
                    state: this.state.validation
                };
                runtime.events.trigger(this.formInstanceId + constants.inputs.events.inputChanged, e);
                runtime.events.trigger(this.instanceId + constants.inputs.events.inputChanged, e);
            };

            if (inStudio) {
                InputComponentBase.prototype.deactivateEvents = function($el) {
                    $el.attr('tabindex', -1);

                    if (components.dependencies.Modernizr.pointerevents) {
                        return;
                    }

                    $el.on('focus' + this.eventNamespace +
                        ' click' + this.eventNamespace +
                        ' mousedown' + this.eventNamespace +
                        ' mouseup' + this.eventNamespace +
                        ' mouseover' + this.eventNamespace +
                        ' mouseout' + this.eventNamespace +
                        ' mousemove' + this.eventNamespace, _.bind(this.onDeactivateEvent, this));
                    $el.css('cursor', 'crosshair');
                };

                InputComponentBase.prototype.onDeactivateEvent = function(e) {
                    try {
                        e.preventDefault();
                        if (e.type === 'focus') {
                            e.stopPropagation();
                            e.stopImmediatePropagation();

                            this.$(e.target).blur();
                        }
                    }
                    catch (ex) {
                    }
                };

                InputComponentBase.prototype.getCssClassesToPreserve = function() {
                    var classes = components.ComponentBase.prototype.getCssClassesToPreserve.apply(this, arguments);
                    return classes
                        ? cssClassesToPreserve.concat(classes)
                        : cssClassesToPreserve;
                };
            }

            return InputComponentBase;
        }).call(this);

        /**
         * creates a component instance
         * @param {String} name - name of the component to load
         * @param {String} instanceId - instance id for the component
         * @param {String, jQuery, Object} $ - DOM element or jQuery instance
         * @param {Object} options - component options
         * @returns {Object} returns a new component instance
         */
        components.create = function(name, instanceId, $, options) {
            var component = this.registry[name];
            if (!component) {
                throw new Error("Component " + name + " could not be found.");
            }

            var opts = _.extend({}, options || {});
            var el = opts.el || ('#ball_' + instanceId);
            delete opts.el;

            var instance = new component(name, instanceId, el, $, opts);
            components.instances[instanceId] = { instance: instance, instanceId: instanceId, type: name };
            components.addInstanceId(name, instanceId);
            return instance;
        };

        /**
         * registers a component
         * @param {string} name - name of the component to register
         * @param {Object} baseType - the base type
         * @param {Object} props - object to be mixed into the component prototype
         * @returns {Object} returns a component ctor function
         */
        components.register = function(name, baseType, props) {
            if (arguments.length === 2) {
                props = baseType;
                baseType = components.ComponentBase;
            }

            var component = function() {
                baseType.apply(this, arguments);
            };

            component.prototype = _.create(baseType.prototype, _.extend(props, { constructor: component }));

            components.registry[name] = component;
            return component;
        };
    }).call(this, runtime.components);
})(window, window.ixp.runtime);

// ion platform (legacy) API (intended for use on ion pages)
// Copyright (c) 2009-2017 ion interactive, inc. All rights reserved.

function _lbapi_XhrFactory() { return new XMLHttpRequest; }
var _lbapi_nativeXhr = new _lbapi_XhrFactory();

var _lbapi_lb3id = _lbapi_lb3id || '';  // lb3id automatically assigned server-side
var _lbapi_asynchronous = true;         // asynchronous requests?

var _lbapi_3id_param = 'lb3id';
var _lbapi_rnd_param = 'rnd';
var _lbapi_tag_param = 'tag';

function _lbapi_urlencode(stg) {
    var retval = ''
    if (stg != null && stg != '') {
        retval = escape(stg);
        retval = retval.replace('+', '%2B');
        retval = retval.replace('/', '%2F');
    }
    return retval;
}

function _lbapi_idstg() {
    return '?' + _lbapi_3id_param + '=' + _lbapi_lb3id + '&' + _lbapi_rnd_param + '=' + parseInt(31777 * Math.random());
}

function liveballConvert(func) {
    if (_lbapi_asynchronous && typeof func !== 'function' &&
        window.ixp && window.ixp.runtime && typeof window.ixp.runtime.convert === 'function') {
        window.ixp.runtime.convert();
        return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open('GET', '/Outside/Convert.ashx' + _lbapi_idstg(), _lbapi_asynchronous);
    if (typeof func === 'function') {
        xhr.onreadystatechange = function () {
            if (this.readyState == XMLHttpRequest.DONE) {
                func();
            }
        }
    }
    xhr.send(null);
}

function liveballTag(tag, func) {
    if (tag == null || tag === '') return;

    if (_lbapi_asynchronous && typeof func !== 'function' &&
        window.ixp && window.ixp.runtime && typeof window.ixp.runtime.tag === 'function') {
        window.ixp.runtime.tag(tag);
        return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open('GET', '/Outside/Tag.ashx' + _lbapi_idstg() + '&' + _lbapi_tag_param + '=' + _lbapi_urlencode(tag), _lbapi_asynchronous);
    if (typeof func === 'function') {
        xhr.onreadystatechange = function () {
            if (this.readyState == XMLHttpRequest.DONE) {
                func();
            }
        }
    }
    xhr.send(null);
}

function liveballData(name, value, func) {
    if (name == null || name === '' || value == null || value === '') return;

    var xhr = new XMLHttpRequest();
    xhr.open('GET', '/Outside/Data.ashx' + _lbapi_idstg() + '&' + name + '=' + _lbapi_urlencode(value), _lbapi_asynchronous);
    if (typeof func === 'function') {
        xhr.onreadystatechange = function () {
            if (this.readyState == XMLHttpRequest.DONE) {
                func();
            }
        }
    }
    xhr.send(null);
}

function liveballDataPost(name, value, func) {
    if (name == null || name === '' || value == null || value === '') return;

    var xhr = new XMLHttpRequest();
    xhr.open('POST', '/Outside/Data.ashx', _lbapi_asynchronous);
    if (typeof func === 'function') {
        xhr.onreadystatechange = function () {
            if (this.readyState == XMLHttpRequest.DONE) {
                func();
            }
        };
    }

    var params = _lbapi_idstg() + '&' + name + '=' + _lbapi_urlencode(value);
    xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
    xhr.setRequestHeader('Content-length', params.length);
    xhr.setRequestHeader('Connection', 'close');
    xhr.send(params);
}

function liveballScriptlet(scriptletId, responseCType, paramList) {
    if (scriptletId == null) return;

    var xhr = new XMLHttpRequest();
    xhr.open('GET', '/Outside/Scriptlet.ashx' + _lbapi_idstg() + '&scrid=' + scriptletId + '&rct=' + responseCType + '&rdata=' + _lbapi_urlencode(paramList), false);
    xhr.send(null);
    if (xhr.readyState == 4 && xhr.status == 200) {
        return xhr.responseText;
    }
    else {
        return ('{RequestState:' + xhr.readystate + ',HttpStatus:' + xhr.status + '}');
    }
}